import { Suspense, lazy } from "react";
import { RouteObject } from "react-router-dom";
import HomePage from "@/pages/home/page";

// Service pages
const PassportServicesPage = lazy(() => import("@/pages/passport-services/page"));
const NewPassportPage = lazy(() => import("@/pages/passport-services/new-passport/page"));
const RenewalPage = lazy(() => import("@/pages/passport-services/renewal/page"));
const LostStolenPage = lazy(() => import("@/pages/passport-services/lost-stolen/page"));
const ChildPassportPage = lazy(() => import("@/pages/passport-services/child-passport/page"));
const DamagedPassportPage = lazy(() => import("@/pages/passport-services/damaged-passport/page"));
const PassportCardPage = lazy(() => import("@/pages/passport-services/passport-card/page"));
const NameChangePage = lazy(() => import("@/pages/passport-services/name-change/page"));
const SecondPassportPage = lazy(() => import("@/pages/passport-services/second-passport/page"));
const ChineseBusinessVisaPage = lazy(() => import("@/pages/passport-services/chinese-business-visa/page"));
const ChinaBusinessVisaChecklistPage = lazy(() => import("@/pages/passport-services/chinese-business-visa-checklist/page"));
const ChineseTouristVisaPage = lazy(() => import("@/pages/passport-services/chinese-tourist-visa/page"));
const ChinaTouristVisaChecklistPage = lazy(() => import("@/pages/passport-services/chinese-tourist-visa-checklist/page"));
const ExpeditedPassportServicePage = lazy(() => import("@/pages/passport-services/expedited-passport-service/page"));
const EmergencyPassportPage = lazy(() => import("@/pages/passport-services/emergency-passport/page"));
const FastPassportRenewalPage = lazy(() => import("@/pages/passport-services/fast-passport-renewal/page"));
const UrgentPassportPage = lazy(() => import("@/pages/passport-services/urgent-passport/page"));
const PassportExpeditingServicePage = lazy(() => import("@/pages/passport-services/passport-expediting-service/page"));

// Helpful Resources pages
const HelpfulResourcesPage = lazy(() => import("@/pages/helpful-resources/page"));
const PassportPhotoRequirementsPage = lazy(() => import("@/pages/helpful-resources/passport-photo-requirements/page"));
const InternationalTravelTipsPage = lazy(() => import("@/pages/helpful-resources/international-travel-tips/page"));
const USCitizenPassportGuidePage = lazy(() => import("@/pages/helpful-resources/us-citizen-passport-guide/page"));
const PassportProcessingTimesPage = lazy(() => import("@/pages/helpful-resources/passport-processing-times/page"));
const DS11vsDS82Page = lazy(() => import("@/pages/helpful-resources/ds11-vs-ds82/page"));
const FirstTimeChecklistPage = lazy(() => import("@/pages/helpful-resources/first-time-checklist/page"));
const CitizenshipThroughParentsPage = lazy(() => import("@/pages/helpful-resources/citizenship-through-parents/page"));
const PassportFeesPage = lazy(() => import("@/pages/helpful-resources/passport-fees/page"));
const ChildPassportGuidePage = lazy(() => import("@/pages/helpful-resources/child-passport-guide/page"));

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1A365D] border-t-[#F6C344]"></div>
  </div>
);

const lazyPage = (factory: () => Promise<{ default: React.ComponentType }>): React.FC => {
  const LazyComponent = lazy(factory);
  return function LazyPageWrapper() {
    return (
      <Suspense fallback={<Loading />}>
        <LazyComponent />
      </Suspense>
    );
  };
};

// Florida pages
const OrlandoPage = lazy(() => import("@/pages/orlando/page"));
const TampaPage = lazy(() => import("@/pages/tampa/page"));
const BocaPage = lazy(() => import("@/pages/boca/page"));
const MiamiPage = lazy(() => import("@/pages/miami/page"));
const FortLauderdalePage = lazy(() => import("@/pages/fortlauderdale/page"));
const NaplesPage = lazy(() => import("@/pages/naples/page"));
const SarasotaPage = lazy(() => import("@/pages/sarasota/page"));
const GainesvillePage = lazy(() => import("@/pages/gainesville/page"));
const JacksonvillePage = lazy(() => import("@/pages/jacksonville/page"));
const PensacolaPage = lazy(() => import("@/pages/pensacola/page"));
const PanamaCityPage = lazy(() => import("@/pages/panamacity/page"));
const TallahasseePage = lazy(() => import("@/pages/tallahassee/page"));
const DaytonaBeachPage = lazy(() => import("@/pages/daytonabeach/page"));
const DelrayPage = lazy(() => import("@/pages/delray/page"));
const AventuraPage = lazy(() => import("@/pages/aventura/page"));
const PalmBeachPage = lazy(() => import("@/pages/palmbeach/page"));
const PalmGardensPage = lazy(() => import("@/pages/palmbgardens/page"));
const CoralGablesPage = lazy(() => import("@/pages/coralgables/page"));
const HollywoodPage = lazy(() => import("@/pages/hollywood/page"));
const PembrokePinesPage = lazy(() => import("@/pages/pembrokepines/page"));
const PlantationPage = lazy(() => import("@/pages/plantation/page"));
const WestonPage = lazy(() => import("@/pages/weston/page"));
const BoyntonBeachPage = lazy(() => import("@/pages/boyntonbeach/page"));
const DeerfieldBeachPage = lazy(() => import("@/pages/deerfieldbeach/page"));
const CoralSpringsPage = lazy(() => import("@/pages/coralsprings/page"));
const ParklandPage = lazy(() => import("@/pages/parkland/page"));
const ClearwaterPage = lazy(() => import("@/pages/clearwater/page"));
const StPetersburgPage = lazy(() => import("@/pages/stpetersburg/page"));
const DunedinPage = lazy(() => import("@/pages/dunedin/page"));
const PalmHarborPage = lazy(() => import("@/pages/palmharbor/page"));
const SafetyHarborPage = lazy(() => import("@/pages/safetyharbor/page"));
const WesleyChapelPage = lazy(() => import("@/pages/wesleychapel/page"));
const WinterGardenPage = lazy(() => import("@/pages/wintergarden/page"));
const WinterParkPage = lazy(() => import("@/pages/winterpark/page"));
const WindermerePage = lazy(() => import("@/pages/windermere/page"));
const LakeMaryPage = lazy(() => import("@/pages/lakemary/page"));
const CelebrationPage = lazy(() => import("@/pages/celebration/page"));
const FortMyersPage = lazy(() => import("@/pages/fortmyers/page"));
const CapeCoralPage = lazy(() => import("@/pages/capecoral/page"));
const BonitaSpringsPage = lazy(() => import("@/pages/bonitasprings/page"));
const EsteroPage = lazy(() => import("@/pages/estero/page"));
const MarcoIslandPage = lazy(() => import("@/pages/marcoisland/page"));
const LakewoodRanchPage = lazy(() => import("@/pages/lakewoodranch/page"));
const JupiterPage = lazy(() => import("@/pages/jupiter/page"));
const PortStLuciePage = lazy(() => import("@/pages/portstlucie/page"));
const StuartPage = lazy(() => import("@/pages/stuart/page"));
const JensenBeachPage = lazy(() => import("@/pages/jensenbeach/page"));
const WellingtonPage = lazy(() => import("@/pages/wellington/page"));
const WestPalmPage = lazy(() => import("@/pages/westpalm/page"));
const KeyWestPage = lazy(() => import("@/pages/keywest/page"));
const MelbournePage = lazy(() => import("@/pages/melbourne/page"));
const KeyLargoPage = lazy(() => import("@/pages/keylargo/page"));
const IslamoradaPage = lazy(() => import("@/pages/islamorada/page"));
const CocoaBeachPage = lazy(() => import("@/pages/cocoa-beach/page"));
const TitusvillePage = lazy(() => import("@/pages/titusville/page"));
const RiverviewPage = lazy(() => import("@/pages/riverview/page"));

const VenicePage = lazy(() => import("@/pages/venice/page"));
const PonteVedraBeachPage = lazy(() => import("@/pages/pontevedrabeach/page"));
const SunnyIslesPage = lazy(() => import("@/pages/sunnyisles/page"));
const QuincyPage = lazy(() => import("@/pages/quincy/page"));
const UpperGrandLagoonPage = lazy(() => import("@/pages/uppergrandlagoon/page"));
const LakelandPage = lazy(() => import("@/pages/lakeland/page"));
const PompanoBeachPage = lazy(() => import("@/pages/pompanobeach/page"));
const CedarKeyPage = lazy(() => import("@/pages/cedarkey/page"));
const UsviPage = lazy(() => import("@/pages/usvi/page"));
const PuertoRicoPage = lazy(() => import("@/pages/puerto-rico/page"));
const DcPage = lazy(() => import("@/pages/dc/page"));
const FloridaPage = lazy(() => import("@/pages/florida/page"));
const HawaiiPage = lazy(() => import("@/pages/hawaii/page"));
const HonoluluPage = lazy(() => import("@/pages/honolulu/page"));
const PearlCityPage = lazy(() => import("@/pages/pearl-city/page"));
const HiloPage = lazy(() => import("@/pages/hilo/page"));
const KailuaPage = lazy(() => import("@/pages/kailua/page"));
const KapoleiPage = lazy(() => import("@/pages/kapolei/page"));
const KaneohePage = lazy(() => import("@/pages/kaneohe/page"));
const KailuaKonaPage = lazy(() => import("@/pages/kailua-kona/page"));
const KahuluiPage = lazy(() => import("@/pages/kahului/page"));
const KiheiPage = lazy(() => import("@/pages/kihei/page"));
const LahainaPage = lazy(() => import("@/pages/lahaina/page"));
const LihuePage = lazy(() => import("@/pages/lihue/page"));
const MililaniPage = lazy(() => import("@/pages/mililani/page"));
const WaikikiPage = lazy(() => import("@/pages/waikiki/page"));
const MauiPage = lazy(() => import("@/pages/maui/page"));
const KauaiPage = lazy(() => import("@/pages/kauai/page"));
const WaipahuPage = lazy(() => import("@/pages/waipahu/page"));
const EwaBeachPage = lazy(() => import("@/pages/ewa-beach/page"));
const KapaaPage = lazy(() => import("@/pages/kapaa/page"));
const WaileaPage = lazy(() => import("@/pages/wailea/page"));
const WahiawaPage = lazy(() => import("@/pages/wahiawa/page"));
const WailukuPage = lazy(() => import("@/pages/wailuku/page"));

const MaconPage = lazy(() => import("@/pages/macon/page"));
const WarnerRobinsPage = lazy(() => import("@/pages/warner-robins/page"));
const ValdostaPage = lazy(() => import("@/pages/valdosta/page"));

const HinesvillePage = lazy(() => import("@/pages/hinesville/page"));
const LaGrangePage = lazy(() => import("@/pages/lagrange/page"));
const KennesawPage = lazy(() => import("@/pages/kennesaw/page"));
const GainesvilleGAPage = lazy(() => import("@/pages/gainesville-ga/page"));
const PeachtreeCityPage = lazy(() => import("@/pages/peachtree-city/page"));
const WoodstockPage = lazy(() => import("@/pages/woodstock/page"));
const LawrencevillePage = lazy(() => import("@/pages/lawrenceville/page"));
const SmyrnaPage = lazy(() => import("@/pages/smyrna/page"));
const MilledgevillePage = lazy(() => import("@/pages/milledgeville/page"));
const RomePage = lazy(() => import("@/pages/rome/page"));
const StatesboroPage = lazy(() => import("@/pages/statesboro/page"));
const NewnanPage = lazy(() => import("@/pages/newnan/page"));
const CaliforniaPage = lazy(() => import("@/pages/california/page"));
const LosAngelesPage = lazy(() => import("@/pages/los-angeles/page"));
const SanDiegoPage = lazy(() => import("@/pages/san-diego/page"));
const SanFranciscoPage = lazy(() => import("@/pages/san-francisco/page"));
const SanJosePage = lazy(() => import("@/pages/san-jose/page"));
const SacramentoPage = lazy(() => import("@/pages/sacramento/page"));
const AnaheimPage = lazy(() => import("@/pages/anaheim/page"));
const OaklandPage = lazy(() => import("@/pages/oakland/page"));
const LongBeachPage = lazy(() => import("@/pages/long-beach/page"));
const IrvinePage = lazy(() => import("@/pages/irvine/page"));
const SantaAnaPage = lazy(() => import("@/pages/santa-ana/page"));
const FresnoPage = lazy(() => import("@/pages/fresno/page"));
const BakersfieldPage = lazy(() => import("@/pages/bakersfield/page"));
const RiversidePage = lazy(() => import("@/pages/riverside/page"));
const SantaBarbaraPage = lazy(() => import("@/pages/santa-barbara/page"));
const StocktonPage = lazy(() => import("@/pages/stockton/page"));
const PasadenaPage = lazy(() => import("@/pages/pasadena/page"));
const ModestoPage = lazy(() => import("@/pages/modesto/page"));
const VenturaPage = lazy(() => import("@/pages/ventura/page"));
const SanBernardinoPage = lazy(() => import("@/pages/san-bernardino/page"));
const OntarioPage = lazy(() => import("@/pages/ontario/page"));
const ChulaVistaPage = lazy(() => import("@/pages/chula-vista/page"));
const HuntingtonBeachPage = lazy(() => import("@/pages/huntington-beach/page"));
const CostaMesaPage = lazy(() => import("@/pages/costa-mesa/page"));
const SantaMonicaPage = lazy(() => import("@/pages/santa-monica/page"));
const NewportBeachPage = lazy(() => import("@/pages/newport-beach/page"));
const GardenGrovePage = lazy(() => import("@/pages/garden-grove/page"));
const OrangePage = lazy(() => import("@/pages/orange/page"));
const FullertonPage = lazy(() => import("@/pages/fullerton/page"));
const TorrancePage = lazy(() => import("@/pages/torrance/page"));
const GlendalePage = lazy(() => import("@/pages/glendale/page"));
const BurbankPage = lazy(() => import("@/pages/burbank/page"));
const ThousandOaksPage = lazy(() => import("@/pages/thousand-oaks/page"));
const SimiValleyPage = lazy(() => import("@/pages/simi-valley/page"));
const RanchoCucamongaPage = lazy(() => import("@/pages/rancho-cucamonga/page"));
const PomonaPage = lazy(() => import("@/pages/pomona/page"));
const EscondidoPage = lazy(() => import("@/pages/escondido/page"));
const CarlsbadPage = lazy(() => import("@/pages/carlsbad/page"));
const OxnardPage = lazy(() => import("@/pages/oxnard/page"));
const SalinasPage = lazy(() => import("@/pages/salinas/page"));
const SantaRosaPage = lazy(() => import("@/pages/santa-rosa/page"));
const BerkeleyPage = lazy(() => import("@/pages/berkeley/page"));
const DalyCityPage = lazy(() => import("@/pages/daly-city/page"));
const SantaClaraPage = lazy(() => import("@/pages/santa-clara/page"));
const SunnyvalePage = lazy(() => import("@/pages/sunnyvale/page"));
const ConcordPage = lazy(() => import("@/pages/concord/page"));
const RosevillePage = lazy(() => import("@/pages/roseville/page"));
const OceansidePage = lazy(() => import("@/pages/oceanside/page"));
const ElCajonPage = lazy(() => import("@/pages/el-cajon/page"));
const VallejoPage = lazy(() => import("@/pages/vallejo/page"));
const HaywardPage = lazy(() => import("@/pages/hayward/page"));
const MorenoValleyPage = lazy(() => import("@/pages/moreno-valley/page"));
const FontanaPage = lazy(() => import("@/pages/fontana/page"));
const VisaliaPage = lazy(() => import("@/pages/visalia/page"));
const AntiochPage = lazy(() => import("@/pages/antioch/page"));
const MenifeePage = lazy(() => import("@/pages/menifee/page"));
const RialtoPage = lazy(() => import("@/pages/rialto/page"));
const ComptonPage = lazy(() => import("@/pages/compton/page"));
const ReddingPage = lazy(() => import("@/pages/redding/page"));
const VacavillePage = lazy(() => import("@/pages/vacaville/page"));
const TustinPage = lazy(() => import("@/pages/tustin/page"));
const MercedPage = lazy(() => import("@/pages/merced/page"));
const MountainViewPage = lazy(() => import("@/pages/mountain-view/page"));
const SanMateoPage = lazy(() => import("@/pages/san-mateo/page"));
const LivermorePage = lazy(() => import("@/pages/livermore/page"));
const TracyPage = lazy(() => import("@/pages/tracy/page"));
const OhioPage = lazy(() => import("@/pages/ohio/page"));
const CincinnatiPage = lazy(() => import("@/pages/cincinnati/page"));
const ClevelandPage = lazy(() => import("@/pages/cleveland/page"));
const ColumbusOhPage = lazy(() => import("@/pages/columbus-oh/page"));
const DaytonPage = lazy(() => import("@/pages/dayton/page"));
const ToledoPage = lazy(() => import("@/pages/toledo/page"));
const AkronPage = lazy(() => import("@/pages/akron/page"));
const CantonPage = lazy(() => import("@/pages/canton/page"));
const YoungstownPage = lazy(() => import("@/pages/youngstown/page"));
const LorainPage = lazy(() => import("@/pages/lorain/page"));
const ParmaPage = lazy(() => import("@/pages/parma/page"));
const HamiltonPage = lazy(() => import("@/pages/hamilton/page"));
const SpringfieldPage = lazy(() => import("@/pages/springfield/page"));
const KetteringPage = lazy(() => import("@/pages/kettering/page"));
const ElyriaPage = lazy(() => import("@/pages/elyria/page"));
const MiddletownPage = lazy(() => import("@/pages/middletown/page"));
const CuyahogaFallsPage = lazy(() => import("@/pages/cuyahoga-falls/page"));
const NewarkPage = lazy(() => import("@/pages/newark/page"));
const JerseyCityPage = lazy(() => import("@/pages/jersey-city/page"));
const KentuckyPage = lazy(() => import("@/pages/kentucky/page"));
const AshlandPage = lazy(() => import("@/pages/ashland/page"));
const BardstownPage = lazy(() => import("@/pages/bardstown/page"));
const BereaPage = lazy(() => import("@/pages/berea/page"));
const BowlingGreenPage = lazy(() => import("@/pages/bowling-green/page"));
const NorthCarolinaPage = lazy(() => import("@/pages/northcarolina/page"));
const ColoradoPage = lazy(() => import("@/pages/colorado/page"));
const TexasPage = lazy(() => import("@/pages/texas/page"));
const HoustonPage = lazy(() => import("@/pages/houston/page"));
const DallasPage = lazy(() => import("@/pages/dallas/page"));
const AustinPage = lazy(() => import("@/pages/austin/page"));
const SanAntonioPage = lazy(() => import("@/pages/san-antonio/page"));
const FortWorthPage = lazy(() => import("@/pages/fort-worth/page"));
const ElPasoPage = lazy(() => import("@/pages/el-paso/page"));
const ArlingtonTXPage = lazy(() => import("@/pages/arlington/page"));
const CorpusChristiPage = lazy(() => import("@/pages/corpus-christi/page"));
const PlanoPage = lazy(() => import("@/pages/plano/page"));
const LaredoPage = lazy(() => import("@/pages/laredo/page"));
const LubbockPage = lazy(() => import("@/pages/lubbock/page"));
const GarlandPage = lazy(() => import("@/pages/garland/page"));
const IrvingTXPage = lazy(() => import("@/pages/irving/page"));
const AmarilloPage = lazy(() => import("@/pages/amarillo/page"));
const GrandPrairiePage = lazy(() => import("@/pages/grand-prairie/page"));
const BrownsvillePage = lazy(() => import("@/pages/brownsville/page"));
const McKinneyPage = lazy(() => import("@/pages/mckinney/page"));
const FriscoPage = lazy(() => import("@/pages/frisco/page"));
const PasadenaTXPage = lazy(() => import("@/pages/pasadena-tx/page"));
const MesquitePage = lazy(() => import("@/pages/mesquite/page"));
const KilleenPage = lazy(() => import("@/pages/killeen/page"));
const McAllenPage = lazy(() => import("@/pages/mcallen/page"));
const WacoPage = lazy(() => import("@/pages/waco/page"));
const DentonPage = lazy(() => import("@/pages/denton/page"));
const MidlandTXPage = lazy(() => import("@/pages/midland/page"));
const StatenIslandPage = lazy(() => import("@/pages/staten-island/page"));
const LongIslandPage = lazy(() => import("@/pages/long-island/page"));
const BayShorePage = lazy(() => import("@/pages/bay-shore/page"));
const CommackPage = lazy(() => import("@/pages/commack/page"));
const HicksvillePage = lazy(() => import("@/pages/hicksville/page"));
const HuntingtonPage = lazy(() => import("@/pages/huntington/page"));
const BinghamtonPage = lazy(() => import("@/pages/binghamton/page"));
const WhitePlainsPage = lazy(() => import("@/pages/white-plains/page"));
const PoughkeepsiePage = lazy(() => import("@/pages/poughkeepsie/page"));
const NewburghPage = lazy(() => import("@/pages/newburgh/page"));
const NewRochellePage = lazy(() => import("@/pages/new-rochelle/page"));

const ElmiraPage = lazy(() => import("@/pages/elmira/page"));
const IthacaPage = lazy(() => import("@/pages/ithaca/page"));
const CorningPage = lazy(() => import("@/pages/corning/page"));
const JamestownPage = lazy(() => import("@/pages/jamestown/page"));

const IllinoisPage = lazy(() => import("@/pages/illinois/page"));
const PennsylvaniaPage = lazy(() => import("@/pages/pennsylvania/page"));
const PhiladelphiaPage = lazy(() => import("@/pages/philadelphia/page"));
const PittsburghPage = lazy(() => import("@/pages/pittsburgh/page"));
const AllentownPage = lazy(() => import("@/pages/allentown/page"));
const LancasterPaPage = lazy(() => import("@/pages/lancaster/page"));
const HarrisburgPaPage = lazy(() => import("@/pages/harrisburg-pa/page"));
const ScrantonPage = lazy(() => import("@/pages/scranton/page"));
const ReadingPage = lazy(() => import("@/pages/reading/page"));
const BethlehemPage = lazy(() => import("@/pages/bethlehem/page"));
const EriePage = lazy(() => import("@/pages/erie/page"));
const StateCollegePage = lazy(() => import("@/pages/state-college/page"));
const YorkPaPage = lazy(() => import("@/pages/york-pa/page"));
const WestChesterPage = lazy(() => import("@/pages/west-chester/page"));
const OregonPage = lazy(() => import("@/pages/oregon/page"));
const PortlandPage = lazy(() => import("@/pages/portland/page"));
const EugenePage = lazy(() => import("@/pages/eugene/page"));
const SalemPage = lazy(() => import("@/pages/salem/page"));
const BendPage = lazy(() => import("@/pages/bend/page"));
const MedfordPage = lazy(() => import("@/pages/medford/page"));
const BeavertonPage = lazy(() => import("@/pages/beaverton/page"));
const CorvallisPage = lazy(() => import("@/pages/corvallis/page"));
const HillsboroPage = lazy(() => import("@/pages/hillsboro/page"));
const GreshamPage = lazy(() => import("@/pages/gresham/page"));
const LakeOswegoPage = lazy(() => import("@/pages/lake-oswego/page"));
const TigardPage = lazy(() => import("@/pages/tigard/page"));
const TualatinPage = lazy(() => import("@/pages/tualatin/page"));
const OregonCityPage = lazy(() => import("@/pages/oregon-city/page"));
const WestLinnPage = lazy(() => import("@/pages/west-linn/page"));
const MilwaukiePage = lazy(() => import("@/pages/milwaukie/page"));
const HappyValleyPage = lazy(() => import("@/pages/happy-valley/page"));
const TroutdalePage = lazy(() => import("@/pages/troutdale/page"));
const ForestGrovePage = lazy(() => import("@/pages/forest-grove/page"));
const NewbergPage = lazy(() => import("@/pages/newberg/page"));
const WilsonvilleORPage = lazy(() => import("@/pages/wilsonville/page"));
const SherwoodORPage = lazy(() => import("@/pages/sherwood/page"));
const CanbyPage = lazy(() => import("@/pages/canby/page"));
const GladstonePage = lazy(() => import("@/pages/gladstone/page"));
const EstacadaPage = lazy(() => import("@/pages/estacada/page"));
const SandyORPage = lazy(() => import("@/pages/sandy/page"));
const HoodRiverPage = lazy(() => import("@/pages/hood-river/page"));
const TheDallesPage = lazy(() => import("@/pages/the-dalles/page"));
const PendletonPage = lazy(() => import("@/pages/pendleton/page"));
const LaGrandePage = lazy(() => import("@/pages/la-grande/page"));
const OntarioOrPage = lazy(() => import("@/pages/ontario-or/page"));
const KlamathFallsPage = lazy(() => import("@/pages/klamath-falls/page"));
const AlbanyOrPage = lazy(() => import("@/pages/albany-or/page"));
const LebanonOrPage = lazy(() => import("@/pages/lebanon-or/page"));
const SpringfieldOrPage = lazy(() => import("@/pages/springfield-or/page"));
const CoosBayPage = lazy(() => import("@/pages/coos-bay/page"));
const GrantsPassPage = lazy(() => import("@/pages/grants-pass/page"));
const AstoriaOrPage = lazy(() => import("@/pages/astoria/page"));
const SeasideOrPage = lazy(() => import("@/pages/seaside/page"));
const LincolnCityOrPage = lazy(() => import("@/pages/lincoln-city/page"));
const NewportOrPage = lazy(() => import("@/pages/newport-or/page"));
const TillamookPage = lazy(() => import("@/pages/tillamook/page"));
const RedmondOrPage = lazy(() => import("@/pages/redmond/page"));
const PrinevillePage = lazy(() => import("@/pages/prineville/page"));
const HermistonPage = lazy(() => import("@/pages/hermiston/page"));
const McMinnvillePage = lazy(() => import("@/pages/mcminnville/page"));
const KeizerPage = lazy(() => import("@/pages/keizer/page"));
const WoodburnPage = lazy(() => import("@/pages/woodburn/page"));
const AshlandOrPage = lazy(() => import("@/pages/ashland-or/page"));
const RoseburgPage = lazy(() => import("@/pages/roseburg/page"));
const ArizonaPage = lazy(() => import("@/pages/arizona/page"));
const ApacheJunctionPage = lazy(() => import("@/pages/apache-junction/page"));
const AvondalePage = lazy(() => import("@/pages/avondale/page"));
const BuckeyePage = lazy(() => import("@/pages/buckeye/page"));
const BullheadCityPage = lazy(() => import("@/pages/bullhead-city/page"));
const CasaGrandePage = lazy(() => import("@/pages/casa-grande/page"));
const ChandlerPage = lazy(() => import("@/pages/chandler/page"));
const CottonwoodPage = lazy(() => import("@/pages/cottonwood/page"));
const FlagstaffPage = lazy(() => import("@/pages/flagstaff/page"));
const GilbertPage = lazy(() => import("@/pages/gilbert/page"));
const GlendaleAZPage = lazy(() => import("@/pages/glendale-az/page"));
const GoodyearPage = lazy(() => import("@/pages/goodyear/page"));
const GreenValleyPage = lazy(() => import("@/pages/green-valley/page"));
const HolbrookPage = lazy(() => import("@/pages/holbrook/page"));
const KingmanPage = lazy(() => import("@/pages/kingman/page"));
const LakeHavasuCityPage = lazy(() => import("@/pages/lake-havasu-city/page"));
const MaranaPage = lazy(() => import("@/pages/marana/page"));
const MaricopaPage = lazy(() => import("@/pages/maricopa/page"));
const MesaPage = lazy(() => import("@/pages/mesa/page"));
const NogalesPage = lazy(() => import("@/pages/nogales/page"));
const OroValleyPage = lazy(() => import("@/pages/oro-valley/page"));
const PageAZPage = lazy(() => import("@/pages/page-az/page"));
const ParadiseValleyPage = lazy(() => import("@/pages/paradise-valley/page"));
const PaysonPage = lazy(() => import("@/pages/payson/page"));
const PeoriaAZPage = lazy(() => import("@/pages/peoria-az/page"));
const PhoenixPage = lazy(() => import("@/pages/phoenix/page"));
const ScottsdalePage = lazy(() => import("@/pages/scottsdale/page"));
const TucsonPage = lazy(() => import("@/pages/tucson/page"));
const PrescottPage = lazy(() => import("@/pages/prescott/page"));
const SedonaPage = lazy(() => import("@/pages/sedona/page"));
const SierraVistaPage = lazy(() => import("@/pages/sierra-vista/page"));
const YumaPage = lazy(() => import("@/pages/yuma/page"));
const TempePage = lazy(() => import("@/pages/tempe/page"));
const PrescottValleyPage = lazy(() => import("@/pages/prescott-valley/page"));
const QueenCreekPage = lazy(() => import("@/pages/queen-creek/page"));
const SahuaritaPage = lazy(() => import("@/pages/sahuarita/page"));
const SanLuisPage = lazy(() => import("@/pages/san-luis/page"));
const ShowLowPage = lazy(() => import("@/pages/show-low/page"));
const SomertonPage = lazy(() => import("@/pages/somerton/page"));
const SurprisePage = lazy(() => import("@/pages/surprise/page"));
const WinslowPage = lazy(() => import("@/pages/winslow/page"));
const ParamusPage = lazy(() => import("@/pages/paramus/page"));
const RidgewoodPage = lazy(() => import("@/pages/ridgewood/page"));
const FortLeePage = lazy(() => import("@/pages/fort-lee/page"));
const NewJerseyPage = lazy(() => import("@/pages/new-jersey/page"));
const HackensackPage = lazy(() => import("@/pages/hackensack/page"));
const NewBrunswickPage = lazy(() => import("@/pages/new-brunswick/page"));
const PiscatawayPage = lazy(() => import("@/pages/piscataway/page"));
const WoodbridgePage = lazy(() => import("@/pages/woodbridge/page"));
const PlainfieldPage = lazy(() => import("@/pages/plainfield/page"));
const PatersonPage = lazy(() => import("@/pages/paterson/page"));
const ElizabethPage = lazy(() => import("@/pages/elizabeth/page"));
const TrentonPage = lazy(() => import("@/pages/trenton/page"));
const CherryHillPage = lazy(() => import("@/pages/cherry-hill/page"));
const HobokenPage = lazy(() => import("@/pages/hoboken/page"));
const EdisonPage = lazy(() => import("@/pages/edison/page"));
const CliftonPage = lazy(() => import("@/pages/clifton/page"));
const PrincetonPage = lazy(() => import("@/pages/princeton/page"));
const BayonnePage = lazy(() => import("@/pages/bayonne/page"));
const UnionCityPage = lazy(() => import("@/pages/union-city/page"));
const SecaucusPage = lazy(() => import("@/pages/secaucus/page"));
const NorthBergenPage = lazy(() => import("@/pages/north-bergen/page"));
const TomsRiverPage = lazy(() => import("@/pages/toms-river/page"));
const BrickPage = lazy(() => import("@/pages/brick/page"));
const PointPleasantPage = lazy(() => import("@/pages/point-pleasant/page"));
const LakewoodNJPage = lazy(() => import("@/pages/lakewood-nj/page"));
const RedBankPage = lazy(() => import("@/pages/red-bank/page"));
const LongBranchPage = lazy(() => import("@/pages/long-branch/page"));
const AsburyParkPage = lazy(() => import("@/pages/asbury-park/page"));
const MiddletownNJPage = lazy(() => import("@/pages/middletown-nj/page"));
const MorristownPage = lazy(() => import("@/pages/morristown/page"));
const ParsippanyPage = lazy(() => import("@/pages/parsippany/page"));
const DenvillePage = lazy(() => import("@/pages/denville/page"));
const MadisonPage = lazy(() => import("@/pages/madison/page"));
const SomervilleNJPage = lazy(() => import("@/pages/somerville-nj/page"));
const BridgewaterPage = lazy(() => import("@/pages/bridgewater/page"));
const HillsboroughPage = lazy(() => import("@/pages/hillsborough/page"));
const FranklinPage = lazy(() => import("@/pages/franklin/page"));
const BurlingtonNJPage = lazy(() => import("@/pages/burlington-nj/page"));
const MountHollyPage = lazy(() => import("@/pages/mount-holly/page"));
const MoorestownPage = lazy(() => import("@/pages/moorestown/page"));
const SpartaNJPage = lazy(() => import("@/pages/sparta-nj/page"));
const VernonNJPage = lazy(() => import("@/pages/vernon-nj/page"));
const HamburgNJPage = lazy(() => import("@/pages/hamburg-nj/page"));
const MedfordNJPage = lazy(() => import("@/pages/medford-nj/page"));
const EveshamPage = lazy(() => import("@/pages/evesham/page"));
const BordentownPage = lazy(() => import("@/pages/bordentown/page"));
const CamdenNJPage = lazy(() => import("@/pages/camden-nj/page"));
const FreeholdPage = lazy(() => import("@/pages/freehold/page"));
const VinelandPage = lazy(() => import("@/pages/vineland/page"));
const BloomfieldPage = lazy(() => import("@/pages/bloomfield/page"));
const EastOrangePage = lazy(() => import("@/pages/east-orange/page"));
const HamiltonNJPage = lazy(() => import("@/pages/hamilton-nj/page"));
const IrvingtonPage = lazy(() => import("@/pages/irvington/page"));
const EnglewoodPage = lazy(() => import("@/pages/englewood/page"));
const KearnyPage = lazy(() => import("@/pages/kearny/page"));
const MontclairPage = lazy(() => import("@/pages/montclair/page"));
const PassaicPage = lazy(() => import("@/pages/passaic/page"));
const PerthAmboyPage = lazy(() => import("@/pages/perth-amboy/page"));
const TeaneckPage = lazy(() => import("@/pages/teaneck/page"));
const WayneNJPage = lazy(() => import("@/pages/wayne-nj/page"));
const WestNewYorkPage = lazy(() => import("@/pages/west-new-york/page"));
const WestfieldPage = lazy(() => import("@/pages/westfield/page"));
const CumberlandMdPage = lazy(() => import("@/pages/cumberland-md/page"));
const CatonsvillePage = lazy(() => import("@/pages/catonsville/page"));
const DundalkPage = lazy(() => import("@/pages/dundalk/page"));
const EastonMdPage = lazy(() => import("@/pages/easton-md/page"));
const EllicottCityPage = lazy(() => import("@/pages/ellicott-city/page"));
const EssexPage = lazy(() => import("@/pages/essex/page"));
const GermantownPage = lazy(() => import("@/pages/germantown/page"));
const GreenbeltPage = lazy(() => import("@/pages/greenbelt/page"));
const LaPlataPage = lazy(() => import("@/pages/la-plata/page"));
const LaurelPage = lazy(() => import("@/pages/laurel/page"));
const LexingtonParkPage = lazy(() => import("@/pages/lexington-park/page"));
const MarylandPage = lazy(() => import("@/pages/maryland/page"));
const MichiganPage = lazy(() => import("@/pages/michigan/page"));
const AnnArborPage = lazy(() => import("@/pages/ann-arbor/page"));
const DetroitPage = lazy(() => import("@/pages/detroit/page"));
const FlintPage = lazy(() => import("@/pages/flint/page"));
const GrandRapidsPage = lazy(() => import("@/pages/grand-rapids/page"));
const KalamazooPage = lazy(() => import("@/pages/kalamazoo/page"));
const LansingMIPage = lazy(() => import("@/pages/lansing-mi/page"));
const SterlingHeightsPage = lazy(() => import("@/pages/sterling-heights/page"));
const WarrenMIPage = lazy(() => import("@/pages/warren/page"));
const DearbornPage = lazy(() => import("@/pages/dearborn/page"));
const PontiacPage = lazy(() => import("@/pages/pontiac/page"));
const SouthfieldPage = lazy(() => import("@/pages/southfield/page"));
const TroyMIPage = lazy(() => import("@/pages/troy-mi/page"));
const BaltimorePage = lazy(() => import("@/pages/baltimore/page"));
const AnnapolisPage = lazy(() => import("@/pages/annapolis/page"));
const SilverSpringPage = lazy(() => import("@/pages/silver-spring/page"));
const BethesdaPage = lazy(() => import("@/pages/bethesda/page"));
const ColumbiaPage = lazy(() => import("@/pages/columbia/page"));
const RockvillePage = lazy(() => import("@/pages/rockville/page"));
const FrederickMdPage = lazy(() => import("@/pages/frederick-md/page"));
const GaithersburgPage = lazy(() => import("@/pages/gaithersburg/page"));
const TowsonPage = lazy(() => import("@/pages/towson/page"));
const HagerstownPage = lazy(() => import("@/pages/hagerstown/page"));
const WaldorfPage = lazy(() => import("@/pages/waldorf/page"));
const SalisburyMdPage = lazy(() => import("@/pages/salisbury-md/page"));
const BowiePage = lazy(() => import("@/pages/bowie/page"));
const CollegeParkPage = lazy(() => import("@/pages/college-park/page"));
const OceanCityPage = lazy(() => import("@/pages/ocean-city/page"));
const MainePage = lazy(() => import("@/pages/maine/page"));
const PortlandMePage = lazy(() => import("@/pages/portland-me/page"));
const BangorPage = lazy(() => import("@/pages/bangor/page"));
const LewistonPage = lazy(() => import("@/pages/lewiston/page"));
const SouthPortlandPage = lazy(() => import("@/pages/south-portland/page"));
const AuburnMePage = lazy(() => import("@/pages/auburn-me/page"));
const BiddefordPage = lazy(() => import("@/pages/biddeford/page"));
const SanfordMePage = lazy(() => import("@/pages/sanford/page"));
const SacoPage = lazy(() => import("@/pages/saco/page"));
const AugustaMePage = lazy(() => import("@/pages/augusta-me/page"));
const WestbrookPage = lazy(() => import("@/pages/westbrook/page"));
const WatervillePage = lazy(() => import("@/pages/waterville/page"));
const PresqueIslePage = lazy(() => import("@/pages/presque-isle/page"));
const EllsworthPage = lazy(() => import("@/pages/ellsworth/page"));
const OldTownPage = lazy(() => import("@/pages/old-town/page"));
const BrewerPage = lazy(() => import("@/pages/brewer/page"));
const GardinerPage = lazy(() => import("@/pages/gardiner/page"));
const BathPage = lazy(() => import("@/pages/bath/page"));
const BrunswickPage = lazy(() => import("@/pages/brunswick/page"));
const ScarboroughPage = lazy(() => import("@/pages/scarborough/page"));
const GorhamPage = lazy(() => import("@/pages/gorham/page"));
const YarmouthPage = lazy(() => import("@/pages/yarmouth/page"));
const WindhamPage = lazy(() => import("@/pages/windham/page"));
const FalmouthPage = lazy(() => import("@/pages/falmouth/page"));
const KennebunkPage = lazy(() => import("@/pages/kennebunk/page"));
const OronoPage = lazy(() => import("@/pages/orono/page"));
const BelfastPage = lazy(() => import("@/pages/belfast/page"));
const RocklandPage = lazy(() => import("@/pages/rockland/page"));
const BarHarborPage = lazy(() => import("@/pages/bar-harbor/page"));
const CamdenPage = lazy(() => import("@/pages/camden/page"));
const FreeportPage = lazy(() => import("@/pages/freeport/page"));
const TopshamPage = lazy(() => import("@/pages/topsham/page"));
const YorkPage = lazy(() => import("@/pages/york/page"));
const WellsPage = lazy(() => import("@/pages/wells/page"));
const CumberlandPage = lazy(() => import("@/pages/cumberland-me/page"));
const OgunquitPage = lazy(() => import("@/pages/ogunquit/page"));
const HampdenPage = lazy(() => import("@/pages/hampden/page"));
const MissouriPage = lazy(() => import("@/pages/missouri/page"));
const IowaPage = lazy(() => import("@/pages/iowa/page"));
const DesMoinesPage = lazy(() => import("@/pages/des-moines/page"));
const CedarRapidsPage = lazy(() => import("@/pages/cedar-rapids/page"));
const IowaCityPage = lazy(() => import("@/pages/iowa-city/page"));
const DavenportPage = lazy(() => import("@/pages/davenport/page"));
const SiouxCityPage = lazyPage(() => import("@/pages/sioux-city/page"));
const WaterlooIAPage = lazyPage(() => import("@/pages/waterloo/page"));
const CouncilBluffsPage = lazyPage(() => import("@/pages/council-bluffs/page"));
const AmesPage = lazyPage(() => import("@/pages/ames/page"));
const DubuquePage = lazyPage(() => import("@/pages/dubuque/page"));
const WestDesMoinesPage = lazyPage(() => import("@/pages/west-des-moines/page"));
const AnkenyPage = lazyPage(() => import("@/pages/ankeny/page"));
const MasonCityPage = lazyPage(() => import("@/pages/mason-city/page"));
const CoralvillePage = lazyPage(() => import("@/pages/coralville/page"));
const BettendorfPage = lazyPage(() => import("@/pages/bettendorf/page"));
const FortDodgePage = lazyPage(() => import("@/pages/fort-dodge/page"));
const OttumwaPage = lazyPage(() => import("@/pages/ottumwa/page"));
const PellaPage = lazyPage(() => import("@/pages/pella/page"));
const BoonePage = lazyPage(() => import("@/pages/boone/page"));
const BurlingtonIAPage = lazyPage(() => import("@/pages/burlington-ia/page"));
const ClintonIAPage = lazyPage(() => import("@/pages/clinton-ia/page"));
const DecorahPage = lazyPage(() => import("@/pages/decorah/page"));
const GrinnellPage = lazyPage(() => import("@/pages/grinnell/page"));
const IndianolaPage = lazyPage(() => import("@/pages/indianola/page"));
const JohnstonIAPage = lazyPage(() => import("@/pages/johnston-ia/page"));
const KeokukPage = lazyPage(() => import("@/pages/keokuk/page"));
const MarionIAPage = lazyPage(() => import("@/pages/marion-ia/page"));
const MarshalltownPage = lazyPage(() => import("@/pages/marshalltown/page"));
const MuscatinePage = lazyPage(() => import("@/pages/muscatine/page"));
const NewtonIAPage = lazyPage(() => import("@/pages/newton-ia/page"));
const NorthLibertyPage = lazyPage(() => import("@/pages/north-liberty/page"));
const OskaloosaPage = lazyPage(() => import("@/pages/oskaloosa/page"));
const SpencerPage = lazyPage(() => import("@/pages/spencer/page"));
const UrbandalePage = lazyPage(() => import("@/pages/urbandale/page"));
const WaverlyPage = lazyPage(() => import("@/pages/waverly/page"));
const AltoonaIAPage = lazyPage(() => import("@/pages/altoona-ia/page"));
const PlymouthPage = lazy(() => import("@/pages/plymouth/page"));
const ExeterPage = lazy(() => import("@/pages/exeter/page"));
const HooksettPage = lazy(() => import("@/pages/hooksett/page"));
const MerrimackPage = lazy(() => import("@/pages/merrimack/page"));
const NewHampshirePage = lazy(() => import("@/pages/newhampshire/page"));
const ManchesterPage = lazy(() => import("@/pages/manchester/page"));
const NashuaPage = lazy(() => import("@/pages/nashua/page"));
const ConcordNHPage = lazy(() => import("@/pages/concord-nh/page"));
const PortsmouthPage = lazy(() => import("@/pages/portsmouth/page"));
const KeenePage = lazy(() => import("@/pages/keene/page"));
const DoverNHPage = lazy(() => import("@/pages/dover/page"));
const LaconiaPage = lazy(() => import("@/pages/laconia/page"));
const LebanonPage = lazy(() => import("@/pages/lebanon/page"));
const RochesterNHPage = lazy(() => import("@/pages/rochester-nh/page"));
const MinneapolisPage = lazy(() => import("@/pages/minneapolis/page"));
const StPaulMNPage = lazy(() => import("@/pages/st-paul/page"));
const DuluthMNPage = lazy(() => import("@/pages/duluth/page"));
const RochesterMNPage = lazy(() => import("@/pages/rochester-mn/page"));
const BloomingtonMNPage = lazy(() => import("@/pages/bloomington/page"));
const BrooklynParkPage = lazy(() => import("@/pages/brooklyn-park/page"));
const PlymouthMNPage = lazy(() => import("@/pages/plymouth-mn/page"));
const MapleGroveMNPage = lazy(() => import("@/pages/maple-grove/page"));
const WoodburyMNPage = lazy(() => import("@/pages/woodbury/page"));
const EaganMNPage = lazy(() => import("@/pages/eagan/page"));
const EdenPrairieMNPage = lazy(() => import("@/pages/eden-prairie/page"));
const BurnsvilleMNPage = lazy(() => import("@/pages/burnsville/page"));
const BlaineMNPage = lazy(() => import("@/pages/blaine/page"));
const LakevilleMNPage = lazy(() => import("@/pages/lakeville/page"));
const MinnetonkaMNPage = lazy(() => import("@/pages/minnetonka/page"));
const StCloudMNPage = lazy(() => import("@/pages/st-cloud/page"));
const EdinaMNPage = lazy(() => import("@/pages/edina/page"));
const AppleValleyMNPage = lazy(() => import("@/pages/apple-valley/page"));
const StLouisParkMNPage = lazy(() => import("@/pages/st-louis-park/page"));
const MoorheadMNPage = lazy(() => import("@/pages/moorhead/page"));
const MankatoMNPage = lazy(() => import("@/pages/mankato/page"));
const ShakopeeMNPage = lazy(() => import("@/pages/shakopee/page"));
const CoonRapidsMNPage = lazy(() => import("@/pages/coon-rapids/page"));
const MaplewoodMNPage = lazy(() => import("@/pages/maplewood/page"));
const ChanhassenMNPage = lazy(() => import("@/pages/chanhassen/page"));
const RichfieldMNPage = lazy(() => import("@/pages/richfield/page"));
const RosevilleMNPage = lazy(() => import("@/pages/roseville-mn/page"));
const InverGroveHeightsMNPage = lazy(() => import("@/pages/inver-grove-heights/page"));
const AndoverMNPage = lazy(() => import("@/pages/andover/page"));
const AustinMNPage = lazy(() => import("@/pages/austin-mn/page"));
const WestStPaulMNPage = lazy(() => import("@/pages/west-st-paul/page"));
const NorthfieldMNPage = lazy(() => import("@/pages/northfield/page"));
const OwatonnaMNPage = lazy(() => import("@/pages/owatonna/page"));
const BrainerdMNPage = lazy(() => import("@/pages/brainerd/page"));
const WinonaMNPage = lazy(() => import("@/pages/winona/page"));
const FaribaultMNPage = lazy(() => import("@/pages/faribault/page"));
const BemidjiMNPage = lazy(() => import("@/pages/bemidji/page"));
const WillmarMNPage = lazy(() => import("@/pages/willmar/page"));
const DerryPage = lazy(() => import("@/pages/derry/page"));
const LondonderryPage = lazy(() => import("@/pages/londonderry/page"));
const HudsonNHPage = lazy(() => import("@/pages/hudson/page"));
const HamptonPage = lazy(() => import("@/pages/hampton/page"));
const MinnesotaPage = lazy(() => import("@/pages/minnesota/page"));
// Georgia pages
const AthensPage = lazy(() => import("@/pages/athens/page"));
const AtlantaPage = lazy(() => import("@/pages/atlanta/page"));
const DuluthPage = lazy(() => import("@/pages/duluth/page.tsx"));
const DaltonPage = lazy(() => import("@/pages/dalton/page"));
const DouglasvillePage = lazy(() => import("@/pages/douglasville/page"));
const GeorgiaPage = lazy(() => import("@/pages/georgia/page"));
const BuckheadPage = lazy(() => import("@/pages/buckhead/page"));
const MariettaPage = lazy(() => import("@/pages/marietta/page"));
const DunwoodyPage = lazy(() => import("@/pages/dunwoody/page"));
const RoswellPage = lazy(() => import("@/pages/roswell/page"));
const SandySpringsPage = lazy(() => import("@/pages/sandysprings/page"));
const JohnsCreekPage = lazy(() => import("@/pages/johnscreek/page"));
const SuwaneePage = lazy(() => import("@/pages/suwanee/page"));
const MiltonPage = lazy(() => import("@/pages/milton/page"));
const PeachtreeCornersPage = lazy(() => import("@/pages/peachtreecorners/page"));
const CarrolltonPage = lazy(() => import("@/pages/carrollton/page"));
const ColumbusPage = lazy(() => import("@/pages/columbus/page"));
const AlbanyPage = lazy(() => import("@/pages/albany/page"));
const AlpharettaPage = lazy(() => import("@/pages/alpharetta/page"));
const DecaturPage = lazy(() => import("@/pages/decatur/page"));
const SavannahPage = lazy(() => import("@/pages/savannah/page"));
const AugustaPage = lazy(() => import("@/pages/augusta/page"));

const LexingtonPage = lazy(() => import("@/pages/lexington/page"));
const LouisvillePage = lazy(() => import("@/pages/louisville/page"));
const CovingtonPage = lazy(() => import("@/pages/covington/page"));
const FlorencePage = lazy(() => import("@/pages/florence/page"));

// North Carolina
const CharlottePage = lazy(() => import("@/pages/charlotte/page"));
const RaleighPage = lazy(() => import("@/pages/raleigh/page"));
const GreensboroPage = lazy(() => import("@/pages/greensboro/page"));
const AshevillePage = lazy(() => import("@/pages/asheville/page"));
const WilmingtonPage = lazy(() => import("@/pages/wilmington/page"));
const WinstonSalemPage = lazy(() => import("@/pages/winston-salem/page"));
const DurhamPage = lazy(() => import("@/pages/durham/page"));
const FayettevillePage = lazy(() => import("@/pages/fayetteville/page"));
const CaryPage = lazy(() => import("@/pages/cary/page"));

const NewportPage = lazy(() => import("@/pages/newport/page"));
const ErlangerPage = lazy(() => import("@/pages/erlanger/page"));
const FortThomasPage = lazy(() => import("@/pages/fort-thomas/page"));
const IndependencePage = lazy(() => import("@/pages/independence/page"));

const FrankfortPage = lazy(() => import("@/pages/frankfort/page"));
const GeorgetownPage = lazy(() => import("@/pages/georgetown/page"));
const DanvillePage = lazy(() => import("@/pages/danville/page"));
const ElizabethtownPage = lazy(() => import("@/pages/elizabethtown/page"));

const PaducahPage = lazy(() => import("@/pages/paducah/page"));
const OwensboroPage = lazy(() => import("@/pages/owensboro/page"));
const HendersonPage = lazy(() => import("@/pages/henderson/page"));
const HopkinsvillePage = lazy(() => import("@/pages/hopkinsville/page"));
const GlasgowPage = lazy(() => import("@/pages/glasgow/page"));
const SomersetPage = lazy(() => import("@/pages/somerset/page"));
const MoreheadPage = lazy(() => import("@/pages/morehead/page"));
const ShelbyvillePage = lazy(() => import("@/pages/shelbyville/page"));
const MountWashingtonPage = lazy(() => import("@/pages/mount-washington/page"));
const ShivelyPage = lazy(() => import("@/pages/shively/page"));
const JeffersontownPage = lazy(() => import("@/pages/jeffersontown/page"));
const VirginiaPage = lazy(() => import("@/pages/virginia/page"));
const RichmondVaPage = lazy(() => import("@/pages/richmond-va/page"));
const ArlingtonVaPage = lazy(() => import("@/pages/arlington-va/page"));
const McLeanVaPage = lazy(() => import("@/pages/mclean-va/page"));
const AlexandriaVaPage = lazy(() => import("@/pages/alexandria-va/page"));
const VirginiaBeachPage = lazy(() => import("@/pages/virginia-beach/page"));
const NorfolkVaPage = lazy(() => import("@/pages/norfolk-va/page"));
const FortMitchellPage = lazy(() => import("@/pages/fort-mitchell/page"));
const MiddlesboroughPage = lazy(() => import("@/pages/middlesborough/page"));
const MurrayPage = lazy(() => import("@/pages/murray/page"));
const NicholasvillePage = lazy(() => import("@/pages/nicholasville/page"));
const RadcliffPage = lazy(() => import("@/pages/radcliff/page"));
const VersaillesPage = lazy(() => import("@/pages/versailles/page"));
const WinchesterPage = lazy(() => import("@/pages/winchester/page"));

const LynchburgVaPage = lazy(() => import("@/pages/lynchburg-va/page"));
const FredericksburgVaPage = lazy(() => import("@/pages/fredericksburg-va/page"));
const NewportNewsVaPage = lazy(() => import("@/pages/newport-news-va/page"));
const WilliamsburgVaPage = lazy(() => import("@/pages/williamsburg-va/page"));

const ManassasVaPage = lazy(() => import("@/pages/manassas-va/page"));
const SuffolkVaPage = lazy(() => import("@/pages/suffolk-va/page"));
const HamptonVaPage = lazy(() => import("@/pages/hampton-va/page"));
const PetersburgVaPage = lazy(() => import("@/pages/petersburg-va/page"));
const LeesburgVaPage = lazy(() => import("@/pages/leesburg-va/page"));
const AshburnVaPage = lazy(() => import("@/pages/ashburn-va/page"));
const WoodbridgeVaPage = lazy(() => import("@/pages/woodbridge-va/page"));
const HerndonVaPage = lazy(() => import("@/pages/herndon-va/page"));
const GlenAllenVaPage = lazy(() => import("@/pages/glen-allen-va/page"));
const MidlothianVaPage = lazy(() => import("@/pages/midlothian-va/page"));
const MechanicsvilleVaPage = lazy(() => import("@/pages/mechanicsville-va/page"));
const ShortPumpVaPage = lazy(() => import("@/pages/short-pump-va/page"));
const FallsChurchVaPage = lazy(() => import("@/pages/falls-church-va/page"));
const TysonsVaPage = lazy(() => import("@/pages/tysons-va/page"));
const SpringfieldVaPage = lazy(() => import("@/pages/springfield-va/page"));
const BurkeVaPage = lazy(() => import("@/pages/burke-va/page"));
const CentrevilleVaPage = lazy(() => import("@/pages/centreville-va/page"));
const ColonialHeightsVaPage = lazy(() => import("@/pages/colonial-heights-va/page"));
const HopewellVaPage = lazy(() => import("@/pages/hopewell-va/page"));
const ChesterVaPage = lazy(() => import("@/pages/chester-va/page"));

const MansfieldPage = lazy(() => import("@/pages/mansfield/page"));
const MentorPage = lazy(() => import("@/pages/mentor/page"));
const BeavercreekPage = lazy(() => import("@/pages/beavercreek/page"));
const DublinPage = lazy(() => import("@/pages/dublin/page"));
const FindlayPage = lazy(() => import("@/pages/findlay/page"));
const LimaPage = lazy(() => import("@/pages/lima-oh/page"));
const MarionPage = lazy(() => import("@/pages/marion-oh/page"));
const KingOfPrussiaPage = lazy(() => import("@/pages/king-of-prussia/page"));
const LehighValleyPage = lazy(() => import("@/pages/lehigh-valley/page"));
const ChambersburgPage = lazy(() => import("@/pages/chambersburg/page"));
const WilkesBarrePage = lazy(() => import("@/pages/wilkes-barre/page"));
const DelawarePage = lazy(() => import("@/pages/delaware-oh/page"));
const MedinaPage = lazy(() => import("@/pages/medina/page"));
const WarrenPage = lazy(() => import("@/pages/warren-oh/page"));
const StrongsvillePage = lazy(() => import("@/pages/strongsville/page"));
const FairfieldPage = lazy(() => import("@/pages/fairfield-oh/page"));
const HuberHeightsPage = lazy(() => import("@/pages/huber-heights/page"));
const ReynoldsburgPage = lazy(() => import("@/pages/reynoldsburg/page"));
const WoosterPage = lazy(() => import("@/pages/wooster/page"));
const ZanesvillePage = lazy(() => import("@/pages/zanesville/page"));
const LancasterOhPage = lazy(() => import("@/pages/lancaster-oh/page"));
const AthensOhPage = lazy(() => import("@/pages/athens-oh/page"));
const BowlingGreenOhPage = lazy(() => import("@/pages/bowling-green-oh/page"));

const HighPointPage = lazy(() => import("@/pages/high-point/page"));
const ChapelHillPage = lazy(() => import("@/pages/chapel-hill/page"));
const ConcordNcPage = lazy(() => import("@/pages/concord-nc/page"));
const GastoniaPage = lazy(() => import("@/pages/gastonia/page"));

const JacksonvilleNcPage = lazy(() => import("@/pages/jacksonville-nc/page"));
const BurlingtonPage = lazy(() => import("@/pages/burlington/page"));
const HickoryPage = lazy(() => import("@/pages/hickory/page"));

const GreenvillePage = lazy(() => import("@/pages/greenville/page"));
const GoldsboroPage = lazy(() => import("@/pages/goldsboro/page"));
const RockyMountPage = lazy(() => import("@/pages/rocky-mount/page"));

const WilsonPage = lazy(() => import("@/pages/wilson/page"));
const NewBernPage = lazy(() => import("@/pages/new-bern/page"));
const WakeForestPage = lazy(() => import("@/pages/wake-forest/page"));
const ApexPage = lazy(() => import("@/pages/apex/page"));
const HuntersvillePage = lazy(() => import("@/pages/huntersville/page"));

const KannapolisPage = lazy(() => import("@/pages/kannapolis/page"));
const MooresvillePage = lazy(() => import("@/pages/mooresville/page"));
const MatthewsPage = lazy(() => import("@/pages/matthews/page"));
const HollySpringsPage = lazy(() => import("@/pages/holly-springs/page"));
const CorneliusPage = lazy(() => import("@/pages/cornelius/page"));
const DavidsonPage = lazy(() => import("@/pages/davidson/page"));
const FuquayVarinaPage = lazy(() => import("@/pages/fuquay-varina/page"));
const MorrisvillePage = lazy(() => import("@/pages/morrisville/page"));
const GarnerPage = lazy(() => import("@/pages/garner/page"));
const StatesvillePage = lazy(() => import("@/pages/statesville/page"));
const SalisburyPage = lazy(() => import("@/pages/salisbury/page"));
const IndianTrailPage = lazy(() => import("@/pages/indian-trail/page"));
const AberdeenPage = lazy(() => import("@/pages/aberdeen/page"));
const AsheboroPage = lazy(() => import("@/pages/asheboro/page"));
const ClaytonPage = lazy(() => import("@/pages/clayton/page"));
const HarrisburgPage = lazy(() => import("@/pages/harrisburg/page"));
const HendersonvillePage = lazy(() => import("@/pages/hendersonville/page"));
const KernersvillePage = lazy(() => import("@/pages/kernersville/page"));
const LelandPage = lazy(() => import("@/pages/leland/page"));
const SanfordPage = lazy(() => import("@/pages/sanford/page"));
const ThomasvillePage = lazy(() => import("@/pages/thomasville/page"));

const RichmondPage = lazy(() => import("@/pages/richmond/page"));

const ChesapeakeVaPage = lazy(() => import("@/pages/chesapeake-va/page"));
const FairfaxVaPage = lazy(() => import("@/pages/fairfax-va/page"));
const PortsmouthVaPage = lazy(() => import("@/pages/portsmouth-va/page"));
const ViennaVaPage = lazy(() => import("@/pages/vienna-va/page"));
const RestonVaPage = lazy(() => import("@/pages/reston-va/page"));
const CharlottesvilleVaPage = lazy(() => import("@/pages/charlottesville-va/page"));
const RoanokeVaPage = lazy(() => import("@/pages/roanoke-va/page"));
const HarrisonburgVaPage = lazy(() => import("@/pages/harrisonburg-va/page"));
const WinchesterVaPage = lazy(() => import("@/pages/winchester-va/page"));

const BlacksburgVaPage = lazy(() => import("@/pages/blacksburg-va/page"));
const DanvilleVaPage = lazy(() => import("@/pages/danville-va/page"));

// Rhode Island city pages
const ProvidencePage = lazy(() => import("@/pages/providence/page"));
const WarwickPage = lazy(() => import("@/pages/warwick/page"));
const CranstonPage = lazy(() => import("@/pages/cranston/page"));
const PawtucketPage = lazy(() => import("@/pages/pawtucket/page"));
const NewportRIPage = lazy(() => import("@/pages/newport-ri/page"));
const WoonsocketPage = lazy(() => import("@/pages/woonsocket/page"));
const EastProvidencePage = lazy(() => import("@/pages/east-providence/page"));

const RhodeIslandPage = lazy(() => import("@/pages/rhode-island/page"));
const ConnecticutPage = lazy(() => import("@/pages/connecticut/page"));
const HartfordPage = lazy(() => import("@/pages/hartford/page"));
const NewHavenPage = lazy(() => import("@/pages/new-haven/page"));
const StamfordPage = lazy(() => import("@/pages/stamford/page"));
const BridgeportPage = lazy(() => import("@/pages/bridgeport/page"));
const WaterburyPage = lazy(() => import("@/pages/waterbury/page"));
const GreenwichPage = lazy(() => import("@/pages/greenwich/page"));

const JohnstonPage = lazy(() => import("@/pages/johnston/page"));
const CoventryPage = lazy(() => import("@/pages/coventry/page"));
const WestWarwickPage = lazy(() => import("@/pages/west-warwick/page"));
const CumberlandRiPage = lazy(() => import("@/pages/cumberland-ri/page"));
const NorthProvidencePage = lazy(() => import("@/pages/north-providence/page"));
const NewYorkCityPage = lazy(() => import("@/pages/new-york-city/page"));
const NewYorkPage = lazy(() => import("@/pages/newyork/page"));
const BuffaloPage = lazy(() => import("@/pages/buffalo/page"));
const AlbanyNYPage = lazy(() => import("@/pages/albany-ny/page"));
const BrooklynPage = lazy(() => import("@/pages/brooklyn/page"));
const QueensPage = lazy(() => import("@/pages/queens/page"));
const RochesterPage = lazy(() => import("@/pages/rochester/page"));
const SyracusePage = lazy(() => import("@/pages/syracuse/page"));
const YonkersPage = lazy(() => import("@/pages/yonkers/page"));
const TheBronxPage = lazy(() => import("@/pages/the-bronx/page"));
const UticaPage = lazy(() => import("@/pages/utica/page"));
const SchenectadyPage = lazy(() => import("@/pages/schenectady/page"));
const TroyPage = lazy(() => import("@/pages/troy/page"));
const SaratogaSpringsPage = lazy(() => import("@/pages/saratoga-springs/page"));
const PlattsburghPage = lazy(() => import("@/pages/plattsburgh/page"));
const WatertownPage = lazy(() => import("@/pages/watertown/page"));
const OgdensburgPage = lazy(() => import("@/pages/ogdensburg/page"));
const GlensFallsPage = lazy(() => import("@/pages/glens-falls/page"));

const KingstonPage = lazy(() => import("@/pages/kingston/page"));
const MiddletownNYPage = lazy(() => import("@/pages/middletown-ny/page"));
const NewPaltzPage = lazy(() => import("@/pages/new-paltz/page"));
const BeaconPage = lazy(() => import("@/pages/beacon/page"));
const FishkillPage = lazy(() => import("@/pages/fishkill/page"));
const MountVernonPage = lazy(() => import("@/pages/mount-vernon/page"));
const TarrytownPage = lazy(() => import("@/pages/tarrytown/page"));
const RhinebeckPage = lazy(() => import("@/pages/rhinebeck/page"));
const HydeParkPage = lazy(() => import("@/pages/hyde-park/page"));
const RedHookPage = lazy(() => import("@/pages/red-hook/page"));
const HudsonPage = lazy(() => import("@/pages/hudson-ny/page"));
const CatskillPage = lazy(() => import("@/pages/catskill/page"));
const LakeGeorgePage = lazy(() => import("@/pages/lake-george/page"));
const BallstonSpaPage = lazy(() => import("@/pages/ballston-spa/page"));
const AmsterdamNYPage = lazy(() => import("@/pages/amsterdam-ny/page"));
const GloversvillePage = lazy(() => import("@/pages/gloversville/page"));
const RomeNYPage = lazy(() => import("@/pages/rome-ny/page"));
const MalonePage = lazy(() => import("@/pages/malone/page"));
const TupperLakePage = lazy(() => import("@/pages/tupper-lake/page"));

const IslipPage = lazy(() => import("@/pages/islip/page"));
const LevittownPage = lazy(() => import("@/pages/levittown/page"));
const SmithtownPage = lazy(() => import("@/pages/smithtown/page"));
const WestBabylonPage = lazy(() => import("@/pages/west-babylon/page"));

const NiagaraFallsPage = lazy(() => import("@/pages/niagara-falls/page"));
const ChathamPage = lazy(() => import("@/pages/chatham/page"));

const NorwalkPage = lazy(() => import("@/pages/norwalk/page"));
const DanburyPage = lazy(() => import("@/pages/danbury/page"));
const BristolPage = lazy(() => import("@/pages/bristol/page"));
const NewBritainPage = lazy(() => import("@/pages/new-britain/page"));

const WestHartfordPage = lazy(() => import("@/pages/west-hartford/page"));
const MilfordPage = lazy(() => import("@/pages/milford/page"));

const MassachusettsPage = lazy(() => import("@/pages/massachusetts/page"));

// Vermont pages
const VermontPage = lazy(() => import("@/pages/vermont/page"));
const BurlingtonVtPage = lazy(() => import("@/pages/burlington-vt/page"));
const MontpelierPage = lazy(() => import("@/pages/montpelier/page"));
const RutlandPage = lazy(() => import("@/pages/rutland/page"));
const StowePage = lazy(() => import("@/pages/stowe/page"));
const BrattleboroPage = lazy(() => import("@/pages/brattleboro/page"));
const BenningtonPage = lazy(() => import("@/pages/bennington/page"));
const MiddleburyPage = lazy(() => import("@/pages/middlebury/page"));
const WoodstockVtPage = lazy(() => import("@/pages/woodstock-vt/page"));
const SpringfieldVtPage = lazy(() => import("@/pages/springfield-vt/page"));
const WhiteRiverJunctionPage = lazy(() => import("@/pages/white-river-junction/page"));

const BostonPage = lazy(() => import("@/pages/boston/page"));
const WorcesterPage = lazy(() => import("@/pages/worcester/page"));
const SpringfieldMaPage = lazy(() => import("@/pages/springfield-ma/page"));
const LowellPage = lazy(() => import("@/pages/lowell/page"));
const CambridgePage = lazy(() => import("@/pages/cambridge/page"));
const QuincyMaPage = lazy(() => import("@/pages/quincy-ma/page"));
const NewBedfordPage = lazy(() => import("@/pages/new-bedford/page"));
const BrocktonPage = lazy(() => import("@/pages/brockton/page"));
const LynnPage = lazy(() => import("@/pages/lynn/page"));
const FallRiverPage = lazy(() => import("@/pages/fall-river/page"));
const NewtonPage = lazy(() => import("@/pages/newton/page"));
const SomervillePage = lazy(() => import("@/pages/somerville/page"));
const NantucketPage = lazy(() => import("@/pages/nantucket/page"));
const MarthasVineyardPage = lazy(() => import("@/pages/marthas-vineyard/page"));

const AltoonaPage = lazy(() => import("@/pages/altoona/page"));
const EastonPage = lazy(() => import("@/pages/easton/page"));
const NorristownPage = lazy(() => import("@/pages/norristown/page"));
const PottstownPage = lazy(() => import("@/pages/pottstown/page"));
const BloomsburgPage = lazy(() => import("@/pages/bloomsburg/page"));
const ChesterPage = lazy(() => import("@/pages/chester/page"));
const GettysburgPage = lazy(() => import("@/pages/gettysburg/page"));
const HazletonPage = lazy(() => import("@/pages/hazleton/page"));
const JohnstownPage = lazy(() => import("@/pages/johnstown/page"));
const LebanonPaPage = lazy(() => import("@/pages/lebanon-pa/page"));
const MainLinePage = lazy(() => import("@/pages/main-line/page"));
const McKeesportPage = lazy(() => import("@/pages/mckeesport/page"));
const MonroevillePage = lazy(() => import("@/pages/monroeville/page"));
const MountPoconoPage = lazy(() => import("@/pages/mount-pocono/page"));
const NewCastlePage = lazy(() => import("@/pages/new-castle/page"));
const UpperDarbyPage = lazy(() => import("@/pages/upper-darby/page"));
const WilliamsportPage = lazy(() => import("@/pages/williamsport/page"));

const HanoverPage = lazy(() => import("@/pages/hanover/page"));
const GoffstownPage = lazy(() => import("@/pages/goffstown/page"));
const WindhamNHPage = lazy(() => import("@/pages/windham-nh/page"));
const MilfordNHPage = lazy(() => import("@/pages/milford-nh/page"));
const ClaremontPage = lazy(() => import("@/pages/claremont/page"));
const SomersworthPage = lazy(() => import("@/pages/somersworth/page"));
const FranklinNHPage = lazy(() => import("@/pages/franklin-nh/page"));
const BerlinPage = lazy(() => import("@/pages/berlin/page"));
const PelhamPage = lazy(() => import("@/pages/pelham/page"));
const RaymondPage = lazy(() => import("@/pages/raymond/page"));
const DurhamNHPage = lazy(() => import("@/pages/durham-nh/page"));
const NewmarketPage = lazy(() => import("@/pages/newmarket/page"));
const ConwayNhPage = lazy(() => import("@/pages/conway-nh/page"));
const WolfeboroPage = lazy(() => import("@/pages/wolfeboro/page"));
const LittletonNhPage = lazy(() => import("@/pages/littleton-nh/page"));
const LancasterNhPage = lazy(() => import("@/pages/lancaster-nh/page"));
const HennikerPage = lazy(() => import("@/pages/henniker/page"));
const JaffreyPage = lazy(() => import("@/pages/jaffrey/page"));
const PeterboroughPage = lazy(() => import("@/pages/peterborough/page"));
const DelawareStatePage = lazy(() => import("@/pages/delaware/page"));
const WilmingtonDePage = lazy(() => import("@/pages/wilmington-de/page"));
const DoverDePage = lazy(() => import("@/pages/dover-de/page"));
const NewarkDePage = lazy(() => import("@/pages/newark-de/page"));

const MiddletownDePage = lazy(() => import("@/pages/middletown-de/page"));
const RehobothBeachPage = lazy(() => import("@/pages/rehoboth-beach/page"));
const NewcastleDePage = lazy(() => import("@/pages/newcastle-de/page"));
const SmyrnaDePage = lazy(() => import("@/pages/smyrna-de/page"));
const MilfordDePage = lazy(() => import("@/pages/milford-de/page"));
const SeafordDePage = lazy(() => import("@/pages/seaford-de/page"));
const GeorgetownDePage = lazy(() => import("@/pages/georgetown-de/page"));
const JacksonMIPage = lazy(() => import("@/pages/jackson-mi/page"));
const CantonMIPage = lazy(() => import("@/pages/canton-mi/page"));
const FarmingtonHillsPage = lazy(() => import("@/pages/farmington-hills/page"));
const RochesterHillsPage = lazy(() => import("@/pages/rochester-hills/page"));
const LivoniaPage = lazy(() => import("@/pages/livonia/page"));
const RoyalOakPage = lazy(() => import("@/pages/royal-oak/page"));
const TaylorPage = lazy(() => import("@/pages/taylor/page"));
const WestlandPage = lazy(() => import("@/pages/westland/page"));
const DearbornHeightsPage = lazy(() => import("@/pages/dearborn-heights/page"));
const RosevilleMIPage = lazy(() => import("@/pages/roseville-mi/page"));
const ClintonTownshipPage = lazy(() => import("@/pages/clinton-township/page"));
const EastpointePage = lazy(() => import("@/pages/eastpointe/page"));

const PortHuronPage = lazy(() => import("@/pages/port-huron/page"));
const SaintClairShoresPage = lazy(() => import("@/pages/saint-clair-shores/page"));
const SaginawPage = lazy(() => import("@/pages/saginaw/page"));
const TraverseCityPage = lazy(() => import("@/pages/traverse-city/page"));
const BattleCreekPage = lazy(() => import("@/pages/battle-creek/page"));
const BayCityPage = lazy(() => import("@/pages/bay-city/page"));
const MidlandPage = lazy(() => import("@/pages/midland/page"));
const HollandPage = lazy(() => import("@/pages/holland/page"));
const WyomingPage = lazy(() => import("@/pages/wyoming/page"));
const KentwoodPage = lazy(() => import("@/pages/kentwood/page"));
const SaultSteMariePage = lazy(() => import("@/pages/sault-ste-marie/page"));
const MarquettePage = lazy(() => import("@/pages/marquette/page"));
const PetoskeyPage = lazy(() => import("@/pages/petoskey/page"));
const LudingtonPage = lazy(() => import("@/pages/ludington/page"));
const StJosephMIPage = lazy(() => import("@/pages/st-joseph-mi/page"));
const AdrianPage = lazy(() => import("@/pages/adrian/page"));
const AlpenaPage = lazy(() => import("@/pages/alpena/page"));
const EscanabaPage = lazy(() => import("@/pages/escanaba/page"));
const GrandBlancPage = lazy(() => import("@/pages/grand-blanc/page"));
const HoughtonPage = lazy(() => import("@/pages/houghton/page"));
const ManisteePage = lazy(() => import("@/pages/manistee/page"));
const SaginawTownshipPage = lazy(() => import("@/pages/saginaw-township/page"));

const MacombCountyPage = lazy(() => import("@/pages/macomb-county/page"));
const KentCountyPage = lazy(() => import("@/pages/kent-county/page"));
const OttawaCountyPage = lazy(() => import("@/pages/ottawa-county/page"));

// Washington pages
const WashingtonPage = lazy(() => import("@/pages/washington/page"));
const SeattlePage = lazy(() => import("@/pages/seattle/page"));
const SpokanuPage = lazy(() => import("@/pages/spokane/page"));
const SpokaneValleyPage = lazy(() => import("@/pages/spokane-valley/page"));
const TacomaPage = lazy(() => import("@/pages/tacoma/page"));
const BellevuePage = lazy(() => import("@/pages/bellevue/page"));
const RentonPage = lazy(() => import("@/pages/renton/page"));
const KirklandPage = lazy(() => import("@/pages/kirkland/page"));
const RedmondWAPage = lazy(() => import("@/pages/redmond-wa/page"));
const EverettPage = lazy(() => import("@/pages/everett/page"));
const BellinghamPage = lazy(() => import("@/pages/bellingham/page"));
const OlympiaPage = lazy(() => import("@/pages/olympia/page"));
const KennewickPage = lazy(() => import("@/pages/kennewick/page"));
const YakimaPage = lazy(() => import("@/pages/yakima/page"));
const MarysvillePage = lazy(() => import("@/pages/marysville/page"));
const FederalWayPage = lazy(() => import("@/pages/federal-way/page"));
const ShorelinePage = lazy(() => import("@/pages/shoreline/page"));
const SammamishPage = lazy(() => import("@/pages/sammamish/page"));
const KentPage = lazy(() => import("@/pages/kent/page"));

// Indiana pages
const IndianaPage = lazy(() => import("@/pages/indiana/page"));
const IndianapolisPage = lazy(() => import("@/pages/indianapolis/page"));
const FortWaynePage = lazy(() => import("@/pages/fort-wayne/page"));
const EvansvillePage = lazy(() => import("@/pages/evansville/page"));
const CarmelPage = lazy(() => import("@/pages/carmel/page"));
const BloomingtonInPage = lazy(() => import("@/pages/bloomington-in/page"));
const ColumbusInPage = lazy(() => import("@/pages/columbus-in/page"));
const ElkhartPage = lazy(() => import("@/pages/elkhart/page"));
const FishersPage = lazy(() => import("@/pages/fishers/page"));
const GaryPage = lazy(() => import("@/pages/gary/page"));
const GoshenPage = lazy(() => import("@/pages/goshen/page"));
const GreenwoodInPage = lazy(() => import("@/pages/greenwood-in/page"));
const HammondInPage = lazy(() => import("@/pages/hammond-in/page"));
const KokomoPage = lazy(() => import("@/pages/kokomo/page"));
const LafayetteInPage = lazy(() => import("@/pages/lafayette-in/page"));
const MerrillvillePage = lazy(() => import("@/pages/merrillville/page"));
const MishawakaPage = lazy(() => import("@/pages/mishawaka/page"));
const MunciePage = lazy(() => import("@/pages/muncie/page"));
const NoblesvillePage = lazy(() => import("@/pages/noblesville/page"));
const SouthBendPage = lazy(() => import("@/pages/south-bend/page"));
const TerreHautePage = lazy(() => import("@/pages/terre-haute/page"));
const ValparaisoPage = lazy(() => import("@/pages/valparaiso/page"));
const GrandHavenPage = lazy(() => import("@/pages/grand-haven/page"));
const PortagePage = lazy(() => import("@/pages/portage/page"));
const MonroePage = lazy(() => import("@/pages/monroe/page"));
const MuskegonPage = lazy(() => import("@/pages/muskegon/page"));
const YpsilantiPage = lazy(() => import("@/pages/ypsilanti/page"));

const TennesseePage = lazy(() => import("@/pages/tennessee/page"));
const NashvillePage = lazy(() => import("@/pages/nashville/page"));
const MemphisPage = lazy(() => import("@/pages/memphis/page"));
const KnoxvillePage = lazy(() => import("@/pages/knoxville/page"));
const ChattanoogaPage = lazy(() => import("@/pages/chattanooga/page"));
const ClarksvilleTNPage = lazy(() => import("@/pages/clarksville/page"));
const MurfreesboroTNPage = lazy(() => import("@/pages/murfreesboro/page"));
const JohnsonCityTNPage = lazy(() => import("@/pages/johnson-city/page"));
const KingsportTNPage = lazy(() => import("@/pages/kingsport/page"));
const FranklinTNPage = lazy(() => import("@/pages/franklin-tn/page"));
const JacksonTNPage = lazy(() => import("@/pages/jackson/page"));
const CookevilleTNPage = lazy(() => import("@/pages/cookeville/page"));
const BristolTNPage = lazy(() => import("@/pages/bristol/page"));
const OakRidgeTNPage = lazy(() => import("@/pages/oak-ridge/page"));
const MaryvilleTNPage = lazy(() => import("@/pages/maryville-tn/page"));
const ColumbiaTnPage = lazy(() => import("@/pages/columbia-tn/page"));

// Wisconsin pages
const WisconsinPage = lazy(() => import("@/pages/wisconsin/page"));
const AppletonPage = lazy(() => import("@/pages/appleton/page"));
const EauClairePage = lazy(() => import("@/pages/eau-claire/page"));
const KenoshaWiPage = lazy(() => import("@/pages/kenosha-wi/page"));
const WausauPage = lazy(() => import("@/pages/wausau/page"));
const LaCrossePage = lazy(() => import("@/pages/la-crosse/page"));
const OshkoshPage = lazy(() => import("@/pages/oshkosh/page"));
const WaukeshaPage = lazy(() => import("@/pages/waukesha/page"));
const JanesvillePage = lazy(() => import("@/pages/janesville/page"));
const SheboyganPage = lazy(() => import("@/pages/sheboygan/page"));
const SuperiorPage = lazy(() => import("@/pages/superior/page"));
const FondDuLacPage = lazy(() => import("@/pages/fond-du-lac/page"));
const WestBendPage = lazy(() => import("@/pages/west-bend/page"));
const ManitowocPage = lazy(() => import("@/pages/manitowoc/page"));
const BeloitPage = lazy(() => import("@/pages/beloit/page"));
const NeenahPage = lazy(() => import("@/pages/neenah/page"));
const RacineCountyPage = lazy(() => import("@/pages/racine-county/page"));

const DenverPage = lazy(() => import("@/pages/denver/page"));
const ColoradoSpringsPage = lazy(() => import("@/pages/colorado-springs/page"));
const BoulderPage = lazy(() => import("@/pages/boulder/page"));
const FortCollinsPage = lazy(() => import("@/pages/fort-collins/page"));
const AuroraCoPage = lazy(() => import("@/pages/aurora-co/page"));
const ArvadaPage = lazy(() => import("@/pages/arvada/page"));
const PuebloPage = lazy(() => import("@/pages/pueblo/page"));
const LakewoodCoPage = lazy(() => import("@/pages/lakewood-co/page"));
const LongmontPage = lazy(() => import("@/pages/longmont/page"));
const ThorntonPage = lazy(() => import("@/pages/thornton/page"));
const AspenPage = lazy(() => import("@/pages/aspen/page"));
const VailPage = lazy(() => import("@/pages/vail/page"));
const GrandJunctionPage = lazy(() => import("@/pages/grand-junction/page"));
const DurangoPage = lazy(() => import("@/pages/durango/page"));
const CastleRockPage = lazy(() => import("@/pages/castle-rock/page"));
const BreckenridgePage = lazy(() => import("@/pages/breckenridge/page"));
const TelluridePage = lazy(() => import("@/pages/telluride/page"));
const GoldenPage = lazy(() => import("@/pages/golden/page"));
const SteamboatSpringsPage = lazy(() => import("@/pages/steamboat-springs/page"));
const HighlandsRanchPage = lazy(() => import("@/pages/highlands-ranch/page"));
const WestminsterCoPage = lazy(() => import("@/pages/westminster/page"));
const BroomfieldPage = lazy(() => import("@/pages/broomfield/page"));
const CentennialPage = lazy(() => import("@/pages/centennial/page"));
const LittletonPage = lazy(() => import("@/pages/littleton/page"));
const LovelandPage = lazy(() => import("@/pages/loveland/page"));
const ParkerPage = lazy(() => import("@/pages/parker/page"));
const GreeleyPage = lazy(() => import("@/pages/greeley/page"));
const EnglewoodCoPage = lazy(() => import("@/pages/englewood-co/page"));
const ErieCoPage = lazy(() => import("@/pages/erie-co/page"));
const LafayetteCoPage = lazy(() => import("@/pages/lafayette-co/page"));
const LouisvilleCoPage = lazy(() => import("@/pages/louisville-co/page"));
const BrightonPage = lazy(() => import("@/pages/brighton/page"));
const CommerceCityPage = lazy(() => import("@/pages/commerce-city/page"));
const NorthglennPage = lazy(() => import("@/pages/northglenn/page"));
const WheatRidgePage = lazy(() => import("@/pages/wheat-ridge/page"));
const CastlePinesPage = lazy(() => import("@/pages/castle-pines/page"));
const FederalHeightsPage = lazy(() => import("@/pages/federal-heights/page"));
const FountainPage = lazy(() => import("@/pages/fountain/page"));
const LoneTreePage = lazy(() => import("@/pages/lone-tree/page"));
const MontrosePage = lazy(() => import("@/pages/montrose/page"));
const NapervillePage = lazy(() => import("@/pages/naperville/page"));
const AuroraPage = lazy(() => import("@/pages/aurora/page"));
const RockfordPage = lazy(() => import("@/pages/rockford/page"));
const JolietPage = lazy(() => import("@/pages/joliet/page"));
const ChicagoILPage = lazy(() => import("@/pages/chicago/page"));
const SpringfieldILPage = lazy(() => import("@/pages/springfield/page"));
const PeoriaILPage = lazy(() => import("@/pages/peoria/page"));
const ChampaignILPage = lazy(() => import("@/pages/champaign/page"));
const ElginILPage = lazy(() => import("@/pages/elgin/page"));
const WaukeganPage = lazy(() => import("@/pages/waukegan/page"));
const EvanstonPage = lazy(() => import("@/pages/evanston/page"));
const SchaumburgPage = lazy(() => import("@/pages/schaumburg/page"));
const ArlingtonHeightsPage = lazy(() => import("@/pages/arlington-heights/page"));
const CiceroPage = lazy(() => import("@/pages/cicero/page"));
const SkokiePage = lazy(() => import("@/pages/skokie/page"));
const OakParkPage = lazy(() => import("@/pages/oak-park/page"));
const PalatinePage = lazy(() => import("@/pages/palatine/page"));
const DesPlainesPage = lazy(() => import("@/pages/des-plaines/page"));
const BloomingtonILPage = lazy(() => import("@/pages/bloomington-il/page"));
const DecaturILPage = lazy(() => import("@/pages/decatur-il/page"));
const CarbondalePage = lazy(() => import("@/pages/carbondale/page"));
const BellevillePage = lazy(() => import("@/pages/belleville/page"));
const AltonPage = lazy(() => import("@/pages/alton/page"));
const MolinePage = lazy(() => import("@/pages/moline/page"));
const RockIslandPage = lazy(() => import("@/pages/rock-island/page"));
const ElmhurstPage = lazy(() => import("@/pages/elmhurst/page"));
const WheatonPage = lazy(() => import("@/pages/wheaton/page"));
const DownersGrovePage = lazy(() => import("@/pages/downers-grove/page"));
const NormalPage = lazy(() => import("@/pages/normal/page"));
const DekalbPage = lazy(() => import("@/pages/dekalb/page"));
const GalesburgPage = lazy(() => import("@/pages/galesburg/page"));
const QuincyIlPage = lazy(() => import("@/pages/quincy-il/page"));
const GlenviewPage = lazy(() => import("@/pages/glenview/page"));
const HighlandParkPage = lazy(() => import("@/pages/highland-park/page"));
const OrlandParkPage = lazy(() => import("@/pages/orland-park/page"));
const MilwaukeePage = lazy(() => import("@/pages/milwaukee/page"));
const MadisonWiPage = lazy(() => import("@/pages/madison-wi/page"));
const GreenBayPage = lazy(() => import("@/pages/green-bay/page"));
const RacinePage = lazy(() => import("@/pages/racine/page"));
const StLouisPage = lazy(() => import("@/pages/st-louis/page"));
const KansasCityPage = lazy(() => import("@/pages/kansas-city/page"));
const SpringfieldMoPage = lazy(() => import("@/pages/springfield-mo/page"));
const ColumbiaMoPage = lazy(() => import("@/pages/columbia-mo/page"));
const JoplinMOPage = lazy(() => import("@/pages/joplin/page"));
const StJosephMOPage = lazy(() => import("@/pages/st-joseph-mo/page"));
const LeeSummitMOPage = lazy(() => import("@/pages/lees-summit/page"));
const OFallonMOPage = lazy(() => import("@/pages/ofallon-mo/page"));
const IndependenceMOPage = lazy(() => import("@/pages/independence-mo/page"));
const JeffersonCityPage = lazy(() => import("@/pages/jefferson-city/page"));
const CapeGirardeauPage = lazy(() => import("@/pages/cape-girardeau/page"));
const HannibalPage = lazy(() => import("@/pages/hannibal/page"));
const StCharlesPage = lazy(() => import("@/pages/st-charles/page"));
const BlueSpringsMOPage = lazy(() => import("@/pages/blue-springs-mo/page"));
const FlorissantMOPage = lazy(() => import("@/pages/florissant-mo/page"));
const ChesterfieldMOPage = lazy(() => import("@/pages/chesterfield-mo/page"));
const WentzvilleMOPage = lazy(() => import("@/pages/wentzville-mo/page"));
const LibertyMOPage = lazy(() => import("@/pages/liberty-mo/page"));
const KirkwoodMOPage = lazy(() => import("@/pages/kirkwood-mo/page"));
const NixaMOPage = lazy(() => import("@/pages/nixa-mo/page"));
const BallwinMOPage = lazy(() => import("@/pages/ballwin-mo/page"));
const RaytownMOPage = lazy(() => import("@/pages/raytown-mo/page"));
const GladstoneMOPage = lazy(() => import("@/pages/gladstone-mo/page"));
const GrandviewMOPage = lazy(() => import("@/pages/grandview-mo/page"));
const BeltonMOPage = lazy(() => import("@/pages/belton-mo/page"));
const OzarkMOPage = lazy(() => import("@/pages/ozark-mo/page"));
const WarrensburgMOPage = lazy(() => import("@/pages/warrensburg-mo/page"));
const RollaMOPage = lazy(() => import("@/pages/rolla-mo/page"));
const SedaliaMOPage = lazy(() => import("@/pages/sedalia-mo/page"));
const KirksvilleMOPage = lazy(() => import("@/pages/kirksville-mo/page"));
const SikestonMOPage = lazy(() => import("@/pages/sikeston-mo/page"));
const PoplarBluffMOPage = lazy(() => import("@/pages/poplar-bluff-mo/page"));
const LebanonMOPage = lazy(() => import("@/pages/lebanon-mo/page"));
const FultonMOPage = lazy(() => import("@/pages/fulton-mo/page"));

// Nevada pages
const NevadaPage = lazy(() => import("@/pages/nevada/page"));
const LasVegasPage = lazy(() => import("@/pages/las-vegas/page"));
const HendersonNVPage = lazy(() => import("@/pages/henderson-nv/page"));
const RenoPage = lazy(() => import("@/pages/reno/page"));
const SparksPage = lazy(() => import("@/pages/sparks/page"));
const CarsonCityPage = lazy(() => import("@/pages/carson-city/page"));
const ElkoPage = lazy(() => import("@/pages/elko/page"));
const PahrumpPage = lazy(() => import("@/pages/pahrump/page"));
const MesquiteNVPage = lazy(() => import("@/pages/mesquite-nv/page"));
const LaughlinPage = lazy(() => import("@/pages/laughlin/page"));
const BoulderCityPage = lazy(() => import("@/pages/boulder-city/page"));
const FernleyPage = lazy(() => import("@/pages/fernley/page"));
const GardnervillePage = lazy(() => import("@/pages/gardnerville/page"));
const InclineVillagePage = lazy(() => import("@/pages/incline-village/page"));
const BattleMountainPage = lazy(() => import("@/pages/battle-mountain/page"));
const FallonPage = lazy(() => import("@/pages/fallon/page"));
const MindenPage = lazy(() => import("@/pages/minden/page"));
const SpringCreekPage = lazy(() => import("@/pages/spring-creek/page"));
const WinnemuccaNVPage = lazy(() => import("@/pages/winnemucca/page"));
const NorthLasVegasPage = lazy(() => import("@/pages/north-las-vegas/page"));
const SummerlinPage = lazy(() => import("@/pages/summerlin/page"));
const SunValleyPage = lazy(() => import("@/pages/sun-valley/page"));
const SaltLakeCityPage = lazy(() => import("@/pages/salt-lake-city/page"));
const ProvoUTPage = lazy(() => import("@/pages/provo/page"));
const OgdenUTPage = lazy(() => import("@/pages/ogden/page"));
const StGeorgePage = lazy(() => import("@/pages/st-george/page"));
const OremUTPage = lazy(() => import("@/pages/orem/page"));
const LoganUTPage = lazy(() => import("@/pages/logan/page"));
const ParkCityUTPage = lazy(() => import("@/pages/park-city/page"));
const MoabUTPage = lazy(() => import("@/pages/moab/page"));
const SandyUTPage = lazy(() => import("@/pages/sandy-ut/page"));
const CedarCityUTPage = lazy(() => import("@/pages/cedar-city/page"));
const WestValleyCityUTPage = lazy(() => import("@/pages/west-valley-city/page"));
const LaytonUTPage = lazy(() => import("@/pages/layton/page"));

// Alabama city pages
const BirminghamPage = lazy(() => import("@/pages/birmingham/page"));
const HuntsvilleALPage = lazy(() => import("@/pages/huntsville/page"));
const MobileALPage = lazy(() => import("@/pages/mobile-al/page"));
const MontgomeryALPage = lazy(() => import("@/pages/montgomery-al/page"));
const TuscaloosaPage = lazy(() => import("@/pages/tuscaloosa/page"));
const AuburnPage = lazy(() => import("@/pages/auburn/page"));
const DothanPage = lazy(() => import("@/pages/dothan/page"));
const HooverPage = lazy(() => import("@/pages/hoover/page"));
const DecaturALPage = lazy(() => import("@/pages/decatur-al/page"));
const MadisonALPage = lazy(() => import("@/pages/madison-al/page"));
const FlorenceALPage = lazy(() => import("@/pages/florence-al/page"));
const GadsdenALPage = lazy(() => import("@/pages/gadsden-al/page"));

// South Carolina, Alabama, Louisiana, Oklahoma, Utah
const SouthCarolinaPage = lazy(() => import("@/pages/south-carolina/page"));
const AlabamaPage = lazy(() => import("@/pages/alabama/page"));
const LouisianaPage = lazy(() => import("@/pages/louisiana/page"));
const NewOrleansPage = lazy(() => import("@/pages/neworleans/page"));
const BatonRougePage = lazy(() => import("@/pages/batonrouge/page"));
const ShreveportPage = lazy(() => import("@/pages/shreveport/page"));
const LafayetteLAPage = lazy(() => import("@/pages/lafayette/page"));
const LakeCharlesPage = lazy(() => import("@/pages/lakecharles/page"));
const MonroeLAPage = lazy(() => import("@/pages/monroe/page"));
const HoumaPage = lazy(() => import("@/pages/houma/page"));
const SlidellPage = lazy(() => import("@/pages/slidell/page"));
const AlexandriaLAPage = lazy(() => import("@/pages/alexandria-la/page"));
const HammondPage = lazy(() => import("@/pages/hammond/page"));
const BossierCityPage = lazy(() => import("@/pages/bossier-city/page"));
const MandevillePage = lazy(() => import("@/pages/mandeville/page"));
const KennerPage = lazy(() => import("@/pages/kenner/page"));
const MetairiePage = lazy(() => import("@/pages/metairie/page"));
const ThibodauxPage = lazy(() => import("@/pages/thibodaux/page"));
const OklahomaPage = lazy(() => import("@/pages/oklahoma/page"));
const UtahPage = lazy(() => import("@/pages/utah/page"));

// Oklahoma city pages
const OklahomaCityPage = lazy(() => import("@/pages/oklahoma-city/page"));
const TulsaPage = lazy(() => import("@/pages/tulsa/page"));
const NormanPage = lazy(() => import("@/pages/norman/page"));
const EdmondOKPage = lazy(() => import("@/pages/edmond-ok/page"));
const BrokenArrowPage = lazy(() => import("@/pages/broken-arrow/page"));
const LawtonPage = lazy(() => import("@/pages/lawton/page"));
const StillwaterPage = lazy(() => import("@/pages/stillwater/page"));
const EnidPage = lazy(() => import("@/pages/enid/page"));
const MuskogeePage = lazy(() => import("@/pages/muskogee/page"));
const MoorePage = lazy(() => import("@/pages/moore/page"));
const BartlesvillePage = lazy(() => import("@/pages/bartlesville/page"));
const ShawneePage = lazy(() => import("@/pages/shawnee/page"));
const ShawneeKsPage = lazy(() => import("@/pages/shawnee-ks/page"));

// Kansas, Arkansas, New Mexico, Mississippi, West Virginia
const KansasPage = lazy(() => import("@/pages/kansas/page"));
const OverlandParkPage = lazy(() => import("@/pages/overland-park/page"));
const OlathePage = lazy(() => import("@/pages/olathe/page"));
const TopekaPage = lazy(() => import("@/pages/topeka/page"));
const LawrencePage = lazy(() => import("@/pages/lawrence/page"));
const WichitaPage = lazy(() => import("@/pages/wichita/page"));
const LenexaPage = lazy(() => import("@/pages/lenexa/page"));
const SalinaPage = lazy(() => import("@/pages/salina/page"));
const LeawoodPage = lazy(() => import("@/pages/leawood/page"));
const HutchinsonPage = lazy(() => import("@/pages/hutchinson/page"));
const ManhattanPage = lazy(() => import("@/pages/manhattan/page"));
const ArkansasPage = lazy(() => import("@/pages/arkansas/page"));
const NewMexicoPage = lazy(() => import("@/pages/new-mexico/page"));
const AlbuquerquePage = lazy(() => import("@/pages/albuquerque/page"));
const LasCrucesPage = lazy(() => import("@/pages/las-cruces/page"));
const RioRanchoPage = lazy(() => import("@/pages/rio-rancho/page"));
const FarmingtonPage = lazy(() => import("@/pages/farmington/page"));
const ClovisPage = lazy(() => import("@/pages/clovis/page"));
const HobbsPage = lazy(() => import("@/pages/hobbs/page"));
const AlamogordoPage = lazy(() => import("@/pages/alamogordo/page"));
const GallupPage = lazy(() => import("@/pages/gallup/page"));
const LosAlamosPage = lazy(() => import("@/pages/los-alamos/page"));
const RoswellNmPage = lazy(() => import("@/pages/roswell-nm/page"));
const CarlsbadNmPage = lazy(() => import("@/pages/carlsbad-nm/page"));
const MississippiPage = lazy(() => import("@/pages/mississippi/page"));
const WestVirginiaPage = lazy(() => import("@/pages/west-virginia/page"));
const MorgantownPage = lazy(() => import("@/pages/morgantown/page"));
const ParkersburgPage = lazy(() => import("@/pages/parkersburg/page"));
const WheelingPage = lazy(() => import("@/pages/wheeling/page"));
const MartinsburgPage = lazy(() => import("@/pages/martinsburg/page"));
const WeirtonPage = lazy(() => import("@/pages/weirton/page"));
const FairmontPage = lazy(() => import("@/pages/fairmont/page"));
const BeckleyPage = lazy(() => import("@/pages/beckley/page"));
const ClarksburgPage = lazy(() => import("@/pages/clarksburg/page"));
const SouthCharlestonPage = lazy(() => import("@/pages/south-charleston/page"));
const StAlbansPage = lazy(() => import("@/pages/st-albans/page"));

// Arkansas city pages
const LittleRockPage = lazy(() => import("@/pages/little-rock/page"));
const FayettevilleARPage = lazy(() => import("@/pages/fayetteville-ar/page"));
const FortSmithPage = lazy(() => import("@/pages/fort-smith/page"));
const SpringdalePage = lazy(() => import("@/pages/springdale/page"));
const JonesboroPage = lazy(() => import("@/pages/jonesboro/page"));
const RogersARPage = lazy(() => import("@/pages/rogers/page"));
const NorthLittleRockPage = lazy(() => import("@/pages/north-little-rock/page"));
const ConwayARPage = lazy(() => import("@/pages/conway/page"));
const BentonvillePage = lazy(() => import("@/pages/bentonville/page"));
const PineBluffPage = lazy(() => import("@/pages/pine-bluff/page"));
const CarrolltonTXPage = lazy(() => import("@/pages/carrollton-tx/page"));
const AbileneTXPage = lazy(() => import("@/pages/abilene/page"));
const BeaumontTXPage = lazy(() => import("@/pages/beaumont/page"));
const RoundRockPage = lazy(() => import("@/pages/round-rock/page"));
const OdessaTXPage = lazy(() => import("@/pages/odessa/page"));
const WichitaFallsPage = lazyPage(() => import("@/pages/wichita-falls/page"));
const RichardsonPage = lazyPage(() => import("@/pages/richardson/page"));
const TylerPage = lazyPage(() => import("@/pages/tyler/page"));
const LewisvillePage = lazyPage(() => import("@/pages/lewisville/page"));
const PearlandPage = lazyPage(() => import("@/pages/pearland/page"));
const SanAngeloPage = lazyPage(() => import("@/pages/san-angelo/page"));
const CollegeStationPage = lazyPage(() => import("@/pages/college-station/page"));
const SugarLandPage = lazyPage(() => import("@/pages/sugar-land/page"));
const AllenPage = lazyPage(() => import("@/pages/allen/page"));
const NewBraunfelsPage = lazyPage(() => import("@/pages/new-braunfels/page"));
const ConroePage = lazyPage(() => import("@/pages/conroe/page"));
const LongviewPage = lazyPage(() => import("@/pages/longview/page"));
const HotSpringsPage = lazy(() => import("@/pages/hot-springs/page"));
const BentonARPage = lazy(() => import("@/pages/benton/page"));
const ElDoradoARPage = lazy(() => import("@/pages/eldorado/page"));
const RussellvillePage = lazy(() => import("@/pages/russellville/page"));
const TexarkanaARPage = lazy(() => import("@/pages/texarkana/page"));

// Mississippi city pages
const JacksonMSPage = lazy(() => import("@/pages/jackson/page"));
const GulfportPage = lazy(() => import("@/pages/gulfport/page"));
const SouthavenPage = lazy(() => import("@/pages/southaven/page"));
const HattiesburgPage = lazy(() => import("@/pages/hattiesburg/page"));
const BiloxiPage = lazy(() => import("@/pages/biloxi/page"));
const MeridianPage = lazy(() => import("@/pages/meridian/page"));
const TupeloPage = lazy(() => import("@/pages/tupelo/page"));
const GreenvilleMSPage = lazy(() => import("@/pages/greenville/page"));
const OliveBranchPage = lazy(() => import("@/pages/olive-branch/page"));
const HornLakePage = lazy(() => import("@/pages/horn-lake/page"));
const ClintonMSPage = lazy(() => import("@/pages/clinton/page"));
const PearlMSPage = lazy(() => import("@/pages/pearl/page"));

// South Carolina city pages
const CharlestonPage = lazy(() => import("@/pages/charleston/page"));
const ColumbiaSCPage = lazy(() => import("@/pages/columbia-sc/page"));
const GreenvilleSCPage = lazy(() => import("@/pages/greenville-sc/page"));
const MyrtleBeachPage = lazy(() => import("@/pages/myrtle-beach/page"));
const HiltonHeadPage = lazy(() => import("@/pages/hilton-head/page"));
const SpartanburgPage = lazy(() => import("@/pages/spartanburg/page"));
const RockHillPage = lazy(() => import("@/pages/rock-hill/page"));
const MountPleasantPage = lazy(() => import("@/pages/mount-pleasant/page"));
const FlorenceSCPage = lazy(() => import("@/pages/florence-sc/page"));
const SummervilleSCPage = lazy(() => import("@/pages/summerville/page"));
const AikenSCPage = lazy(() => import("@/pages/aiken/page"));
const GooseCreekSCPage = lazy(() => import("@/pages/goose-creek/page"));

// Montana city pages
const BillingsPage = lazy(() => import("@/pages/billings/page"));
const MissoulaPage = lazy(() => import("@/pages/missoula/page"));
const BozemanPage = lazy(() => import("@/pages/bozeman/page"));
const GreatFallsPage = lazy(() => import("@/pages/great-falls/page"));
const HelenaPage = lazy(() => import("@/pages/helena/page"));
const KalispellPage = lazy(() => import("@/pages/kalispell/page"));
const ButtePage = lazy(() => import("@/pages/butte/page"));
const BelgradePage = lazy(() => import("@/pages/belgrade/page"));
const MilesCityPage = lazy(() => import("@/pages/miles-city/page"));
const LivingstonPage = lazy(() => import("@/pages/livingston/page"));
const HavrePage = lazy(() => import("@/pages/havre/page"));
const WhitefishPage = lazy(() => import("@/pages/whitefish/page"));

// Montana, Nebraska, North Dakota, South Dakota, Idaho
const MontanaPage = lazy(() => import("@/pages/montana/page"));
const NebraskaPage = lazy(() => import("@/pages/nebraska/page"));
const OmahaPage = lazy(() => import("@/pages/omaha/page"));
const LincolnNEPage = lazy(() => import("@/pages/lincoln/page"));
const GrandIslandPage = lazy(() => import("@/pages/grand-island/page"));
const KearneyPage = lazy(() => import("@/pages/kearney/page"));
const FremontPage = lazy(() => import("@/pages/fremont/page"));
const HastingsPage = lazy(() => import("@/pages/hastings/page"));
const NorthPlattePage = lazy(() => import("@/pages/north-platte/page"));
const NorfolkPage = lazy(() => import("@/pages/norfolk/page"));
const PapillionPage = lazy(() => import("@/pages/papillion/page"));
const LaVistaPage = lazy(() => import("@/pages/la-vista/page"));
const BellevueNePage = lazy(() => import("@/pages/bellevue-ne/page"));
const ColumbusNePage = lazy(() => import("@/pages/columbus-ne/page"));
const NorthDakotaPage = lazy(() => import("@/pages/north-dakota/page"));
const SouthDakotaPage = lazy(() => import("@/pages/south-dakota/page"));
const IdahoPage = lazy(() => import("@/pages/idaho/page"));

// North Dakota city pages
const FargoPage = lazy(() => import("@/pages/fargo/page"));
const BismarckPage = lazy(() => import("@/pages/bismarck/page"));
const GrandForksPage = lazy(() => import("@/pages/grand-forks/page"));
const MinotPage = lazy(() => import("@/pages/minot/page"));
const WestFargoPage = lazy(() => import("@/pages/west-fargo/page"));
const WillistonPage = lazy(() => import("@/pages/williston/page"));
const DickinsonPage = lazy(() => import("@/pages/dickinson/page"));
const MandanPage = lazy(() => import("@/pages/mandan/page"));
const WahpetonPage = lazy(() => import("@/pages/wahpeton/page"));
const DevilsLakePage = lazy(() => import("@/pages/devils-lake/page"));
const ValleyCityPage = lazy(() => import("@/pages/valley-city/page"));
const JamestownNDPage = lazy(() => import("@/pages/jamestown-nd/page"));

// South Dakota city pages
const SiouxFallsPage = lazy(() => import("@/pages/sioux-falls/page"));
const RapidCityPage = lazy(() => import("@/pages/rapid-city/page"));
const AberdeenSDPage = lazy(() => import("@/pages/aberdeen-sd/page"));
const BrookingsPage = lazy(() => import("@/pages/brookings/page"));
const MitchellPage = lazy(() => import("@/pages/mitchell/page"));
const YanktonPage = lazy(() => import("@/pages/yankton/page"));
const HuronPage = lazy(() => import("@/pages/huron/page"));
const PierrePage = lazy(() => import("@/pages/pierre/page"));
const SpearfishPage = lazy(() => import("@/pages/spearfish/page"));
const VermillionPage = lazy(() => import("@/pages/vermillion/page"));
const SturgisPage = lazy(() => import("@/pages/sturgis/page"));
const WatertownSDPage = lazy(() => import("@/pages/watertown-sd/page"));

// Idaho city pages
const BoisePage = lazy(() => import("@/pages/boise/page"));
const IdahoFallsPage = lazy(() => import("@/pages/idaho-falls/page"));
const CoeurDalenePage = lazy(() => import("@/pages/coeur-dalene/page"));
const PostFallsPage = lazy(() => import("@/pages/post-falls/page"));
const NampaPage = lazy(() => import("@/pages/nampa/page"));
const PocatelloPage = lazy(() => import("@/pages/pocatello/page"));
const TwinFallsPage = lazy(() => import("@/pages/twin-falls/page"));
const CaldwellPage = lazy(() => import("@/pages/caldwell/page"));
const RexburgPage = lazy(() => import("@/pages/rexburg/page"));
const MoscowPage = lazy(() => import("@/pages/moscow/page"));
const MeridianIDPage = lazy(() => import("@/pages/meridian-id/page"));

// Alaska and Wyoming (state hub)
const AlaskaPage = lazy(() => import("@/pages/alaska/page"));
const WyomingStatePage = lazy(() => import("@/pages/wyoming-state/page"));

// Alaska city pages
const AnchoragePage = lazy(() => import("@/pages/anchorage/page"));
const JuneauPage = lazy(() => import("@/pages/juneau/page"));
const FairbankAKPage = lazy(() => import("@/pages/fairbanks-ak/page"));
const SitkaPage = lazy(() => import("@/pages/sitka/page"));
const KetchikanPage = lazy(() => import("@/pages/ketchikan/page"));
const WasillaPage = lazy(() => import("@/pages/wasilla/page"));
const KenaiPage = lazy(() => import("@/pages/kenai/page"));
const KodiakPage = lazy(() => import("@/pages/kodiak/page"));
const HomerAkPage = lazy(() => import("@/pages/homer-ak/page"));
const SoldotnaPage = lazy(() => import("@/pages/soldotna/page"));
const PalmerAkPage = lazy(() => import("@/pages/palmer-ak/page"));
const SewardAkPage = lazy(() => import("@/pages/seward-ak/page"));

// Wyoming city pages
const CheyennePage = lazy(() => import("@/pages/cheyenne/page"));
const CasperPage = lazy(() => import("@/pages/casper/page"));
const JacksonWYPage = lazy(() => import("@/pages/jackson-wy/page"));
const LaramiePage = lazy(() => import("@/pages/laramie/page"));
const GillettePage = lazy(() => import("@/pages/gillette/page"));
const RockSpringsPage = lazy(() => import("@/pages/rock-springs/page"));
const SheridanWyPage = lazy(() => import("@/pages/sheridan-wy/page"));
const EvanstonWyPage = lazy(() => import("@/pages/evanston-wy/page"));
const RivertonPage = lazy(() => import("@/pages/riverton/page"));
const LanderPage = lazy(() => import("@/pages/lander/page"));
const RawlinsPage = lazy(() => import("@/pages/rawlins/page"));
const CodyPage = lazy(() => import("@/pages/cody/page"));

const SantaClaritaPage = lazy(() => import("@/pages/santa-clarita/page"));
const PalmdalePage = lazy(() => import("@/pages/palmdale/page"));
const CoronaPage = lazy(() => import("@/pages/corona/page"));
const LancasterCaPage = lazy(() => import("@/pages/lancaster-ca/page"));
const FremontCaPage = lazy(() => import("@/pages/fremont-ca/page"));
const ElkGrovePage = lazy(() => import("@/pages/elk-grove/page"));

const routes: RouteObject[] = [
  // Service pages
  { path: "/cities/passport-services", element: <Suspense fallback={<Loading />}><PassportServicesPage /></Suspense> },
  { path: "/passport-services/new-passport", element: <NewPassportPage /> },
  { path: "/passport-services/renewal", element: <RenewalPage /> },
  { path: "/passport-services/lost-stolen", element: <LostStolenPage /> },
  { path: "/passport-services/child-passport", element: <ChildPassportPage /> },
  { path: "/passport-services/damaged-passport", element: <DamagedPassportPage /> },
  { path: "/passport-services/passport-card", element: <PassportCardPage /> },
  { path: "/passport-services/name-change", element: <NameChangePage /> },
  { path: "/passport-services/second-passport", element: <SecondPassportPage /> },
  { path: "/passport-services/chinese-business-visa", element: <Suspense fallback={<Loading />}><ChineseBusinessVisaPage /></Suspense> },
  { path: "/passport-services/chinese-business-visa-checklist", element: <Suspense fallback={<Loading />}><ChinaBusinessVisaChecklistPage /></Suspense> },
  { path: "/passport-services/chinese-tourist-visa", element: <Suspense fallback={<Loading />}><ChineseTouristVisaPage /></Suspense> },
  { path: "/passport-services/chinese-tourist-visa-checklist", element: <Suspense fallback={<Loading />}><ChinaTouristVisaChecklistPage /></Suspense> },
  { path: "/passport-services/expedited-passport-service", element: <Suspense fallback={<Loading />}><ExpeditedPassportServicePage /></Suspense> },
  { path: "/passport-services/emergency-passport", element: <Suspense fallback={<Loading />}><EmergencyPassportPage /></Suspense> },
  { path: "/passport-services/fast-passport-renewal", element: <Suspense fallback={<Loading />}><FastPassportRenewalPage /></Suspense> },
  { path: "/passport-services/urgent-passport", element: <Suspense fallback={<Loading />}><UrgentPassportPage /></Suspense> },
  { path: "/passport-services/passport-expediting-service", element: <Suspense fallback={<Loading />}><PassportExpeditingServicePage /></Suspense> },
  
  // Helpful Resources pages
  { path: "/cities/helpful-resources", element: <Suspense fallback={<Loading />}><HelpfulResourcesPage /></Suspense> },
  { path: "/helpful-resources/passport-photo-requirements", element: <Suspense fallback={<Loading />}><PassportPhotoRequirementsPage /></Suspense> },
  { path: "/helpful-resources/international-travel-tips", element: <Suspense fallback={<Loading />}><InternationalTravelTipsPage /></Suspense> },
  { path: "/helpful-resources/us-citizen-passport-guide", element: <Suspense fallback={<Loading />}><USCitizenPassportGuidePage /></Suspense> },
  { path: "/helpful-resources/passport-processing-times", element: <Suspense fallback={<Loading />}><PassportProcessingTimesPage /></Suspense> },
  { path: "/helpful-resources/ds11-vs-ds82", element: <Suspense fallback={<Loading />}><DS11vsDS82Page /></Suspense> },
  { path: "/helpful-resources/first-time-checklist", element: <Suspense fallback={<Loading />}><FirstTimeChecklistPage /></Suspense> },
  { path: "/helpful-resources/citizenship-through-parents", element: <Suspense fallback={<Loading />}><CitizenshipThroughParentsPage /></Suspense> },
  { path: "/helpful-resources/passport-fees", element: <Suspense fallback={<Loading />}><PassportFeesPage /></Suspense> },
  { path: "/helpful-resources/child-passport-guide", element: <Suspense fallback={<Loading />}><ChildPassportGuidePage /></Suspense> },
  { path: "/cities/lakeland", element: <LakelandPage /> },
  { path: "/cities/pompano-beach", element: <PompanoBeachPage /> },
  // Florida
  { path: "/cities/orlando", element: <OrlandoPage /> },
  { path: "/cities/tampa", element: <TampaPage /> },
  { path: "/cities/boca-raton", element: <BocaPage /> },
  { path: "/cities/miami", element: <MiamiPage /> },
  { path: "/cities/fort-lauderdale", element: <FortLauderdalePage /> },
  { path: "/cities/naples", element: <NaplesPage /> },
  { path: "/cities/sarasota", element: <SarasotaPage /> },
  { path: "/cities/gainesville", element: <GainesvillePage /> },
  { path: "/cities/jacksonville", element: <JacksonvillePage /> },
  { path: "/cities/pensacola", element: <PensacolaPage /> },
  { path: "/cities/panama-city", element: <PanamaCityPage /> },
  { path: "/cities/tallahassee", element: <TallahasseePage /> },
  { path: "/cities/daytona-beach", element: <DaytonaBeachPage /> },
  { path: "/cities/delray-beach", element: <DelrayPage /> },
  { path: "/cities/aventura", element: <AventuraPage /> },
  { path: "/cities/palm-beach", element: <PalmBeachPage /> },
  { path: "/cities/palm-beach-gardens", element: <PalmGardensPage /> },
  { path: "/cities/coral-gables", element: <CoralGablesPage /> },
  { path: "/cities/hollywood", element: <HollywoodPage /> },
  { path: "/cities/pembroke-pines", element: <PembrokePinesPage /> },
  { path: "/cities/plantation", element: <PlantationPage /> },
  { path: "/cities/weston", element: <WestonPage /> },
  { path: "/cities/boynton-beach", element: <BoyntonBeachPage /> },
  { path: "/cities/deerfield-beach", element: <DeerfieldBeachPage /> },
  { path: "/cities/coral-springs", element: <CoralSpringsPage /> },
  { path: "/cities/parkland", element: <ParklandPage /> },
  { path: "/cities/clearwater", element: <ClearwaterPage /> },
  { path: "/cities/st-petersburg", element: <StPetersburgPage /> },
  { path: "/cities/dunedin", element: <DunedinPage /> },
  { path: "/cities/palm-harbor", element: <PalmHarborPage /> },
  { path: "/cities/safety-harbor", element: <SafetyHarborPage /> },
  { path: "/cities/wesley-chapel", element: <WesleyChapelPage /> },
  { path: "/cities/winter-garden", element: <WinterGardenPage /> },
  { path: "/cities/winter-park", element: <WinterParkPage /> },
  { path: "/cities/windermere", element: <WindermerePage /> },
  { path: "/cities/lake-mary", element: <LakeMaryPage /> },
  { path: "/cities/celebration", element: <CelebrationPage /> },
  { path: "/cities/fort-myers", element: <FortMyersPage /> },
  { path: "/cities/cape-coral", element: <CapeCoralPage /> },
  { path: "/cities/bonita-springs", element: <BonitaSpringsPage /> },
  { path: "/cities/estero", element: <EsteroPage /> },
  { path: "/cities/marco-island", element: <MarcoIslandPage /> },
  { path: "/cities/lakewood-ranch", element: <LakewoodRanchPage /> },
  { path: "/cities/jupiter", element: <JupiterPage /> },
  { path: "/cities/port-st-lucie", element: <PortStLuciePage /> },
  { path: "/cities/stuart", element: <StuartPage /> },
  { path: "/cities/jensen-beach", element: <JensenBeachPage /> },
  { path: "/cities/wellington", element: <WellingtonPage /> },
  { path: "/cities/west-palm-beach", element: <WestPalmPage /> },
  { path: "/cities/key-west", element: <KeyWestPage /> },
  { path: "/cities/melbourne", element: <MelbournePage /> },
  { path: "/cities/cocoa-beach", element: <CocoaBeachPage /> },
  { path: "/cities/titusville", element: <TitusvillePage /> },
  { path: "/cities/key-largo", element: <KeyLargoPage /> },
  { path: "/cities/islamorada", element: <IslamoradaPage /> },
  { path: "/cities/riverview", element: <RiverviewPage /> },
  { path: "/cities/venice", element: <VenicePage /> },
  { path: "/cities/ponte-vedra-beach", element: <PonteVedraBeachPage /> },
  { path: "/cities/sunny-isles", element: <SunnyIslesPage /> },
  { path: "/cities/quincy", element: <QuincyPage /> },
  { path: "/cities/upper-grand-lagoon", element: <UpperGrandLagoonPage /> },
  { path: "/cities/cedar-key", element: <CedarKeyPage /> },
  { path: "/states/usvi", element: <UsviPage /> },
  { path: "/states/puerto-rico", element: <PuertoRicoPage /> },
  { path: "/states/washington-dc", element: <DcPage /> },
  { path: "/states/florida", element: <FloridaPage /> },
  { path: "/states/hawaii", element: <HawaiiPage /> },
  { path: "/cities/honolulu", element: <HonoluluPage /> },
  { path: "/cities/pearl-city", element: <PearlCityPage /> },
  { path: "/cities/hilo", element: <HiloPage /> },
  { path: "/cities/kailua", element: <KailuaPage /> },
  { path: "/cities/kapolei", element: <KapoleiPage /> },
  { path: "/cities/kaneohe", element: <KaneohePage /> },
  { path: "/cities/kailua-kona", element: <KailuaKonaPage /> },
  { path: "/cities/kahului", element: <KahuluiPage /> },
  { path: "/cities/kihei", element: <KiheiPage /> },
  { path: "/cities/lahaina", element: <LahainaPage /> },
  { path: "/cities/lihue", element: <LihuePage /> },
  { path: "/cities/mililani", element: <MililaniPage /> },
  { path: "/cities/waikiki", element: <WaikikiPage /> },
  { path: "/cities/wailuku", element: <WailukuPage /> },
  { path: "/cities/waipahu", element: <WaipahuPage /> },
  { path: "/cities/ewa-beach", element: <EwaBeachPage /> },
  { path: "/cities/kapaa", element: <KapaaPage /> },
  { path: "/cities/wailea", element: <WaileaPage /> },
  { path: "/cities/wahiawa", element: <WahiawaPage /> },
  { path: "/cities/maui", element: <MauiPage /> },
  { path: "/cities/kauai", element: <KauaiPage /> },

  // Georgia
  { path: "/cities/athens", element: <AthensPage /> },
  { path: "/cities/atlanta", element: <AtlantaPage /> },
  { path: "/cities/dalton", element: <DaltonPage /> },
  { path: "/cities/douglasville", element: <DouglasvillePage /> },
  { path: "/cities/hinesville", element: <HinesvillePage /> },
  { path: "/cities/lagrange", element: <LaGrangePage /> },
  { path: "/cities/kennesaw", element: <KennesawPage /> },
  { path: "/cities/gainesville-ga", element: <GainesvilleGAPage /> },
  { path: "/cities/peachtree-city", element: <PeachtreeCityPage /> },
  { path: "/cities/woodstock", element: <WoodstockPage /> },
  { path: "/cities/smyrna", element: <SmyrnaPage /> },
  { path: "/cities/lawrenceville", element: <LawrencevillePage /> },
  { path: "/cities/milledgeville", element: <MilledgevillePage /> },
  { path: "/cities/rome", element: <RomePage /> },
  { path: "/cities/newnan", element: <NewnanPage /> },
  { path: "/cities/statesboro", element: <StatesboroPage /> },
  { path: "/states/georgia", element: <GeorgiaPage /> },
  { path: "/cities/buckhead", element: <BuckheadPage /> },
  { path: "/cities/marietta", element: <MariettaPage /> },
  { path: "/cities/dunwoody", element: <DunwoodyPage /> },
  { path: "/cities/roswell", element: <RoswellPage /> },
  { path: "/cities/sandy-springs", element: <SandySpringsPage /> },
  { path: "/cities/johns-creek", element: <JohnsCreekPage /> },
  { path: "/cities/suwanee", element: <SuwaneePage /> },
  { path: "/cities/milton", element: <MiltonPage /> },
  { path: "/cities/peachtree-corners", element: <PeachtreeCornersPage /> },
  { path: "/cities/carrollton", element: <CarrolltonPage /> },
  { path: "/cities/columbus", element: <ColumbusPage /> },
  { path: "/cities/albany-ga", element: <AlbanyPage /> },
  { path: "/cities/alpharetta", element: <AlpharettaPage /> },
  { path: "/cities/decatur", element: <DecaturPage /> },
  { path: "/cities/duluth", element: <DuluthPage /> },
  { path: "/cities/savannah", element: <SavannahPage /> },
  { path: "/cities/augusta", element: <AugustaPage /> },
  { path: "/cities/warner-robins", element: <WarnerRobinsPage /> },
  { path: "/cities/valdosta", element: <ValdostaPage /> },
  { path: "/cities/macon", element: <MaconPage /> },

  // California
  { path: "/states/california", element: <CaliforniaPage /> },
  { path: "/cities/los-angeles", element: <LosAngelesPage /> },
  { path: "/cities/san-diego", element: <SanDiegoPage /> },
  { path: "/cities/san-francisco", element: <SanFranciscoPage /> },
  { path: "/cities/san-jose", element: <SanJosePage /> },
  { path: "/cities/sacramento", element: <SacramentoPage /> },
  { path: "/cities/anaheim", element: <AnaheimPage /> },
  { path: "/cities/oakland", element: <OaklandPage /> },
  { path: "/cities/long-beach", element: <LongBeachPage /> },
  { path: "/cities/irvine", element: <IrvinePage /> },
  { path: "/cities/santa-ana", element: <SantaAnaPage /> },
  { path: "/cities/fresno", element: <FresnoPage /> },
  { path: "/cities/bakersfield", element: <BakersfieldPage /> },
  { path: "/cities/riverside", element: <RiversidePage /> },
  { path: "/cities/santa-barbara", element: <SantaBarbaraPage /> },
  { path: "/cities/stockton", element: <StocktonPage /> },
  { path: "/cities/pasadena", element: <PasadenaPage /> },
  { path: "/cities/modesto", element: <ModestoPage /> },
  { path: "/cities/ventura", element: <VenturaPage /> },
  { path: "/cities/san-bernardino", element: <SanBernardinoPage /> },
  { path: "/cities/ontario", element: <OntarioPage /> },
  { path: "/cities/chula-vista", element: <ChulaVistaPage /> },
  { path: "/cities/huntington-beach", element: <HuntingtonBeachPage /> },
  { path: "/cities/costa-mesa", element: <CostaMesaPage /> },
  { path: "/cities/santa-monica", element: <SantaMonicaPage /> },
  { path: "/cities/newport-beach", element: <NewportBeachPage /> },
  { path: "/cities/garden-grove", element: <GardenGrovePage /> },
  { path: "/cities/orange", element: <OrangePage /> },
  { path: "/cities/fullerton", element: <FullertonPage /> },
  { path: "/cities/torrance", element: <TorrancePage /> },
  { path: "/cities/glendale", element: <GlendalePage /> },
  { path: "/cities/burbank", element: <BurbankPage /> },
  { path: "/cities/thousand-oaks", element: <ThousandOaksPage /> },
  { path: "/cities/simi-valley", element: <SimiValleyPage /> },
  { path: "/cities/rancho-cucamonga", element: <RanchoCucamongaPage /> },
  { path: "/cities/pomona", element: <PomonaPage /> },
  { path: "/cities/escondido", element: <EscondidoPage /> },
  { path: "/cities/carlsbad", element: <CarlsbadPage /> },
  { path: "/cities/oxnard", element: <OxnardPage /> },
  { path: "/cities/salinas", element: <SalinasPage /> },
  { path: "/cities/santa-rosa", element: <SantaRosaPage /> },
  { path: "/cities/berkeley", element: <BerkeleyPage /> },
  { path: "/cities/daly-city", element: <DalyCityPage /> },
  { path: "/cities/santa-clara", element: <SantaClaraPage /> },
  { path: "/cities/sunnyvale", element: <SunnyvalePage /> },
  { path: "/cities/concord", element: <ConcordPage /> },
  { path: "/cities/roseville", element: <RosevillePage /> },
  { path: "/cities/oceanside", element: <OceansidePage /> },
  { path: "/cities/el-cajon", element: <ElCajonPage /> },
  { path: "/cities/vallejo", element: <VallejoPage /> },
  { path: "/cities/hayward", element: <HaywardPage /> },
  { path: "/cities/moreno-valley", element: <MorenoValleyPage /> },
  { path: "/cities/fontana", element: <FontanaPage /> },
  { path: "/cities/visalia", element: <VisaliaPage /> },
  { path: "/cities/antioch", element: <AntiochPage /> },
  { path: "/cities/menifee", element: <MenifeePage /> },
  { path: "/cities/rialto", element: <RialtoPage /> },
  { path: "/cities/compton", element: <ComptonPage /> },
  { path: "/cities/redding", element: <ReddingPage /> },
  { path: "/cities/vacaville", element: <VacavillePage /> },
  { path: "/cities/tustin", element: <TustinPage /> },
  { path: "/cities/merced", element: <MercedPage /> },
  { path: "/cities/mountain-view", element: <MountainViewPage /> },
  { path: "/cities/san-mateo", element: <SanMateoPage /> },
  { path: "/cities/livermore", element: <LivermorePage /> },
  { path: "/cities/tracy", element: <TracyPage /> },
  { path: "/cities/santa-clarita", element: <SantaClaritaPage /> },
  { path: "/cities/palmdale", element: <PalmdalePage /> },
  { path: "/cities/corona", element: <CoronaPage /> },
  { path: "/cities/lancaster-ca", element: <LancasterCaPage /> },
  { path: "/cities/fremont-ca", element: <FremontCaPage /> },
  { path: "/cities/elk-grove", element: <ElkGrovePage /> },

  // Virginia
  { path: "/states/virginia", element: <VirginiaPage /> },
  { path: "/cities/richmond-va", element: <RichmondVaPage /> },
  { path: "/cities/arlington-va", element: <ArlingtonVaPage /> },
  { path: "/cities/mclean-va", element: <McLeanVaPage /> },
  { path: "/cities/alexandria-va", element: <AlexandriaVaPage /> },
  { path: "/cities/virginia-beach", element: <VirginiaBeachPage /> },
  { path: "/cities/norfolk-va", element: <NorfolkVaPage /> },
  { path: "/cities/chesapeake-va", element: <ChesapeakeVaPage /> },
  { path: "/cities/fairfax-va", element: <FairfaxVaPage /> },
  { path: "/cities/portsmouth-va", element: <PortsmouthVaPage /> },
  { path: "/cities/vienna-va", element: <ViennaVaPage /> },
  { path: "/cities/reston-va", element: <RestonVaPage /> },
  { path: "/cities/charlottesville-va", element: <CharlottesvilleVaPage /> },
  { path: "/cities/roanoke-va", element: <RoanokeVaPage /> },
  { path: "/cities/lynchburg-va", element: <LynchburgVaPage /> },
  { path: "/cities/fredericksburg-va", element: <FredericksburgVaPage /> },
  { path: "/cities/newport-news-va", element: <NewportNewsVaPage /> },
  { path: "/cities/williamsburg-va", element: <WilliamsburgVaPage /> },
  { path: "/cities/harrisonburg-va", element: <HarrisonburgVaPage /> },
  { path: "/cities/winchester-va", element: <WinchesterVaPage /> },
  { path: "/cities/manassas-va", element: <ManassasVaPage /> },
  { path: "/cities/suffolk-va", element: <SuffolkVaPage /> },
  { path: "/cities/hampton-va", element: <HamptonVaPage /> },
  { path: "/cities/petersburg-va", element: <PetersburgVaPage /> },
  { path: "/cities/leesburg-va", element: <LeesburgVaPage /> },
  { path: "/cities/ashburn-va", element: <AshburnVaPage /> },
  { path: "/cities/woodbridge-va", element: <WoodbridgeVaPage /> },
  { path: "/cities/herndon-va", element: <HerndonVaPage /> },
  { path: "/cities/glen-allen-va", element: <GlenAllenVaPage /> },
  { path: "/cities/midlothian-va", element: <MidlothianVaPage /> },
  { path: "/cities/mechanicsville-va", element: <MechanicsvilleVaPage /> },
  { path: "/cities/short-pump-va", element: <ShortPumpVaPage /> },
  { path: "/cities/falls-church-va", element: <FallsChurchVaPage /> },
  { path: "/cities/tysons-va", element: <TysonsVaPage /> },
  { path: "/cities/springfield-va", element: <SpringfieldVaPage /> },
  { path: "/cities/burke-va", element: <BurkeVaPage /> },
  { path: "/cities/centreville-va", element: <CentrevilleVaPage /> },
  { path: "/cities/colonial-heights-va", element: <ColonialHeightsVaPage /> },
  { path: "/cities/hopewell-va", element: <HopewellVaPage /> },
  { path: "/cities/chester-va", element: <ChesterVaPage /> },
  { path: "/cities/blacksburg-va", element: <BlacksburgVaPage /> },
  { path: "/cities/danville-va", element: <DanvilleVaPage /> },

  // Connecticut
  { path: "/states/connecticut", element: <ConnecticutPage /> },
  { path: "/cities/hartford", element: <HartfordPage /> },
  { path: "/cities/new-haven", element: <NewHavenPage /> },
  { path: "/cities/stamford", element: <StamfordPage /> },
  { path: "/cities/bridgeport", element: <BridgeportPage /> },
  { path: "/cities/waterbury", element: <WaterburyPage /> },
  { path: "/cities/greenwich", element: <GreenwichPage /> },
  { path: "/cities/norwalk", element: <NorwalkPage /> },
  { path: "/cities/danbury", element: <DanburyPage /> },
  { path: "/cities/bristol", element: <BristolPage /> },
  { path: "/cities/new-britain", element: <NewBritainPage /> },
  { path: "/cities/west-hartford", element: <WestHartfordPage /> },
  { path: "/cities/milford", element: <MilfordPage /> },

  // Massachusetts
  { path: "/states/massachusetts", element: <MassachusettsPage /> },
  { path: "/cities/boston", element: <BostonPage /> },
  { path: "/cities/worcester", element: <WorcesterPage /> },
  { path: "/cities/springfield-ma", element: <SpringfieldMaPage /> },
  { path: "/cities/lowell", element: <LowellPage /> },
  { path: "/cities/cambridge", element: <CambridgePage /> },
  { path: "/cities/new-bedford", element: <NewBedfordPage /> },
  { path: "/cities/brockton", element: <BrocktonPage /> },
  { path: "/cities/quincy-ma", element: <QuincyMaPage /> },
  { path: "/cities/lynn", element: <LynnPage /> },
  { path: "/cities/fall-river", element: <FallRiverPage /> },
  { path: "/cities/newton", element: <NewtonPage /> },
  { path: "/cities/somerville", element: <SomervillePage /> },
  { path: "/cities/nantucket", element: <NantucketPage /> },
  { path: "/cities/marthas-vineyard", element: <MarthasVineyardPage /> },

  // Vermont
  { path: "/states/vermont", element: <VermontPage /> },
  { path: "/cities/burlington-vt", element: <BurlingtonVtPage /> },
  { path: "/cities/montpelier", element: <MontpelierPage /> },
  { path: "/cities/rutland", element: <RutlandPage /> },
  { path: "/cities/stowe", element: <StowePage /> },
  { path: "/cities/brattleboro", element: <BrattleboroPage /> },
  { path: "/cities/bennington", element: <BenningtonPage /> },
  { path: "/cities/middlebury", element: <MiddleburyPage /> },
  { path: "/cities/woodstock-vt", element: <WoodstockVtPage /> },
  { path: "/cities/springfield-vt", element: <SpringfieldVtPage /> },
  { path: "/cities/white-river-junction", element: <WhiteRiverJunctionPage /> },

  // Rhode Island
  { path: "/states/rhode-island", element: <RhodeIslandPage /> },
  { path: "/cities/providence", element: <ProvidencePage /> },
  { path: "/cities/warwick", element: <WarwickPage /> },
  { path: "/cities/cranston", element: <CranstonPage /> },
  { path: "/cities/pawtucket", element: <PawtucketPage /> },
  { path: "/cities/newport-ri", element: <NewportRIPage /> },
  { path: "/cities/woonsocket", element: <WoonsocketPage /> },
  { path: "/cities/east-providence", element: <EastProvidencePage /> },
  { path: "/cities/johnston", element: <JohnstonPage /> },
  { path: "/cities/coventry", element: <CoventryPage /> },
  { path: "/cities/west-warwick", element: <WestWarwickPage /> },
  { path: "/cities/cumberland-ri", element: <CumberlandRiPage /> },
  { path: "/cities/north-providence", element: <NorthProvidencePage /> },

  // Kentucky
  { path: "/states/kentucky", element: <KentuckyPage /> },
  { path: "/cities/ashland", element: <AshlandPage /> },
  { path: "/cities/bardstown", element: <BardstownPage /> },
  { path: "/cities/berea", element: <BereaPage /> },
  { path: "/cities/bowling-green", element: <BowlingGreenPage /> },
  { path: "/cities/lexington", element: <LexingtonPage /> },
  { path: "/cities/louisville", element: <LouisvillePage /> },
  { path: "/cities/covington", element: <CovingtonPage /> },
  { path: "/cities/florence", element: <FlorencePage /> },
  { path: "/cities/newport", element: <NewportPage /> },
  { path: "/cities/erlanger", element: <ErlangerPage /> },
  { path: "/cities/fort-thomas", element: <FortThomasPage /> },
  { path: "/cities/independence", element: <IndependencePage /> },
  { path: "/cities/frankfort", element: <FrankfortPage /> },
  { path: "/cities/georgetown", element: <GeorgetownPage /> },
  { path: "/cities/danville", element: <DanvillePage /> },
  { path: "/cities/elizabethtown", element: <ElizabethtownPage /> },
  { path: "/cities/paducah", element: <PaducahPage /> },
  { path: "/cities/owensboro", element: <OwensboroPage /> },
  { path: "/cities/henderson", element: <HendersonPage /> },
  { path: "/cities/hopkinsville", element: <HopkinsvillePage /> },
  { path: "/cities/glasgow", element: <GlasgowPage /> },
  { path: "/cities/somerset", element: <SomersetPage /> },
  { path: "/cities/morehead", element: <MoreheadPage /> },
  { path: "/cities/shelbyville", element: <ShelbyvillePage /> },
  { path: "/cities/mount-washington", element: <MountWashingtonPage /> },
  { path: "/cities/shively", element: <ShivelyPage /> },
  { path: "/cities/jeffersontown", element: <JeffersontownPage /> },
  { path: "/cities/richmond", element: <RichmondPage /> },
  { path: "/cities/fort-mitchell", element: <FortMitchellPage /> },
  { path: "/cities/middlesborough", element: <MiddlesboroughPage /> },
  { path: "/cities/murray", element: <MurrayPage /> },
  { path: "/cities/nicholasville", element: <NicholasvillePage /> },
  { path: "/cities/radcliff", element: <RadcliffPage /> },
  { path: "/cities/versailles", element: <VersaillesPage /> },
  { path: "/cities/winchester", element: <WinchesterPage /> },

  // Ohio
  { path: "/states/ohio", element: <OhioPage /> },
  { path: "/cities/cincinnati", element: <CincinnatiPage /> },
  { path: "/cities/cleveland", element: <ClevelandPage /> },
  { path: "/cities/columbus-oh", element: <ColumbusOhPage /> },
  { path: "/cities/dayton", element: <DaytonPage /> },
  { path: "/cities/toledo", element: <ToledoPage /> },
  { path: "/cities/akron", element: <AkronPage /> },
  { path: "/cities/canton", element: <CantonPage /> },
  { path: "/cities/youngstown", element: <YoungstownPage /> },
  { path: "/cities/lorain", element: <LorainPage /> },
  { path: "/cities/parma", element: <ParmaPage /> },
  { path: "/cities/hamilton-oh", element: <HamiltonPage /> },
  { path: "/cities/springfield-oh", element: <SpringfieldPage /> },
  { path: "/cities/kettering", element: <KetteringPage /> },
  { path: "/cities/elyria", element: <ElyriaPage /> },
  { path: "/cities/middletown-oh", element: <MiddletownPage /> },
  { path: "/cities/cuyahoga-falls", element: <CuyahogaFallsPage /> },
  { path: "/cities/newark-oh", element: <NewarkPage /> },
  { path: "/cities/mansfield", element: <MansfieldPage /> },
  { path: "/cities/mentor", element: <MentorPage /> },
  { path: "/cities/beavercreek", element: <BeavercreekPage /> },
  { path: "/cities/dublin-oh", element: <DublinPage /> },
  { path: "/cities/findlay", element: <FindlayPage /> },
  { path: "/cities/lima-oh", element: <LimaPage /> },
  { path: "/cities/marion-oh", element: <MarionPage /> },
  { path: "/cities/lancaster-oh", element: <LancasterOhPage /> },
  { path: "/cities/delaware-oh", element: <DelawarePage /> },
  { path: "/cities/medina", element: <MedinaPage /> },
  { path: "/cities/warren-oh", element: <WarrenPage /> },
  { path: "/cities/strongsville", element: <StrongsvillePage /> },
  { path: "/cities/fairfield-oh", element: <FairfieldPage /> },
  { path: "/cities/huber-heights", element: <HuberHeightsPage /> },
  { path: "/cities/reynoldsburg", element: <ReynoldsburgPage /> },
  { path: "/cities/wooster", element: <WoosterPage /> },
  { path: "/cities/zanesville", element: <ZanesvillePage /> },
  { path: "/cities/athens-oh", element: <AthensOhPage /> },
  { path: "/cities/bowling-green-oh", element: <BowlingGreenOhPage /> },

  // North Carolina
  { path: "/states/north-carolina", element: <NorthCarolinaPage /> },
  { path: "/cities/charlotte", element: <CharlottePage /> },
  { path: "/cities/raleigh", element: <RaleighPage /> },
  { path: "/cities/greensboro", element: <GreensboroPage /> },
  { path: "/cities/asheville", element: <AshevillePage /> },
  { path: "/cities/wilmington", element: <WilmingtonPage /> },
  { path: "/cities/winston-salem", element: <WinstonSalemPage /> },
  { path: "/cities/durham", element: <DurhamPage /> },
  { path: "/cities/fayetteville", element: <FayettevillePage /> },
  { path: "/cities/cary", element: <CaryPage /> },
  { path: "/cities/high-point", element: <HighPointPage /> },
  { path: "/cities/chapel-hill", element: <ChapelHillPage /> },
  { path: "/cities/concord-nc", element: <ConcordNcPage /> },
  { path: "/cities/gastonia", element: <GastoniaPage /> },
  { path: "/cities/jacksonville-nc", element: <JacksonvilleNcPage /> },
  { path: "/cities/burlington", element: <BurlingtonPage /> },
  { path: "/cities/hickory", element: <HickoryPage /> },
  { path: "/cities/greenville", element: <GreenvillePage /> },
  { path: "/cities/goldsboro", element: <GoldsboroPage /> },
  { path: "/cities/rocky-mount", element: <RockyMountPage /> },
  { path: "/cities/wilson", element: <WilsonPage /> },
  { path: "/cities/new-bern", element: <NewBernPage /> },
  { path: "/cities/wake-forest", element: <WakeForestPage /> },
  { path: "/cities/kannapolis", element: <KannapolisPage /> },
  { path: "/cities/apex", element: <ApexPage /> },
  { path: "/cities/huntersville", element: <HuntersvillePage /> },
  { path: "/cities/mooresville", element: <MooresvillePage /> },
  { path: "/cities/matthews", element: <MatthewsPage /> },
  { path: "/cities/holly-springs", element: <HollySpringsPage /> },
  { path: "/cities/cornelius", element: <CorneliusPage /> },
  { path: "/cities/davidson", element: <DavidsonPage /> },
  { path: "/cities/fuquay-varina", element: <FuquayVarinaPage /> },
  { path: "/cities/morrisville", element: <MorrisvillePage /> },
  { path: "/cities/garner", element: <GarnerPage /> },
  { path: "/cities/statesville", element: <StatesvillePage /> },
  { path: "/cities/salisbury", element: <SalisburyPage /> },
  { path: "/cities/indian-trail", element: <IndianTrailPage /> },
  { path: "/cities/aberdeen", element: <AberdeenPage /> },
  { path: "/cities/asheboro", element: <AsheboroPage /> },
  { path: "/cities/clayton", element: <ClaytonPage /> },
  { path: "/cities/harrisburg", element: <HarrisburgPage /> },
  { path: "/cities/hendersonville", element: <HendersonvillePage /> },
  { path: "/cities/kernersville", element: <KernersvillePage /> },

  { path: "/cities/leland", element: <LelandPage /> },
  { path: "/cities/sanford", element: <SanfordPage /> },
  { path: "/cities/thomasville", element: <ThomasvillePage /> },

  // Colorado
  { path: "/states/colorado", element: <ColoradoPage /> },
  { path: "/cities/denver", element: <DenverPage /> },
  { path: "/cities/colorado-springs", element: <ColoradoSpringsPage /> },
  { path: "/cities/boulder", element: <BoulderPage /> },
  { path: "/cities/fort-collins", element: <FortCollinsPage /> },
  { path: "/cities/aurora-co", element: <AuroraCoPage /> },
  { path: "/cities/arvada", element: <ArvadaPage /> },
  { path: "/cities/pueblo", element: <PuebloPage /> },
  { path: "/cities/lakewood-co", element: <LakewoodCoPage /> },
  { path: "/cities/longmont", element: <LongmontPage /> },
  { path: "/cities/thornton", element: <ThorntonPage /> },
  { path: "/cities/aspen", element: <AspenPage /> },
  { path: "/cities/vail", element: <VailPage /> },
  { path: "/cities/grand-junction", element: <GrandJunctionPage /> },
  { path: "/cities/durango", element: <DurangoPage /> },
  { path: "/cities/castle-rock", element: <CastleRockPage /> },
  { path: "/cities/breckenridge", element: <BreckenridgePage /> },
  { path: "/cities/telluride", element: <TelluridePage /> },
  { path: "/cities/golden", element: <GoldenPage /> },
  { path: "/cities/steamboat-springs", element: <SteamboatSpringsPage /> },
  { path: "/cities/highlands-ranch", element: <HighlandsRanchPage /> },
  { path: "/cities/westminster-co", element: <WestminsterCoPage /> },
  { path: "/cities/broomfield", element: <BroomfieldPage /> },
  { path: "/cities/centennial", element: <CentennialPage /> },
  { path: "/cities/littleton", element: <LittletonPage /> },
  { path: "/cities/loveland", element: <LovelandPage /> },
  { path: "/cities/parker", element: <ParkerPage /> },
  { path: "/cities/greeley", element: <GreeleyPage /> },
  { path: "/cities/englewood-co", element: <EnglewoodCoPage /> },
  { path: "/cities/erie-co", element: <ErieCoPage /> },
  { path: "/cities/lafayette-co", element: <LafayetteCoPage /> },
  { path: "/cities/louisville-co", element: <LouisvilleCoPage /> },
  { path: "/cities/brighton", element: <BrightonPage /> },
  { path: "/cities/commerce-city", element: <CommerceCityPage /> },
  { path: "/cities/northglenn", element: <NorthglennPage /> },
  { path: "/cities/wheat-ridge", element: <WheatRidgePage /> },
  { path: "/cities/castle-pines", element: <CastlePinesPage /> },
  { path: "/cities/federal-heights", element: <FederalHeightsPage /> },
  { path: "/cities/fountain", element: <FountainPage /> },
  { path: "/cities/lone-tree", element: <LoneTreePage /> },
  { path: "/cities/montrose", element: <MontrosePage /> },

  // Texas
  { path: "/states/texas", element: <TexasPage /> },
  { path: "/cities/houston", element: <HoustonPage /> },
  { path: "/cities/dallas", element: <DallasPage /> },
  { path: "/cities/austin", element: <AustinPage /> },
  { path: "/cities/san-antonio", element: <SanAntonioPage /> },
  { path: "/cities/fort-worth", element: <FortWorthPage /> },
  { path: "/cities/el-paso", element: <ElPasoPage /> },
  { path: "/cities/arlington", element: <ArlingtonTXPage /> },
  { path: "/cities/corpus-christi", element: <CorpusChristiPage /> },
  { path: "/cities/plano", element: <PlanoPage /> },
  { path: "/cities/laredo", element: <LaredoPage /> },
  { path: "/cities/lubbock", element: <LubbockPage /> },
  { path: "/cities/garland", element: <GarlandPage /> },
  { path: "/cities/irving", element: <IrvingTXPage /> },
  { path: "/cities/amarillo", element: <AmarilloPage /> },
  { path: "/cities/grand-prairie", element: <GrandPrairiePage /> },
  { path: "/cities/brownsville", element: <BrownsvillePage /> },
  { path: "/cities/mckinney", element: <McKinneyPage /> },
  { path: "/cities/frisco", element: <FriscoPage /> },
  { path: "/cities/pasadena-tx", element: <PasadenaTXPage /> },
  { path: "/cities/mesquite", element: <MesquitePage /> },
  { path: "/cities/killeen", element: <KilleenPage /> },
  { path: "/cities/mcallen", element: <McAllenPage /> },
  { path: "/cities/waco", element: <WacoPage /> },
  { path: "/cities/denton", element: <DentonPage /> },
  { path: "/cities/midland-tx", element: <MidlandTXPage /> },
  { path: "/cities/carrollton-tx", element: <CarrolltonTXPage /> },
  { path: "/cities/abilene", element: <AbileneTXPage /> },
  { path: "/cities/beaumont", element: <BeaumontTXPage /> },
  { path: "/cities/round-rock", element: <RoundRockPage /> },
  { path: "/cities/odessa", element: <OdessaTXPage /> },
  { path: "/cities/wichita-falls", element: <WichitaFallsPage /> },
  { path: "/cities/richardson", element: <RichardsonPage /> },
  { path: "/cities/tyler", element: <TylerPage /> },
  { path: "/cities/lewisville", element: <LewisvillePage /> },
  { path: "/cities/pearland", element: <PearlandPage /> },
  { path: "/cities/san-angelo", element: <SanAngeloPage /> },
  { path: "/cities/college-station", element: <CollegeStationPage /> },
  { path: "/cities/sugar-land", element: <SugarLandPage /> },
  { path: "/cities/allen", element: <AllenPage /> },
  { path: "/cities/new-braunfels", element: <NewBraunfelsPage /> },
  { path: "/cities/conroe", element: <ConroePage /> },
  { path: "/cities/longview", element: <LongviewPage /> },

  // New York
  { path: "/cities/staten-island", element: <StatenIslandPage /> },
  { path: "/cities/long-island", element: <LongIslandPage /> },
  { path: "/cities/bay-shore", element: <BayShorePage /> },
  { path: "/cities/commack", element: <CommackPage /> },
  { path: "/cities/hicksville", element: <HicksvillePage /> },
  { path: "/cities/huntington", element: <HuntingtonPage /> },
  { path: "/cities/binghamton", element: <BinghamtonPage /> },
  { path: "/cities/white-plains", element: <WhitePlainsPage /> },
  { path: "/cities/poughkeepsie", element: <PoughkeepsiePage /> },
  { path: "/cities/newburgh", element: <NewburghPage /> },
  { path: "/cities/new-rochelle", element: <NewRochellePage /> },
  { path: "/cities/elmira", element: <ElmiraPage /> },
  { path: "/cities/ithaca", element: <IthacaPage /> },
  { path: "/cities/corning", element: <CorningPage /> },
  { path: "/cities/jamestown", element: <JamestownPage /> },

  { path: "/states/new-york", element: <NewYorkPage /> },
  { path: "/cities/new-york-city", element: <NewYorkCityPage /> },
  { path: "/cities/buffalo", element: <BuffaloPage /> },
  { path: "/cities/albany-ny", element: <AlbanyNYPage /> },
  { path: "/cities/brooklyn", element: <BrooklynPage /> },
  { path: "/cities/queens", element: <QueensPage /> },
  { path: "/cities/rochester", element: <RochesterPage /> },
  { path: "/cities/syracuse", element: <SyracusePage /> },
  { path: "/cities/yonkers", element: <YonkersPage /> },
  { path: "/cities/the-bronx", element: <TheBronxPage /> },
  { path: "/cities/utica", element: <UticaPage /> },
  { path: "/cities/schenectady", element: <SchenectadyPage /> },
  { path: "/cities/troy", element: <TroyPage /> },
  { path: "/cities/saratoga-springs", element: <SaratogaSpringsPage /> },
  { path: "/cities/plattsburgh", element: <PlattsburghPage /> },
  { path: "/cities/watertown", element: <WatertownPage /> },
  { path: "/cities/ogdensburg", element: <OgdensburgPage /> },
  { path: "/cities/glens-falls", element: <GlensFallsPage /> },
  { path: "/cities/kingston", element: <KingstonPage /> },
  { path: "/cities/middletown-ny", element: <MiddletownNYPage /> },
  { path: "/cities/new-paltz", element: <NewPaltzPage /> },
  { path: "/cities/beacon", element: <BeaconPage /> },
  { path: "/cities/fishkill", element: <FishkillPage /> },
  { path: "/cities/mount-vernon", element: <MountVernonPage /> },
  { path: "/cities/tarrytown", element: <TarrytownPage /> },
  { path: "/cities/rhinebeck", element: <RhinebeckPage /> },
  { path: "/cities/hyde-park", element: <HydeParkPage /> },
  { path: "/cities/red-hook", element: <RedHookPage /> },
  { path: "/cities/hudson-ny", element: <HudsonPage /> },
  { path: "/cities/catskill", element: <CatskillPage /> },
  { path: "/cities/lake-george", element: <LakeGeorgePage /> },
  { path: "/cities/ballston-spa", element: <BallstonSpaPage /> },
  { path: "/cities/amsterdam-ny", element: <AmsterdamNYPage /> },
  { path: "/cities/gloversville", element: <GloversvillePage /> },
  { path: "/cities/rome-ny", element: <RomeNYPage /> },
  { path: "/cities/malone", element: <MalonePage /> },
  { path: "/cities/tupper-lake", element: <TupperLakePage /> },
  { path: "/cities/islip", element: <IslipPage /> },
  { path: "/cities/levittown", element: <LevittownPage /> },
  { path: "/cities/smithtown", element: <SmithtownPage /> },
  { path: "/cities/west-babylon", element: <WestBabylonPage /> },
  { path: "/cities/niagara-falls", element: <NiagaraFallsPage /> },
  { path: "/cities/chatham", element: <ChathamPage /> },

  // Illinois
  { path: "/states/illinois", element: <IllinoisPage /> },
  { path: "/cities/naperville", element: <NapervillePage /> },
  { path: "/cities/aurora", element: <AuroraPage /> },
  { path: "/cities/rockford", element: <RockfordPage /> },
  { path: "/cities/joliet", element: <JolietPage /> },
  { path: "/cities/chicago", element: <ChicagoILPage /> },
  { path: "/cities/springfield-il", element: <SpringfieldILPage /> },
  { path: "/cities/peoria", element: <PeoriaILPage /> },
  { path: "/cities/champaign", element: <ChampaignILPage /> },
  { path: "/cities/elgin-il", element: <ElginILPage /> },
  { path: "/cities/waukegan", element: <WaukeganPage /> },
  { path: "/cities/evanston", element: <EvanstonPage /> },
  { path: "/cities/schaumburg", element: <SchaumburgPage /> },
  { path: "/cities/arlington-heights", element: <ArlingtonHeightsPage /> },
  { path: "/cities/cicero", element: <CiceroPage /> },
  { path: "/cities/skokie", element: <SkokiePage /> },
  { path: "/cities/oak-park", element: <OakParkPage /> },
  { path: "/cities/palatine", element: <PalatinePage /> },
  { path: "/cities/des-plaines", element: <DesPlainesPage /> },
  { path: "/cities/bloomington-il", element: <BloomingtonILPage /> },
  { path: "/cities/decatur-il", element: <DecaturILPage /> },
  { path: "/cities/carbondale", element: <CarbondalePage /> },
  { path: "/cities/belleville", element: <BellevillePage /> },
  { path: "/cities/alton", element: <AltonPage /> },
  { path: "/cities/moline", element: <MolinePage /> },
  { path: "/cities/rock-island", element: <RockIslandPage /> },
  { path: "/cities/elmhurst", element: <ElmhurstPage /> },
  { path: "/cities/wheaton", element: <WheatonPage /> },
  { path: "/cities/downers-grove", element: <DownersGrovePage /> },
  { path: "/cities/normal", element: <NormalPage /> },
  { path: "/cities/dekalb", element: <DekalbPage /> },
  { path: "/cities/galesburg", element: <GalesburgPage /> },
  { path: "/cities/quincy-il", element: <QuincyIlPage /> },
  { path: "/cities/glenview", element: <GlenviewPage /> },
  { path: "/cities/highland-park", element: <HighlandParkPage /> },
  { path: "/cities/orland-park", element: <OrlandParkPage /> },

  // Pennsylvania
  { path: "/states/pennsylvania", element: <PennsylvaniaPage /> },
  { path: "/cities/philadelphia", element: <PhiladelphiaPage /> },
  { path: "/cities/pittsburgh", element: <PittsburghPage /> },
  { path: "/cities/allentown", element: <AllentownPage /> },
  { path: "/cities/lancaster", element: <LancasterPaPage /> },
  { path: "/cities/harrisburg-pa", element: <HarrisburgPaPage /> },
  { path: "/cities/scranton", element: <ScrantonPage /> },
  { path: "/cities/reading", element: <ReadingPage /> },
  { path: "/cities/bethlehem", element: <BethlehemPage /> },
  { path: "/cities/erie", element: <EriePage /> },
  { path: "/cities/state-college", element: <StateCollegePage /> },
  { path: "/cities/chambersburg", element: <ChambersburgPage /> },
  { path: "/cities/wilkes-barre", element: <WilkesBarrePage /> },
  { path: "/cities/king-of-prussia", element: <KingOfPrussiaPage /> },
  { path: "/cities/lehigh-valley", element: <LehighValleyPage /> },
  { path: "/cities/altoona", element: <AltoonaPage /> },
  { path: "/cities/easton", element: <EastonPage /> },
  { path: "/cities/norristown", element: <NorristownPage /> },
  { path: "/cities/pottstown", element: <PottstownPage /> },
  { path: "/cities/york-pa", element: <YorkPaPage /> },
  { path: "/cities/west-chester", element: <WestChesterPage /> },
  { path: "/cities/bloomsburg", element: <BloomsburgPage /> },
  { path: "/cities/chester", element: <ChesterPage /> },
  { path: "/cities/gettysburg", element: <GettysburgPage /> },
  { path: "/cities/hazleton", element: <HazletonPage /> },
  { path: "/cities/johnstown", element: <JohnstownPage /> },
  { path: "/cities/lebanon-pa", element: <LebanonPaPage /> },
  { path: "/cities/main-line", element: <MainLinePage /> },
  { path: "/cities/mckeesport", element: <McKeesportPage /> },
  { path: "/cities/monroeville", element: <MonroevillePage /> },
  { path: "/cities/mount-pocono", element: <MountPoconoPage /> },
  { path: "/cities/new-castle", element: <NewCastlePage /> },
  { path: "/cities/upper-darby", element: <UpperDarbyPage /> },
  { path: "/cities/williamsport", element: <WilliamsportPage /> },

  // Oregon
  { path: "/states/oregon", element: <OregonPage /> },
  { path: "/cities/portland", element: <PortlandPage /> },
  { path: "/cities/eugene", element: <EugenePage /> },
  { path: "/cities/salem", element: <SalemPage /> },
  { path: "/cities/bend", element: <BendPage /> },
  { path: "/cities/medford", element: <MedfordPage /> },
  { path: "/cities/beaverton", element: <BeavertonPage /> },
  { path: "/cities/corvallis", element: <CorvallisPage /> },
  { path: "/cities/hillsboro", element: <HillsboroPage /> },
  { path: "/cities/gresham", element: <GreshamPage /> },
  { path: "/cities/lake-oswego", element: <LakeOswegoPage /> },
  { path: "/cities/tigard", element: <TigardPage /> },
  { path: "/cities/tualatin", element: <TualatinPage /> },
  { path: "/cities/oregon-city", element: <OregonCityPage /> },
  { path: "/cities/west-linn", element: <WestLinnPage /> },
  { path: "/cities/milwaukie", element: <MilwaukiePage /> },
  { path: "/cities/happy-valley", element: <HappyValleyPage /> },
  { path: "/cities/troutdale", element: <TroutdalePage /> },
  { path: "/cities/forest-grove", element: <ForestGrovePage /> },
  { path: "/cities/newberg", element: <NewbergPage /> },
  { path: "/cities/wilsonville", element: <WilsonvilleORPage /> },
  { path: "/cities/sherwood", element: <SherwoodORPage /> },
  { path: "/cities/canby", element: <CanbyPage /> },
  { path: "/cities/gladstone", element: <GladstonePage /> },
  { path: "/cities/estacada", element: <EstacadaPage /> },
  { path: "/cities/sandy", element: <SandyORPage /> },
  { path: "/cities/hood-river", element: <HoodRiverPage /> },
  { path: "/cities/the-dalles", element: <TheDallesPage /> },
  { path: "/cities/pendleton", element: <PendletonPage /> },
  { path: "/cities/la-grande", element: <LaGrandePage /> },
  { path: "/cities/ontario-or", element: <OntarioOrPage /> },
  { path: "/cities/klamath-falls", element: <KlamathFallsPage /> },
  { path: "/cities/grants-pass", element: <GrantsPassPage /> },
  { path: "/cities/ashland-or", element: <AshlandOrPage /> },
  { path: "/cities/roseburg", element: <RoseburgPage /> },
  { path: "/cities/albany-or", element: <AlbanyOrPage /> },
  { path: "/cities/lebanon-or", element: <LebanonOrPage /> },
  { path: "/cities/springfield-or", element: <SpringfieldOrPage /> },
  { path: "/cities/coos-bay", element: <CoosBayPage /> },
  { path: "/cities/astoria", element: <AstoriaOrPage /> },
  { path: "/cities/seaside", element: <SeasideOrPage /> },
  { path: "/cities/lincoln-city", element: <LincolnCityOrPage /> },
  { path: "/cities/newport-or", element: <NewportOrPage /> },
  { path: "/cities/tillamook", element: <TillamookPage /> },
  { path: "/cities/redmond", element: <RedmondOrPage /> },
  { path: "/cities/prineville", element: <PrinevillePage /> },
  { path: "/cities/hermiston", element: <HermistonPage /> },
  { path: "/cities/mcminnville", element: <McMinnvillePage /> },
  { path: "/cities/keizer", element: <KeizerPage /> },
  { path: "/cities/woodburn", element: <WoodburnPage /> },

  // Arizona
  { path: "/states/arizona", element: <ArizonaPage /> },
  { path: "/cities/apache-junction", element: <ApacheJunctionPage /> },
  { path: "/cities/avondale", element: <AvondalePage /> },
  { path: "/cities/buckeye", element: <BuckeyePage /> },
  { path: "/cities/bullhead-city", element: <BullheadCityPage /> },
  { path: "/cities/casa-grande", element: <CasaGrandePage /> },
  { path: "/cities/chandler", element: <ChandlerPage /> },
  { path: "/cities/cottonwood", element: <CottonwoodPage /> },
  { path: "/cities/flagstaff", element: <FlagstaffPage /> },
  { path: "/cities/gilbert", element: <GilbertPage /> },
  { path: "/cities/glendale-az", element: <GlendaleAZPage /> },
  { path: "/cities/goodyear", element: <GoodyearPage /> },
  { path: "/cities/green-valley", element: <GreenValleyPage /> },
  { path: "/cities/holbrook", element: <HolbrookPage /> },
  { path: "/cities/kingman", element: <KingmanPage /> },
  { path: "/cities/lake-havasu-city", element: <LakeHavasuCityPage /> },
  { path: "/cities/marana", element: <MaranaPage /> },
  { path: "/cities/maricopa", element: <MaricopaPage /> },
  { path: "/cities/mesa", element: <MesaPage /> },
  { path: "/cities/nogales", element: <NogalesPage /> },
  { path: "/cities/oro-valley", element: <OroValleyPage /> },
  { path: "/cities/page-az", element: <PageAZPage /> },
  { path: "/cities/paradise-valley", element: <ParadiseValleyPage /> },
  { path: "/cities/payson", element: <PaysonPage /> },
  { path: "/cities/peoria-az", element: <PeoriaAZPage /> },
  { path: "/cities/phoenix", element: <PhoenixPage /> },
  { path: "/cities/scottsdale", element: <ScottsdalePage /> },
  { path: "/cities/tucson", element: <TucsonPage /> },
  { path: "/cities/prescott", element: <PrescottPage /> },
  { path: "/cities/sedona", element: <SedonaPage /> },
  { path: "/cities/sierra-vista", element: <SierraVistaPage /> },
  { path: "/cities/yuma", element: <YumaPage /> },
  { path: "/cities/tempe", element: <TempePage /> },
  { path: "/cities/prescott-valley", element: <PrescottValleyPage /> },
  { path: "/cities/queen-creek", element: <QueenCreekPage /> },
  { path: "/cities/sahuarita", element: <SahuaritaPage /> },
  { path: "/cities/san-luis", element: <SanLuisPage /> },
  { path: "/cities/show-low", element: <ShowLowPage /> },
  { path: "/cities/somerton", element: <SomertonPage /> },
  { path: "/cities/surprise", element: <SurprisePage /> },
  { path: "/cities/winslow", element: <WinslowPage /> },

  // New Jersey
  { path: "/states/new-jersey", element: <NewJerseyPage /> },
  { path: "/cities/newark", element: <NewarkPage /> },
  { path: "/cities/jersey-city", element: <JerseyCityPage /> },
  { path: "/cities/paterson", element: <PatersonPage /> },
  { path: "/cities/elizabeth", element: <ElizabethPage /> },
  { path: "/cities/trenton", element: <TrentonPage /> },
  { path: "/cities/cherry-hill", element: <CherryHillPage /> },
  { path: "/cities/hoboken", element: <HobokenPage /> },
  { path: "/cities/edison", element: <EdisonPage /> },
  { path: "/cities/clifton", element: <CliftonPage /> },
  { path: "/cities/princeton", element: <PrincetonPage /> },
  { path: "/cities/bayonne", element: <BayonnePage /> },
  { path: "/cities/union-city", element: <UnionCityPage /> },
  { path: "/cities/secaucus", element: <SecaucusPage /> },
  { path: "/cities/north-bergen", element: <NorthBergenPage /> },
  { path: "/cities/hackensack", element: <HackensackPage /> },
  { path: "/cities/paramus", element: <ParamusPage /> },
  { path: "/cities/ridgewood", element: <RidgewoodPage /> },
  { path: "/cities/fort-lee", element: <FortLeePage /> },
  { path: "/cities/new-brunswick", element: <NewBrunswickPage /> },
  { path: "/cities/piscataway", element: <PiscatawayPage /> },
  { path: "/cities/woodbridge", element: <WoodbridgePage /> },
  { path: "/cities/plainfield", element: <PlainfieldPage /> },
  { path: "/cities/toms-river", element: <TomsRiverPage /> },
  { path: "/cities/brick", element: <BrickPage /> },
  { path: "/cities/point-pleasant", element: <PointPleasantPage /> },
  { path: "/cities/lakewood-nj", element: <LakewoodNJPage /> },
  { path: "/cities/red-bank", element: <RedBankPage /> },
  { path: "/cities/long-branch", element: <LongBranchPage /> },
  { path: "/cities/asbury-park", element: <AsburyParkPage /> },
  { path: "/cities/middletown-nj", element: <MiddletownNJPage /> },
  { path: "/cities/morristown", element: <MorristownPage /> },
  { path: "/cities/parsippany", element: <ParsippanyPage /> },
  { path: "/cities/denville", element: <DenvillePage /> },
  { path: "/cities/madison", element: <MadisonPage /> },
  { path: "/cities/somerville-nj", element: <SomervilleNJPage /> },
  { path: "/cities/bridgewater", element: <BridgewaterPage /> },
  { path: "/cities/hillsborough", element: <HillsboroughPage /> },
  { path: "/cities/franklin", element: <FranklinPage /> },
  { path: "/cities/burlington-nj", element: <BurlingtonNJPage /> },
  { path: "/cities/mount-holly", element: <MountHollyPage /> },
  { path: "/cities/moorestown", element: <MoorestownPage /> },
  { path: "/cities/sparta-nj", element: <SpartaNJPage /> },
  { path: "/cities/vernon-nj", element: <VernonNJPage /> },
  { path: "/cities/hamburg-nj", element: <HamburgNJPage /> },
  { path: "/cities/medford-nj", element: <MedfordNJPage /> },
  { path: "/cities/evesham", element: <EveshamPage /> },
  { path: "/cities/bordentown", element: <BordentownPage /> },
  { path: "/cities/camden-nj", element: <CamdenNJPage /> },
  { path: "/cities/freehold", element: <FreeholdPage /> },
  { path: "/cities/vineland", element: <VinelandPage /> },
  { path: "/cities/bloomfield", element: <BloomfieldPage /> },
  { path: "/cities/east-orange", element: <EastOrangePage /> },
  { path: "/cities/hamilton-nj", element: <HamiltonNJPage /> },
  { path: "/cities/irvington", element: <IrvingtonPage /> },
  { path: "/cities/englewood", element: <EnglewoodPage /> },
  { path: "/cities/kearny", element: <KearnyPage /> },
  { path: "/cities/montclair", element: <MontclairPage /> },
  { path: "/cities/passaic", element: <PassaicPage /> },
  { path: "/cities/perth-amboy", element: <PerthAmboyPage /> },
  { path: "/cities/teaneck", element: <TeaneckPage /> },
  { path: "/cities/wayne-nj", element: <WayneNJPage /> },
  { path: "/cities/west-new-york", element: <WestNewYorkPage /> },
  { path: "/cities/westfield", element: <WestfieldPage /> },

  // Washington
  { path: "/states/washington", element: <WashingtonPage /> },
  { path: "/cities/seattle", element: <SeattlePage /> },
  { path: "/cities/spokane", element: <SpokanuPage /> },
  { path: "/cities/spokane-valley", element: <SpokaneValleyPage /> },
  { path: "/cities/tacoma", element: <TacomaPage /> },
  { path: "/cities/bellevue", element: <BellevuePage /> },
  { path: "/cities/renton", element: <RentonPage /> },
  { path: "/cities/kirkland", element: <KirklandPage /> },
  { path: "/cities/redmond-wa", element: <RedmondWAPage /> },
  { path: "/cities/everett", element: <EverettPage /> },
  { path: "/cities/bellingham", element: <BellinghamPage /> },
  { path: "/cities/olympia", element: <OlympiaPage /> },
  { path: "/cities/kennewick", element: <KennewickPage /> },
  { path: "/cities/yakima", element: <YakimaPage /> },
  { path: "/cities/marysville", element: <MarysvillePage /> },
  { path: "/cities/federal-way", element: <FederalWayPage /> },
  { path: "/cities/shoreline", element: <ShorelinePage /> },
  { path: "/cities/sammamish", element: <SammamishPage /> },
  { path: "/cities/kent", element: <KentPage /> },

  // Tennessee
  { path: "/states/tennessee", element: <TennesseePage /> },
  { path: "/cities/nashville", element: <NashvillePage /> },
  { path: "/cities/memphis", element: <MemphisPage /> },
  { path: "/cities/knoxville", element: <KnoxvillePage /> },
  { path: "/cities/chattanooga", element: <ChattanoogaPage /> },
  { path: "/cities/clarksville", element: <ClarksvilleTNPage /> },
  { path: "/cities/murfreesboro", element: <MurfreesboroTNPage /> },
  { path: "/cities/johnson-city", element: <JohnsonCityTNPage /> },
  { path: "/cities/kingsport", element: <KingsportTNPage /> },
  { path: "/cities/franklin-tn", element: <FranklinTNPage /> },
  { path: "/cities/jackson", element: <JacksonTNPage /> },
  { path: "/cities/cookeville", element: <CookevilleTNPage /> },
  { path: "/cities/bristol", element: <BristolTNPage /> },
  { path: "/cities/oak-ridge", element: <OakRidgeTNPage /> },
  { path: "/cities/maryville-tn", element: <MaryvilleTNPage /> },
  { path: "/cities/columbia-tn", element: <ColumbiaTnPage /> },

  // Indiana
  { path: "/states/indiana", element: <IndianaPage /> },
  { path: "/cities/indianapolis", element: <IndianapolisPage /> },
  { path: "/cities/fort-wayne", element: <FortWaynePage /> },
  { path: "/cities/evansville", element: <EvansvillePage /> },
  { path: "/cities/carmel", element: <CarmelPage /> },
  { path: "/cities/bloomington-in", element: <BloomingtonInPage /> },
  { path: "/cities/columbus-in", element: <ColumbusInPage /> },
  { path: "/cities/elkhart", element: <ElkhartPage /> },
  { path: "/cities/fishers", element: <FishersPage /> },
  { path: "/cities/gary", element: <GaryPage /> },
  { path: "/cities/goshen", element: <GoshenPage /> },
  { path: "/cities/greenwood-in", element: <GreenwoodInPage /> },
  { path: "/cities/hammond-in", element: <HammondInPage /> },
  { path: "/cities/kokomo", element: <KokomoPage /> },
  { path: "/cities/lafayette-in", element: <LafayetteInPage /> },
  { path: "/cities/merrillville", element: <MerrillvillePage /> },
  { path: "/cities/mishawaka", element: <MishawakaPage /> },
  { path: "/cities/muncie", element: <MunciePage /> },
  { path: "/cities/noblesville", element: <NoblesvillePage /> },
  { path: "/cities/south-bend", element: <SouthBendPage /> },
  { path: "/cities/terre-haute", element: <TerreHautePage /> },
  { path: "/cities/valparaiso", element: <ValparaisoPage /> },

  // Michigan
  { path: "/states/michigan", element: <MichiganPage /> },
  { path: "/cities/macomb-county", element: <MacombCountyPage /> },
  { path: "/cities/kent-county", element: <KentCountyPage /> },
  { path: "/cities/ottawa-county", element: <OttawaCountyPage /> },
  { path: "/cities/grand-haven", element: <GrandHavenPage /> },
  { path: "/cities/portage", element: <PortagePage /> },
  { path: "/cities/monroe", element: <MonroePage /> },
  { path: "/cities/muskegon", element: <MuskegonPage /> },
  { path: "/cities/ypsilanti", element: <YpsilantiPage /> },
  { path: "/cities/detroit", element: <DetroitPage /> },
  { path: "/cities/flint", element: <FlintPage /> },
  { path: "/cities/grand-rapids", element: <GrandRapidsPage /> },
  { path: "/cities/kalamazoo", element: <KalamazooPage /> },
  { path: "/cities/ann-arbor", element: <AnnArborPage /> },
  { path: "/cities/lansing-mi", element: <LansingMIPage /> },
  { path: "/cities/sterling-heights", element: <SterlingHeightsPage /> },
  { path: "/cities/troy-mi", element: <TroyMIPage /> },
  { path: "/cities/warren", element: <WarrenMIPage /> },
  { path: "/cities/dearborn", element: <DearbornPage /> },
  { path: "/cities/pontiac", element: <PontiacPage /> },
  { path: "/cities/southfield", element: <SouthfieldPage /> },

  // Maryland
  { path: "/states/maryland", element: <MarylandPage /> },
  { path: "/cities/baltimore", element: <BaltimorePage /> },
  { path: "/cities/annapolis", element: <AnnapolisPage /> },
  { path: "/cities/silver-spring", element: <SilverSpringPage /> },
  { path: "/cities/bethesda", element: <BethesdaPage /> },
  { path: "/cities/columbia", element: <ColumbiaPage /> },
  { path: "/cities/rockville", element: <RockvillePage /> },
  { path: "/cities/frederick-md", element: <FrederickMdPage /> },
  { path: "/cities/gaithersburg", element: <GaithersburgPage /> },
  { path: "/cities/towson", element: <TowsonPage /> },
  { path: "/cities/hagerstown", element: <HagerstownPage /> },
  { path: "/cities/waldorf", element: <WaldorfPage /> },
  { path: "/cities/salisbury-md", element: <SalisburyMdPage /> },
  { path: "/cities/bowie", element: <BowiePage /> },
  { path: "/cities/college-park", element: <CollegeParkPage /> },
  { path: "/cities/ocean-city", element: <OceanCityPage /> },
  { path: "/cities/catonsville", element: <CatonsvillePage /> },
  { path: "/cities/cumberland", element: <CumberlandMdPage /> },
  { path: "/cities/dundalk", element: <DundalkPage /> },
  { path: "/cities/easton-md", element: <EastonMdPage /> },
  { path: "/cities/ellicott-city", element: <EllicottCityPage /> },
  { path: "/cities/essex", element: <EssexPage /> },
  { path: "/cities/germantown", element: <GermantownPage /> },
  { path: "/cities/greenbelt", element: <GreenbeltPage /> },
  { path: "/cities/la-plata", element: <LaPlataPage /> },
  { path: "/cities/laurel", element: <LaurelPage /> },
  { path: "/cities/lexington-park", element: <LexingtonParkPage /> },

  // Maine
  { path: "/states/maine", element: <MainePage /> },
  { path: "/cities/portland-me", element: <PortlandMePage /> },
  { path: "/cities/bangor", element: <BangorPage /> },
  { path: "/cities/lewiston", element: <LewistonPage /> },
  { path: "/cities/south-portland", element: <SouthPortlandPage /> },
  { path: "/cities/auburn-me", element: <AuburnMePage /> },
  { path: "/cities/biddeford", element: <BiddefordPage /> },
  { path: "/cities/sanford", element: <SanfordMePage /> },
  { path: "/cities/saco", element: <SacoPage /> },
  { path: "/cities/augusta-me", element: <AugustaMePage /> },
  { path: "/cities/westbrook", element: <WestbrookPage /> },
  { path: "/cities/waterville", element: <WatervillePage /> },
  { path: "/cities/presque-isle", element: <PresqueIslePage /> },
  { path: "/cities/ellsworth", element: <EllsworthPage /> },
  { path: "/cities/old-town", element: <OldTownPage /> },
  { path: "/cities/brewer", element: <BrewerPage /> },
  { path: "/cities/gardiner", element: <GardinerPage /> },
  { path: "/cities/bath", element: <BathPage /> },
  { path: "/cities/brunswick", element: <BrunswickPage /> },
  { path: "/cities/scarborough", element: <ScarboroughPage /> },
  { path: "/cities/gorham", element: <GorhamPage /> },
  { path: "/cities/yarmouth", element: <YarmouthPage /> },
  { path: "/cities/windham", element: <WindhamPage /> },
  { path: "/cities/falmouth", element: <FalmouthPage /> },
  { path: "/cities/kennebunk", element: <KennebunkPage /> },
  { path: "/cities/orono", element: <OronoPage /> },
  { path: "/cities/belfast", element: <BelfastPage /> },
  { path: "/cities/rockland", element: <RocklandPage /> },
  { path: "/cities/bar-harbor", element: <BarHarborPage /> },
  { path: "/cities/camden", element: <CamdenPage /> },
  { path: "/cities/freeport", element: <FreeportPage /> },
  { path: "/cities/topsham", element: <TopshamPage /> },
  { path: "/cities/york-me", element: <YorkPage /> },
  { path: "/cities/wells", element: <WellsPage /> },
  { path: "/cities/cumberland-me", element: <CumberlandPage /> },
  { path: "/cities/ogunquit", element: <OgunquitPage /> },
  { path: "/cities/hampden", element: <HampdenPage /> },

  // Wisconsin
  { path: "/states/wisconsin", element: <WisconsinPage /> },
  { path: "/cities/milwaukee", element: <MilwaukeePage /> },
  { path: "/cities/madison-wi", element: <MadisonWiPage /> },
  { path: "/cities/green-bay", element: <GreenBayPage /> },
  { path: "/cities/racine", element: <RacinePage /> },
  { path: "/cities/appleton", element: <AppletonPage /> },
  { path: "/cities/eau-claire", element: <EauClairePage /> },
  { path: "/cities/kenosha-wi", element: <KenoshaWiPage /> },
  { path: "/cities/wausau", element: <WausauPage /> },
  { path: "/cities/la-crosse", element: <LaCrossePage /> },
  { path: "/cities/oshkosh", element: <OshkoshPage /> },
  { path: "/cities/waukesha", element: <WaukeshaPage /> },
  { path: "/cities/janesville", element: <JanesvillePage /> },
  { path: "/cities/sheboygan", element: <SheboyganPage /> },
  { path: "/cities/superior", element: <SuperiorPage /> },
  { path: "/cities/fond-du-lac", element: <FondDuLacPage /> },
  { path: "/cities/west-bend", element: <WestBendPage /> },
  { path: "/cities/manitowoc", element: <ManitowocPage /> },
  { path: "/cities/beloit", element: <BeloitPage /> },
  { path: "/cities/neenah", element: <NeenahPage /> },
  { path: "/cities/racine-county", element: <RacineCountyPage /> },

  // Nevada
  { path: "/states/nevada", element: <NevadaPage /> },
  { path: "/cities/las-vegas", element: <LasVegasPage /> },
  { path: "/cities/henderson-nv", element: <HendersonNVPage /> },
  { path: "/cities/reno", element: <RenoPage /> },
  { path: "/cities/sparks", element: <SparksPage /> },
  { path: "/cities/carson-city", element: <CarsonCityPage /> },
  { path: "/cities/elko", element: <ElkoPage /> },
  { path: "/cities/pahrump", element: <PahrumpPage /> },
  { path: "/cities/mesquite-nv", element: <MesquiteNVPage /> },
  { path: "/cities/laughlin", element: <LaughlinPage /> },
  { path: "/cities/boulder-city", element: <BoulderCityPage /> },
  { path: "/cities/fernley", element: <FernleyPage /> },
  { path: "/cities/gardnerville", element: <GardnervillePage /> },
  { path: "/cities/incline-village", element: <InclineVillagePage /> },
  { path: "/cities/battle-mountain", element: <BattleMountainPage /> },
  { path: "/cities/fallon", element: <FallonPage /> },
  { path: "/cities/minden", element: <MindenPage /> },
  { path: "/cities/spring-creek", element: <SpringCreekPage /> },
  { path: "/cities/winnemucca", element: <WinnemuccaNVPage /> },
  { path: "/cities/north-las-vegas", element: <NorthLasVegasPage /> },
  { path: "/cities/summerlin", element: <SummerlinPage /> },
  { path: "/cities/sun-valley", element: <SunValleyPage /> },

  // South Carolina
  { path: "/states/south-carolina", element: <SouthCarolinaPage /> },
  { path: "/cities/charleston", element: <CharlestonPage /> },
  { path: "/cities/columbia-sc", element: <ColumbiaSCPage /> },
  { path: "/cities/greenville-sc", element: <GreenvilleSCPage /> },
  { path: "/cities/myrtle-beach", element: <MyrtleBeachPage /> },
  { path: "/cities/hilton-head", element: <HiltonHeadPage /> },
  { path: "/cities/spartanburg", element: <SpartanburgPage /> },
  { path: "/cities/rock-hill", element: <RockHillPage /> },
  { path: "/cities/mount-pleasant", element: <MountPleasantPage /> },
  { path: "/cities/florence-sc", element: <FlorenceSCPage /> },
  { path: "/cities/summerville", element: <SummervilleSCPage /> },
  { path: "/cities/aiken", element: <AikenSCPage /> },
  { path: "/cities/goose-creek", element: <GooseCreekSCPage /> },

  // Alabama
  { path: "/states/alabama", element: <AlabamaPage /> },
  { path: "/cities/birmingham", element: <BirminghamPage /> },
  { path: "/cities/huntsville", element: <HuntsvilleALPage /> },
  { path: "/cities/mobile-al", element: <MobileALPage /> },
  { path: "/cities/montgomery-al", element: <MontgomeryALPage /> },
  { path: "/cities/tuscaloosa", element: <TuscaloosaPage /> },
  { path: "/cities/auburn", element: <AuburnPage /> },
  { path: "/cities/dothan", element: <DothanPage /> },
  { path: "/cities/hoover", element: <HooverPage /> },
  { path: "/cities/decatur-al", element: <DecaturALPage /> },
  { path: "/cities/madison-al", element: <MadisonALPage /> },
  { path: "/cities/florence-al", element: <FlorenceALPage /> },
  { path: "/cities/gadsden-al", element: <GadsdenALPage /> },

  // Louisiana
  { path: "/states/louisiana", element: <LouisianaPage /> },
  { path: "/cities/new-orleans", element: <NewOrleansPage /> },
  { path: "/cities/baton-rouge", element: <BatonRougePage /> },
  { path: "/cities/shreveport", element: <ShreveportPage /> },
  { path: "/cities/lafayette", element: <LafayetteLAPage /> },
  { path: "/cities/lake-charles", element: <LakeCharlesPage /> },
  { path: "/cities/monroe", element: <MonroeLAPage /> },
  { path: "/cities/houma", element: <HoumaPage /> },
  { path: "/cities/slidell", element: <SlidellPage /> },
  { path: "/cities/alexandria-la", element: <AlexandriaLAPage /> },
  { path: "/cities/hammond", element: <HammondPage /> },
  { path: "/cities/bossier-city", element: <BossierCityPage /> },
  { path: "/cities/mandeville", element: <MandevillePage /> },
  { path: "/cities/kenner", element: <KennerPage /> },
  { path: "/cities/metairie", element: <MetairiePage /> },
  { path: "/cities/thibodaux", element: <ThibodauxPage /> },

  // Oklahoma
  { path: "/states/oklahoma", element: <OklahomaPage /> },
  { path: "/cities/oklahoma-city", element: <OklahomaCityPage /> },
  { path: "/cities/tulsa", element: <TulsaPage /> },
  { path: "/cities/norman", element: <NormanPage /> },
  { path: "/cities/edmond", element: <EdmondOKPage /> },
  { path: "/cities/broken-arrow", element: <BrokenArrowPage /> },
  { path: "/cities/lawton", element: <LawtonPage /> },
  { path: "/cities/stillwater", element: <StillwaterPage /> },
  { path: "/cities/enid", element: <EnidPage /> },
  { path: "/cities/muskogee", element: <MuskogeePage /> },
  { path: "/cities/moore", element: <MoorePage /> },
  { path: "/cities/bartlesville", element: <BartlesvillePage /> },
  { path: "/cities/shawnee", element: <ShawneePage /> },

  // Utah
  { path: "/states/utah", element: <UtahPage /> },
  { path: "/cities/salt-lake-city", element: <SaltLakeCityPage /> },
  { path: "/cities/provo", element: <ProvoUTPage /> },
  { path: "/cities/ogden", element: <OgdenUTPage /> },
  { path: "/cities/st-george", element: <StGeorgePage /> },
  { path: "/cities/orem", element: <OremUTPage /> },
  { path: "/cities/logan", element: <LoganUTPage /> },
  { path: "/cities/park-city", element: <ParkCityUTPage /> },
  { path: "/cities/moab", element: <MoabUTPage /> },
  { path: "/cities/sandy-ut", element: <SandyUTPage /> },
  { path: "/cities/cedar-city", element: <CedarCityUTPage /> },
  { path: "/cities/west-valley-city", element: <WestValleyCityUTPage /> },
  { path: "/cities/layton", element: <LaytonUTPage /> },

  // Kansas
  { path: "/states/kansas", element: <KansasPage /> },
  { path: "/cities/overland-park", element: <OverlandParkPage /> },
  { path: "/cities/olathe", element: <OlathePage /> },
  { path: "/cities/topeka", element: <TopekaPage /> },
  { path: "/cities/lawrence", element: <LawrencePage /> },
  { path: "/cities/wichita", element: <WichitaPage /> },
  { path: "/cities/lenexa", element: <LenexaPage /> },
  { path: "/cities/salina", element: <SalinaPage /> },
  { path: "/cities/leawood", element: <LeawoodPage /> },
  { path: "/cities/hutchinson", element: <HutchinsonPage /> },
  { path: "/cities/manhattan", element: <ManhattanPage /> },
  { path: "/cities/shawnee-ks", element: <ShawneeKsPage /> },

  // Arkansas
  { path: "/states/arkansas", element: <ArkansasPage /> },
  { path: "/cities/little-rock", element: <LittleRockPage /> },
  { path: "/cities/fayetteville-ar", element: <FayettevilleARPage /> },
  { path: "/cities/fort-smith", element: <FortSmithPage /> },
  { path: "/cities/springdale", element: <SpringdalePage /> },
  { path: "/cities/jonesboro", element: <JonesboroPage /> },
  { path: "/cities/rogers", element: <RogersARPage /> },
  { path: "/cities/north-little-rock", element: <NorthLittleRockPage /> },
  { path: "/cities/conway", element: <ConwayARPage /> },
  { path: "/cities/bentonville", element: <BentonvillePage /> },
  { path: "/cities/pine-bluff", element: <PineBluffPage /> },
  { path: "/cities/hot-springs", element: <HotSpringsPage /> },
  { path: "/cities/benton", element: <BentonARPage /> },
  { path: "/cities/el-dorado", element: <ElDoradoARPage /> },
  { path: "/cities/russellville", element: <RussellvillePage /> },
  { path: "/cities/texarkana", element: <TexarkanaARPage /> },

  // New Mexico
  { path: "/states/new-mexico", element: <NewMexicoPage /> },
  { path: "/cities/albuquerque", element: <AlbuquerquePage /> },
  { path: "/cities/las-cruces", element: <LasCrucesPage /> },
  { path: "/cities/rio-rancho", element: <RioRanchoPage /> },
  { path: "/cities/farmington", element: <FarmingtonPage /> },
  { path: "/cities/clovis", element: <ClovisPage /> },
  { path: "/cities/hobbs", element: <HobbsPage /> },
  { path: "/cities/alamogordo", element: <AlamogordoPage /> },
  { path: "/cities/gallup", element: <GallupPage /> },
  { path: "/cities/los-alamos", element: <LosAlamosPage /> },
  { path: "/cities/roswell-nm", element: <RoswellNmPage /> },
  { path: "/cities/carlsbad-nm", element: <CarlsbadNmPage /> },

  // Mississippi
  { path: "/states/mississippi", element: <MississippiPage /> },
  { path: "/cities/jackson", element: <JacksonMSPage /> },
  { path: "/cities/gulfport", element: <GulfportPage /> },
  { path: "/cities/southaven", element: <SouthavenPage /> },
  { path: "/cities/hattiesburg", element: <HattiesburgPage /> },
  { path: "/cities/biloxi", element: <BiloxiPage /> },
  { path: "/cities/meridian", element: <MeridianPage /> },
  { path: "/cities/tupelo", element: <TupeloPage /> },
  { path: "/cities/greenville-ms", element: <GreenvilleMSPage /> },
  { path: "/cities/olive-branch", element: <OliveBranchPage /> },
  { path: "/cities/horn-lake", element: <HornLakePage /> },
  { path: "/cities/clinton", element: <ClintonMSPage /> },
  { path: "/cities/pearl", element: <PearlMSPage /> },

  // West Virginia
  { path: "/states/west-virginia", element: <WestVirginiaPage /> },
  { path: "/cities/morgantown", element: <MorgantownPage /> },
  { path: "/cities/parkersburg", element: <ParkersburgPage /> },
  { path: "/cities/wheeling", element: <WheelingPage /> },
  { path: "/cities/martinsburg", element: <MartinsburgPage /> },
  { path: "/cities/weirton", element: <WeirtonPage /> },
  { path: "/cities/fairmont", element: <FairmontPage /> },
  { path: "/cities/beckley", element: <BeckleyPage /> },
  { path: "/cities/clarksburg", element: <ClarksburgPage /> },
  { path: "/cities/south-charleston", element: <SouthCharlestonPage /> },
  { path: "/cities/st-albans", element: <StAlbansPage /> },

  // Montana city pages
  { path: "/cities/billings", element: <BillingsPage /> },
  { path: "/cities/missoula", element: <MissoulaPage /> },
  { path: "/cities/bozeman", element: <BozemanPage /> },
  { path: "/cities/great-falls", element: <GreatFallsPage /> },
  { path: "/cities/helena", element: <HelenaPage /> },
  { path: "/cities/kalispell", element: <KalispellPage /> },
  { path: "/cities/butte", element: <ButtePage /> },
  { path: "/cities/belgrade", element: <BelgradePage /> },
  { path: "/cities/miles-city", element: <MilesCityPage /> },
  { path: "/cities/livingston", element: <LivingstonPage /> },
  { path: "/cities/havre", element: <HavrePage /> },
  { path: "/cities/whitefish", element: <WhitefishPage /> },

  // Montana, Nebraska, North Dakota, South Dakota, Idaho
  { path: "/states/montana", element: <MontanaPage /> },
  { path: "/states/nebraska", element: <NebraskaPage /> },
  { path: "/cities/omaha", element: <OmahaPage /> },
  { path: "/cities/lincoln", element: <LincolnNEPage /> },
  { path: "/cities/grand-island", element: <GrandIslandPage /> },
  { path: "/cities/kearney", element: <KearneyPage /> },
  { path: "/cities/fremont", element: <FremontPage /> },
  { path: "/cities/hastings", element: <HastingsPage /> },
  { path: "/cities/north-platte", element: <NorthPlattePage /> },
  { path: "/cities/norfolk", element: <NorfolkPage /> },
  { path: "/cities/papillion", element: <PapillionPage /> },
  { path: "/cities/la-vista", element: <LaVistaPage /> },
  { path: "/cities/bellevue-ne", element: <BellevueNePage /> },
  { path: "/cities/columbus-ne", element: <ColumbusNePage /> },
  { path: "/states/north-dakota", element: <NorthDakotaPage /> },
  { path: "/states/south-dakota", element: <SouthDakotaPage /> },
  { path: "/states/idaho", element: <IdahoPage /> },

  // Idaho city pages
  { path: "/cities/boise", element: <BoisePage /> },
  { path: "/cities/idaho-falls", element: <IdahoFallsPage /> },
  { path: "/cities/coeur-dalene", element: <CoeurDalenePage /> },
  { path: "/cities/post-falls", element: <PostFallsPage /> },
  { path: "/cities/nampa", element: <NampaPage /> },
  { path: "/cities/pocatello", element: <PocatelloPage /> },
  { path: "/cities/twin-falls", element: <TwinFallsPage /> },
  { path: "/cities/caldwell", element: <CaldwellPage /> },
  { path: "/cities/rexburg", element: <RexburgPage /> },
  { path: "/cities/moscow", element: <MoscowPage /> },
  { path: "/cities/meridian-id", element: <MeridianIDPage /> },

  // Alaska and Wyoming state hubs
  { path: "/states/alaska", element: <AlaskaPage /> },
  { path: "/cities/wyoming-state", element: <WyomingStatePage /> },
  { path: "/states/wyoming", element: <WyomingStatePage /> },

  // Alaska city pages
  { path: "/cities/anchorage", element: <AnchoragePage /> },
  { path: "/cities/juneau", element: <JuneauPage /> },
  { path: "/cities/fairbanks-ak", element: <FairbankAKPage /> },
  { path: "/cities/sitka", element: <SitkaPage /> },
  { path: "/cities/ketchikan", element: <KetchikanPage /> },
  { path: "/cities/wasilla", element: <WasillaPage /> },
  { path: "/cities/kenai", element: <KenaiPage /> },
  { path: "/cities/kodiak", element: <KodiakPage /> },
  { path: "/cities/homer-ak", element: <HomerAkPage /> },
  { path: "/cities/soldotna", element: <SoldotnaPage /> },
  { path: "/cities/palmer-ak", element: <PalmerAkPage /> },
  { path: "/cities/seward-ak", element: <SewardAkPage /> },

  // Wyoming city pages
  { path: "/cities/cheyenne", element: <CheyennePage /> },
  { path: "/cities/casper", element: <CasperPage /> },
  { path: "/cities/jackson-wy", element: <JacksonWYPage /> },
  { path: "/cities/laramie", element: <LaramiePage /> },
  { path: "/cities/gillette", element: <GillettePage /> },
  { path: "/cities/rock-springs", element: <RockSpringsPage /> },
  { path: "/cities/sheridan-wy", element: <SheridanWyPage /> },
  { path: "/cities/evanston-wy", element: <EvanstonWyPage /> },
  { path: "/cities/riverton", element: <RivertonPage /> },
  { path: "/cities/lander", element: <LanderPage /> },
  { path: "/cities/rawlins", element: <RawlinsPage /> },
  { path: "/cities/cody", element: <CodyPage /> },

  // North Dakota city pages
  { path: "/cities/fargo", element: <FargoPage /> },
  { path: "/cities/bismarck", element: <BismarckPage /> },
  { path: "/cities/grand-forks", element: <GrandForksPage /> },
  { path: "/cities/minot", element: <MinotPage /> },
  { path: "/cities/west-fargo", element: <WestFargoPage /> },
  { path: "/cities/williston", element: <WillistonPage /> },
  { path: "/cities/dickinson", element: <DickinsonPage /> },
  { path: "/cities/mandan", element: <MandanPage /> },
  { path: "/cities/wahpeton", element: <WahpetonPage /> },
  { path: "/cities/devils-lake", element: <DevilsLakePage /> },
  { path: "/cities/valley-city", element: <ValleyCityPage /> },
  { path: "/cities/jamestown-nd", element: <JamestownNDPage /> },

  // South Dakota city pages
  { path: "/cities/sioux-falls", element: <SiouxFallsPage /> },
  { path: "/cities/rapid-city", element: <RapidCityPage /> },
  { path: "/cities/aberdeen-sd", element: <AberdeenSDPage /> },
  { path: "/cities/brookings", element: <BrookingsPage /> },
  { path: "/cities/mitchell", element: <MitchellPage /> },
  { path: "/cities/yankton", element: <YanktonPage /> },
  { path: "/cities/huron", element: <HuronPage /> },
  { path: "/cities/pierre", element: <PierrePage /> },
  { path: "/cities/spearfish", element: <SpearfishPage /> },
  { path: "/cities/vermillion", element: <VermillionPage /> },
  { path: "/cities/sturgis", element: <SturgisPage /> },
  { path: "/cities/watertown-sd", element: <WatertownSDPage /> },

  // Missouri
  { path: "/states/missouri", element: <MissouriPage /> },
  { path: "/cities/st-louis", element: <StLouisPage /> },
  { path: "/cities/kansas-city", element: <KansasCityPage /> },
  { path: "/cities/springfield-mo", element: <SpringfieldMoPage /> },
  { path: "/cities/columbia-mo", element: <ColumbiaMoPage /> },
  { path: "/cities/joplin", element: <JoplinMOPage /> },
  { path: "/cities/st-joseph-mo", element: <StJosephMOPage /> },
  { path: "/cities/lees-summit", element: <LeeSummitMOPage /> },
  { path: "/cities/ofallon-mo", element: <OFallonMOPage /> },
  { path: "/cities/independence-mo", element: <IndependenceMOPage /> },
  { path: "/cities/jefferson-city", element: <JeffersonCityPage /> },
  { path: "/cities/cape-girardeau", element: <CapeGirardeauPage /> },
  { path: "/cities/hannibal", element: <HannibalPage /> },
  { path: "/cities/st-charles", element: <StCharlesPage /> },
  { path: "/cities/blue-springs-mo", element: <BlueSpringsMOPage /> },
  { path: "/cities/florissant-mo", element: <FlorissantMOPage /> },
  { path: "/cities/chesterfield-mo", element: <ChesterfieldMOPage /> },
  { path: "/cities/wentzville-mo", element: <WentzvilleMOPage /> },
  { path: "/cities/liberty-mo", element: <LibertyMOPage /> },
  { path: "/cities/kirkwood-mo", element: <KirkwoodMOPage /> },
  { path: "/cities/nixa-mo", element: <NixaMOPage /> },
  { path: "/cities/ballwin-mo", element: <BallwinMOPage /> },
  { path: "/cities/raytown-mo", element: <RaytownMOPage /> },
  { path: "/cities/gladstone-mo", element: <GladstoneMOPage /> },
  { path: "/cities/grandview-mo", element: <GrandviewMOPage /> },
  { path: "/cities/belton-mo", element: <BeltonMOPage /> },
  { path: "/cities/ozark-mo", element: <OzarkMOPage /> },
  { path: "/cities/warrensburg-mo", element: <WarrensburgMOPage /> },
  { path: "/cities/rolla-mo", element: <RollaMOPage /> },
  { path: "/cities/sedalia-mo", element: <SedaliaMOPage /> },
  { path: "/cities/kirksville-mo", element: <KirksvilleMOPage /> },
  { path: "/cities/sikeston-mo", element: <SikestonMOPage /> },
  { path: "/cities/poplar-bluff-mo", element: <PoplarBluffMOPage /> },
  { path: "/cities/lebanon-mo", element: <LebanonMOPage /> },
  { path: "/cities/fulton-mo", element: <FultonMOPage /> },

  // Iowa
  { path: "/states/iowa", element: <IowaPage /> },
  { path: "/cities/des-moines", element: <DesMoinesPage /> },
  { path: "/cities/cedar-rapids", element: <CedarRapidsPage /> },
  { path: "/cities/iowa-city", element: <IowaCityPage /> },
  { path: "/cities/davenport", element: <DavenportPage /> },
  { path: "/cities/sioux-city", element: <SiouxCityPage /> },
  { path: "/cities/waterloo-ia", element: <WaterlooIAPage /> },
  { path: "/cities/council-bluffs", element: <CouncilBluffsPage /> },
  { path: "/cities/ames", element: <AmesPage /> },
  { path: "/cities/dubuque", element: <DubuquePage /> },
  { path: "/cities/west-des-moines", element: <WestDesMoinesPage /> },
  { path: "/cities/ankeny", element: <AnkenyPage /> },
  { path: "/cities/mason-city", element: <MasonCityPage /> },
  { path: "/cities/coralville", element: <CoralvillePage /> },
  { path: "/cities/bettendorf", element: <BettendorfPage /> },
  { path: "/cities/fort-dodge", element: <FortDodgePage /> },
  { path: "/cities/ottumwa", element: <OttumwaPage /> },
  { path: "/cities/pella", element: <PellaPage /> },
  { path: "/cities/boone", element: <BoonePage /> },
  { path: "/cities/burlington-ia", element: <BurlingtonIAPage /> },
  { path: "/cities/clinton-ia", element: <ClintonIAPage /> },
  { path: "/cities/decorah", element: <DecorahPage /> },
  { path: "/cities/grinnell", element: <GrinnellPage /> },
  { path: "/cities/indianola", element: <IndianolaPage /> },
  { path: "/cities/johnston-ia", element: <JohnstonIAPage /> },
  { path: "/cities/keokuk", element: <KeokukPage /> },
  { path: "/cities/marion-ia", element: <MarionIAPage /> },
  { path: "/cities/marshalltown", element: <MarshalltownPage /> },
  { path: "/cities/muscatine", element: <MuscatinePage /> },
  { path: "/cities/newton-ia", element: <NewtonIAPage /> },
  { path: "/cities/north-liberty", element: <NorthLibertyPage /> },
  { path: "/cities/oskaloosa", element: <OskaloosaPage /> },
  { path: "/cities/spencer", element: <SpencerPage /> },
  { path: "/cities/urbandale", element: <UrbandalePage /> },
  { path: "/cities/waverly", element: <WaverlyPage /> },
  { path: "/cities/altoona-ia", element: <AltoonaIAPage /> },

  // New Hampshire
  { path: "/states/new-hampshire", element: <NewHampshirePage /> },
  { path: "/cities/manchester", element: <ManchesterPage /> },
  { path: "/cities/nashua", element: <NashuaPage /> },
  { path: "/cities/concord-nh", element: <ConcordNHPage /> },
  { path: "/cities/portsmouth", element: <PortsmouthPage /> },
  { path: "/cities/keene", element: <KeenePage /> },
  { path: "/cities/dover", element: <DoverNHPage /> },
  { path: "/cities/laconia", element: <LaconiaPage /> },
  { path: "/cities/lebanon", element: <LebanonPage /> },
  { path: "/cities/rochester-nh", element: <RochesterNHPage /> },
  { path: "/cities/derry", element: <DerryPage /> },
  { path: "/cities/londonderry", element: <LondonderryPage /> },
  { path: "/cities/hudson", element: <HudsonNHPage /> },
  { path: "/cities/hampton", element: <HamptonPage /> },
  { path: "/cities/exeter", element: <ExeterPage /> },
  { path: "/cities/hooksett", element: <HooksettPage /> },
  { path: "/cities/merrimack", element: <MerrimackPage /> },
  { path: "/cities/plymouth", element: <PlymouthPage /> },
  { path: "/cities/goffstown", element: <GoffstownPage /> },
  { path: "/cities/windham-nh", element: <WindhamNHPage /> },
  { path: "/cities/milford-nh", element: <MilfordNHPage /> },
  { path: "/cities/hanover", element: <HanoverPage /> },
  { path: "/cities/claremont", element: <ClaremontPage /> },
  { path: "/cities/somersworth", element: <SomersworthPage /> },
  { path: "/cities/franklin-nh", element: <FranklinNHPage /> },
  { path: "/cities/berlin", element: <BerlinPage /> },
  { path: "/cities/pelham", element: <PelhamPage /> },
  { path: "/cities/raymond", element: <RaymondPage /> },
  { path: "/cities/durham-nh", element: <DurhamNHPage /> },
  { path: "/cities/newmarket", element: <NewmarketPage /> },
  { path: "/cities/conway-nh", element: <ConwayNhPage /> },
  { path: "/cities/wolfeboro", element: <WolfeboroPage /> },
  { path: "/cities/littleton-nh", element: <LittletonNhPage /> },
  { path: "/cities/lancaster-nh", element: <LancasterNhPage /> },
  { path: "/cities/henniker", element: <HennikerPage /> },
  { path: "/cities/jaffrey", element: <JaffreyPage /> },
  { path: "/cities/peterborough", element: <PeterboroughPage /> },

  // Minnesota
  { path: "/states/minnesota", element: <MinnesotaPage /> },
  { path: "/cities/minneapolis", element: <MinneapolisPage /> },
  { path: "/cities/st-paul", element: <StPaulMNPage /> },
  { path: "/cities/duluth-mn", element: <DuluthMNPage /> },
  { path: "/cities/rochester-mn", element: <RochesterMNPage /> },
  { path: "/cities/bloomington", element: <BloomingtonMNPage /> },
  { path: "/cities/brooklyn-park", element: <BrooklynParkPage /> },
  { path: "/cities/plymouth-mn", element: <PlymouthMNPage /> },
  { path: "/cities/maple-grove", element: <MapleGroveMNPage /> },
  { path: "/cities/woodbury", element: <WoodburyMNPage /> },
  { path: "/cities/eagan", element: <EaganMNPage /> },
  { path: "/cities/eden-prairie", element: <EdenPrairieMNPage /> },
  { path: "/cities/burnsville", element: <BurnsvilleMNPage /> },
  { path: "/cities/blaine", element: <BlaineMNPage /> },
  { path: "/cities/lakeville", element: <LakevilleMNPage /> },
  { path: "/cities/minnetonka", element: <MinnetonkaMNPage /> },
  { path: "/cities/st-cloud", element: <StCloudMNPage /> },
  { path: "/cities/edina", element: <EdinaMNPage /> },
  { path: "/cities/apple-valley", element: <AppleValleyMNPage /> },
  { path: "/cities/st-louis-park", element: <StLouisParkMNPage /> },
  { path: "/cities/moorhead", element: <MoorheadMNPage /> },
  { path: "/cities/mankato", element: <MankatoMNPage /> },
  { path: "/cities/shakopee", element: <ShakopeeMNPage /> },
  { path: "/cities/coon-rapids", element: <CoonRapidsMNPage /> },
  { path: "/cities/maplewood", element: <MaplewoodMNPage /> },
  { path: "/cities/chanhassen", element: <ChanhassenMNPage /> },
  { path: "/cities/richfield", element: <RichfieldMNPage /> },
  { path: "/cities/roseville-mn", element: <RosevilleMNPage /> },
  { path: "/cities/inver-grove-heights", element: <InverGroveHeightsMNPage /> },
  { path: "/cities/andover", element: <AndoverMNPage /> },
  { path: "/cities/austin-mn", element: <AustinMNPage /> },
  { path: "/cities/west-st-paul", element: <WestStPaulMNPage /> },
  { path: "/cities/northfield", element: <NorthfieldMNPage /> },
  { path: "/cities/owatonna", element: <OwatonnaMNPage /> },
  { path: "/cities/brainerd", element: <BrainerdMNPage /> },
  { path: "/cities/winona", element: <WinonaMNPage /> },
  { path: "/cities/faribault", element: <FaribaultMNPage /> },
  { path: "/cities/bemidji", element: <BemidjiMNPage /> },
  { path: "/cities/willmar", element: <WillmarMNPage /> },

  // Delaware
  { path: "/states/delaware", element: <DelawareStatePage /> },
  { path: "/cities/wilmington-de", element: <WilmingtonDePage /> },
  { path: "/cities/dover-de", element: <DoverDePage /> },
  { path: "/cities/newark-de", element: <NewarkDePage /> },
  { path: "/cities/middletown-de", element: <MiddletownDePage /> },
  { path: "/cities/rehoboth-beach", element: <RehobothBeachPage /> },
  { path: "/cities/newcastle-de", element: <NewcastleDePage /> },
  { path: "/cities/smyrna-de", element: <SmyrnaDePage /> },
  { path: "/cities/milford-de", element: <MilfordDePage /> },
  { path: "/cities/seaford-de", element: <SeafordDePage /> },
  { path: "/cities/georgetown-de", element: <GeorgetownDePage /> },

  // Michigan batch 4
  { path: "/cities/jackson-mi", element: <JacksonMIPage /> },
  { path: "/cities/canton-mi", element: <CantonMIPage /> },
  { path: "/cities/farmington-hills", element: <FarmingtonHillsPage /> },
  { path: "/cities/rochester-hills", element: <RochesterHillsPage /> },
  { path: "/cities/livonia", element: <LivoniaPage /> },
  { path: "/cities/royal-oak", element: <RoyalOakPage /> },
  { path: "/cities/taylor", element: <TaylorPage /> },
  { path: "/cities/westland", element: <WestlandPage /> },
  { path: "/cities/dearborn-heights", element: <DearbornHeightsPage /> },
  { path: "/cities/roseville-mi", element: <RosevilleMIPage /> },
  { path: "/cities/clinton-township", element: <ClintonTownshipPage /> },
  { path: "/cities/eastpointe", element: <EastpointePage /> },
  { path: "/cities/port-huron", element: <PortHuronPage /> },
  { path: "/cities/saint-clair-shores", element: <SaintClairShoresPage /> },
  { path: "/cities/saginaw", element: <SaginawPage /> },
  { path: "/cities/traverse-city", element: <TraverseCityPage /> },
  { path: "/cities/battle-creek", element: <BattleCreekPage /> },
  { path: "/cities/bay-city", element: <BayCityPage /> },
  { path: "/cities/midland", element: <MidlandPage /> },
  { path: "/cities/holland", element: <HollandPage /> },
  { path: "/states/wyoming", element: <WyomingPage /> },
  { path: "/cities/kentwood", element: <KentwoodPage /> },
  { path: "/cities/sault-ste-marie", element: <SaultSteMariePage /> },
  { path: "/cities/marquette", element: <MarquettePage /> },
  { path: "/cities/petoskey", element: <PetoskeyPage /> },
  { path: "/cities/ludington", element: <LudingtonPage /> },
  { path: "/cities/st-joseph-mi", element: <StJosephMIPage /> },
  { path: "/cities/adrian", element: <AdrianPage /> },
  { path: "/cities/alpena", element: <AlpenaPage /> },
  { path: "/cities/escanaba", element: <EscanabaPage /> },
  { path: "/cities/grand-blanc", element: <GrandBlancPage /> },
  { path: "/cities/houghton", element: <HoughtonPage /> },
  { path: "/cities/manistee", element: <ManisteePage /> },
  { path: "/cities/saginaw-township", element: <SaginawTownshipPage /> },
];

export default routes;